import { useApp } from '@/store';
export { Lang, LANGUAGES } from '@/lib/lang';
import { Lang } from '@/lib/lang';

// L() expands one English string into all 8 supported locales as a placeholder.
// Used for new keys that haven't been translated yet — replace with real
// translations as they arrive.
const L = (en: string) => ({
  en,
  de: en,
  fr: en,
  it: en,
  es: en,
  pt: en,
  nl: en,
  pl: en,
});

const dict = {
  // Nav
  'nav.main': {
    en: 'Main',
    de: 'Hauptmenü',
    fr: 'Principal',
    it: 'Principale',
    es: 'Principal',
    pt: 'Principal',
    nl: 'Hoofdmenu',
    pl: 'Główne',
  },
  'nav.admin': {
    en: 'Administration',
    de: 'Verwaltung',
    fr: 'Administration',
    it: 'Amministrazione',
    es: 'Administración',
    pt: 'Administração',
    nl: 'Beheer',
    pl: 'Administracja',
  },
  'nav.data': {
    en: 'Data',
    de: 'Daten',
    fr: 'Données',
    it: 'Dati',
    es: 'Datos',
    pt: 'Dados',
    nl: 'Gegevens',
    pl: 'Dane',
  },
  'nav.overview': {
    en: 'Overview',
    de: 'Übersicht',
    fr: 'Aperçu',
    it: 'Panoramica',
    es: 'Resumen',
    pt: 'Visão geral',
    nl: 'Overzicht',
    pl: 'Przegląd',
  },
  'nav.map': {
    en: 'Map',
    de: 'Karte',
    fr: 'Carte',
    it: 'Mappa',
    es: 'Mapa',
    pt: 'Mapa',
    nl: 'Kaart',
    pl: 'Mapa',
  },
  'nav.reminders': {
    en: 'Reminders',
    de: 'Erinnerungen',
    fr: 'Rappels',
    it: 'Promemoria',
    es: 'Recordatorios',
    pt: 'Lembretes',
    nl: 'Herinneringen',
    pl: 'Przypomnienia',
  },
  'reminders.title': {
    en: 'Reminders',
    de: 'Erinnerungen',
    fr: 'Rappels',
    it: 'Promemoria',
    es: 'Recordatorios',
    pt: 'Lembretes',
    nl: 'Herinneringen',
    pl: 'Przypomnienia',
  },
  'reminders.subtitle': {
    en: 'Inspections due within 30 days',
    de: 'Prüfungen fällig innerhalb von 30 Tagen',
    fr: 'Inspections dues sous 30 jours',
    it: 'Ispezioni in scadenza entro 30 giorni',
    es: 'Inspecciones que vencen en 30 días',
    pt: 'Inspeções a vencer em 30 dias',
    nl: 'Inspecties verschuldigd binnen 30 dagen',
    pl: 'Przeglądy w ciągu 30 dni',
  },
  'reminders.overdue': {
    en: 'Overdue',
    de: 'Überfällig',
    fr: 'En retard',
    it: 'In ritardo',
    es: 'Vencido',
    pt: 'Atrasado',
    nl: 'Achterstallig',
    pl: 'Zaległe',
  },
  'reminders.dueSoon': {
    en: 'Due soon',
    de: 'Bald fällig',
    fr: 'Bientôt dû',
    it: 'In scadenza',
    es: 'Próximo a vencer',
    pt: 'A vencer em breve',
    nl: 'Binnenkort',
    pl: 'Wkrótce',
  },
  'reminders.none': {
    en: 'No inspections due in the next 30 days',
    de: 'Keine Prüfungen in den nächsten 30 Tagen fällig',
    fr: 'Aucune inspection due dans les 30 prochains jours',
    it: 'Nessuna ispezione in scadenza nei prossimi 30 giorni',
    es: 'No hay inspecciones en los próximos 30 días',
    pt: 'Nenhuma inspeção nos próximos 30 dias',
    nl: 'Geen inspecties in de komende 30 dagen',
    pl: 'Brak przeglądów w ciągu najbliższych 30 dni',
  },
  'reminders.today': {
    en: 'Due today',
    de: 'Heute fällig',
    fr: "Dû aujourd'hui",
    it: 'In scadenza oggi',
    es: 'Vence hoy',
    pt: 'Vence hoje',
    nl: 'Vandaag',
    pl: 'Dziś',
  },
  'reminders.dueInDays': {
    en: 'days left',
    de: 'Tage übrig',
    fr: 'jours restants',
    it: 'giorni rimasti',
    es: 'días restantes',
    pt: 'dias restantes',
    nl: 'dagen resterend',
    pl: 'dni pozostało',
  },
  'reminders.daysOverdue': {
    en: 'days overdue',
    de: 'Tage überfällig',
    fr: 'jours de retard',
    it: 'giorni di ritardo',
    es: 'días de retraso',
    pt: 'dias de atraso',
    nl: 'dagen te laat',
    pl: 'dni po terminie',
  },
  'nav.manufacturers': {
    en: 'Manufacturers',
    de: 'Hersteller',
    fr: 'Fabricants',
    it: 'Produttori',
    es: 'Fabricantes',
    pt: 'Fabricantes',
    nl: 'Fabrikanten',
    pl: 'Producenci',
  },
  'nav.deviceTypes': {
    en: 'Device Types',
    de: 'Gerätetypen',
    fr: "Types d'appareils",
    it: 'Tipi di dispositivi',
    es: 'Tipos de dispositivos',
    pt: 'Tipos de dispositivos',
    nl: 'Apparaattypen',
    pl: 'Typy urządzeń',
  },
  'nav.spareParts': {
    en: 'Spare Parts',
    de: 'Ersatzteile',
    fr: 'Pièces de rechange',
    it: 'Ricambi',
    es: 'Repuestos',
    pt: 'Peças sobressalentes',
    nl: 'Onderdelen',
    pl: 'Części zamienne',
  },
  'nav.servicePrices': {
    en: 'Service Prices',
    de: 'Servicepreise',
    fr: 'Tarifs',
    it: 'Prezzi servizio',
    es: 'Precios de servicio',
    pt: 'Preços de serviço',
    nl: 'Serviceprijzen',
    pl: 'Ceny usług',
  },
  'nav.workers': {
    en: 'Workers',
    de: 'Mitarbeiter',
    fr: 'Techniciens',
    it: 'Tecnici',
    es: 'Técnicos',
    pt: 'Técnicos',
    nl: 'Medewerkers',
    pl: 'Pracownicy',
  },
  'nav.settings': {
    en: 'Settings',
    de: 'Einstellungen',
    fr: 'Paramètres',
    it: 'Impostazioni',
    es: 'Configuración',
    pt: 'Configurações',
    nl: 'Instellingen',
    pl: 'Ustawienia',
  },
  'nav.permissions': {
    en: 'Permissions',
    de: 'Berechtigungen',
    fr: 'Autorisations',
    it: 'Autorizzazioni',
    es: 'Permisos',
    pt: 'Permissões',
    nl: 'Rechten',
    pl: 'Uprawnienia',
  },
  'perms.title': {
    en: 'Permissions',
    de: 'Berechtigungen',
    fr: 'Autorisations',
    it: 'Autorizzazioni',
    es: 'Permisos',
    pt: 'Permissões',
    nl: 'Rechten',
    pl: 'Uprawnienia',
  },
  'perms.subtitle': {
    en: 'What each role may do, per section',
    de: 'Rechte je Rolle und Bereich',
    fr: 'Droits par rôle et par section',
    it: 'Diritti per ruolo e sezione',
    es: 'Derechos por rol y sección',
    pt: 'Direitos por função e seção',
    nl: 'Rechten per rol en onderdeel',
    pl: 'Uprawnienia wg roli i sekcji',
  },
  'perms.note': {
    en: 'Admins always have all rights. These rules also apply offline.',
    de: 'Admins haben immer alle Rechte. Diese Regeln gelten auch offline.',
    fr: 'Les admins ont toujours tous les droits. Ces règles s’appliquent aussi hors ligne.',
    it: 'Gli admin hanno sempre tutti i diritti. Queste regole valgono anche offline.',
    es: 'Los administradores siempre tienen todos los derechos. Estas reglas también aplican sin conexión.',
    pt: 'Os admins sempre têm todos os direitos. Estas regras também valem offline.',
    nl: 'Admins hebben altijd alle rechten. Deze regels gelden ook offline.',
    pl: 'Administratorzy zawsze mają wszystkie uprawnienia. Reguły działają też offline.',
  },
  // Client #92: which customers reach a technician's device.
  'perms.techScopeTitle': {
    en: 'Customers technicians see',
    de: 'Kundensicht Techniker',
    fr: 'Clients visibles par les techniciens',
    it: 'Clienti visibili ai tecnici',
    es: 'Clientes que ven los técnicos',
    pt: 'Clientes que os técnicos veem',
    nl: 'Klanten die monteurs zien',
    pl: 'Klienci widoczni dla techników',
  },
  'perms.techScopeIntro': {
    en: 'Technicians only receive the customers assigned to them in the calendar, until 7 days after the appointment. Other customers are not loaded onto their device.',
    de: 'Techniker erhalten nur die Kunden, die ihnen im Kalender zugewiesen sind, bis 7 Tage nach dem Termin. Andere Kunden werden nicht auf ihr Gerät geladen.',
    fr: "Les techniciens ne reçoivent que les clients qui leur sont attribués dans le calendrier, jusqu'à 7 jours après le rendez-vous. Les autres clients ne sont pas chargés sur leur appareil.",
    it: "I tecnici ricevono solo i clienti assegnati loro nel calendario, fino a 7 giorni dopo l'appuntamento. Gli altri clienti non vengono caricati sul loro dispositivo.",
    es: 'Los técnicos solo reciben los clientes que tienen asignados en el calendario, hasta 7 días después de la cita. Los demás clientes no se cargan en su dispositivo.',
    pt: 'Os técnicos só recebem os clientes que lhes estão atribuídos no calendário, até 7 dias após a marcação. Os outros clientes não são carregados no dispositivo.',
    nl: 'Monteurs krijgen alleen de klanten die hun in de agenda zijn toegewezen, tot 7 dagen na de afspraak. Andere klanten worden niet op hun apparaat geladen.',
    pl: 'Technicy otrzymują tylko klientów przypisanych im w kalendarzu, do 7 dni po terminie. Pozostali klienci nie są ładowani na ich urządzenie.',
  },
  'perms.techScopeDueSoon': {
    en: 'Also overdue customers and customers due soon (next 30 days)',
    de: 'Zusätzlich fällige und bald fällige Kunden (nächste 30 Tage)',
    fr: 'Aussi les clients en retard et à échéance proche (30 prochains jours)',
    it: 'Anche i clienti scaduti e in scadenza (prossimi 30 giorni)',
    es: 'También los clientes vencidos y próximos a vencer (próximos 30 días)',
    pt: 'Também os clientes em atraso e com vencimento próximo (próximos 30 dias)',
    nl: 'Ook achterstallige klanten en klanten die binnenkort aan de beurt zijn (komende 30 dagen)',
    pl: 'Także klienci zaległi i z bliskim terminem (następne 30 dni)',
  },
  'perms.techScopeAll': {
    en: 'All customers',
    de: 'Alle Kunden',
    fr: 'Tous les clients',
    it: 'Tutti i clienti',
    es: 'Todos los clientes',
    pt: 'Todos os clientes',
    nl: 'Alle klanten',
    pl: 'Wszyscy klienci',
  },
  'perms.techScopeApply': {
    en: "Takes effect on the technicians' devices within an hour at the latest.",
    de: 'Wird auf den Geräten der Techniker spätestens nach einer Stunde wirksam.',
    fr: "Prend effet sur les appareils des techniciens au plus tard dans l'heure.",
    it: "Diventa effettivo sui dispositivi dei tecnici entro un'ora al massimo.",
    es: 'Se aplica en los dispositivos de los técnicos en una hora como máximo.',
    pt: 'Entra em vigor nos dispositivos dos técnicos no máximo dentro de uma hora.',
    nl: 'Wordt uiterlijk binnen een uur actief op de apparaten van de monteurs.',
    pl: 'Zaczyna obowiązywać na urządzeniach techników najpóźniej po godzinie.',
  },
  'perms.matrixTitle': {
    en: 'Rights per section',
    de: 'Rechte je Bereich',
    fr: 'Droits par section',
    it: 'Diritti per sezione',
    es: 'Derechos por sección',
    pt: 'Direitos por seção',
    nl: 'Rechten per onderdeel',
    pl: 'Uprawnienia wg sekcji',
  },
  'perms.section': {
    en: 'Section',
    de: 'Bereich',
    fr: 'Section',
    it: 'Sezione',
    es: 'Sección',
    pt: 'Seção',
    nl: 'Onderdeel',
    pl: 'Sekcja',
  },
  'perms.colAll': {
    en: 'All',
    de: 'Alle',
    fr: 'Tout',
    it: 'Tutti',
    es: 'Todo',
    pt: 'Todos',
    nl: 'Alle',
    pl: 'Wszystko',
  },
  'perms.colRead': {
    en: 'Read',
    de: 'Lesen',
    fr: 'Lire',
    it: 'Leggere',
    es: 'Leer',
    pt: 'Ler',
    nl: 'Lezen',
    pl: 'Odczyt',
  },
  'perms.colCreate': {
    en: 'Create',
    de: 'Erstellen',
    fr: 'Créer',
    it: 'Creare',
    es: 'Crear',
    pt: 'Criar',
    nl: 'Aanmaken',
    pl: 'Tworzenie',
  },
  'perms.colEdit': {
    en: 'Edit',
    de: 'Bearbeiten',
    fr: 'Modifier',
    it: 'Modificare',
    es: 'Editar',
    pt: 'Editar',
    nl: 'Bewerken',
    pl: 'Edycja',
  },
  'perms.colDelete': {
    en: 'Delete',
    de: 'Löschen',
    fr: 'Supprimer',
    it: 'Eliminare',
    es: 'Eliminar',
    pt: 'Excluir',
    nl: 'Verwijderen',
    pl: 'Usuwanie',
  },
  'perms.adminOnly': {
    en: 'Admins only',
    de: 'Nur Admin',
    fr: 'Admins uniquement',
    it: 'Solo admin',
    es: 'Solo administradores',
    pt: 'Só admins',
    nl: 'Alleen admins',
    pl: 'Tylko administrator',
  },
  'perms.adminOnlyHint': {
    en: 'Greyed-out boxes are reserved for admins by the server and cannot be granted.',
    de: 'Ausgegraute Felder sind vom Server den Admins vorbehalten und können nicht vergeben werden.',
    fr: 'Les cases grisées sont réservées aux admins par le serveur et ne peuvent pas être accordées.',
    it: 'Le caselle in grigio sono riservate agli admin dal server e non possono essere concesse.',
    es: 'Las casillas en gris están reservadas a los administradores por el servidor y no se pueden conceder.',
    pt: 'As caixas em cinza são reservadas aos admins pelo servidor e não podem ser concedidas.',
    nl: 'Grijze vakjes zijn door de server voorbehouden aan admins en kunnen niet worden toegekend.',
    pl: 'Wyszarzone pola są zastrzeżone przez serwer dla administratorów i nie można ich przyznać.',
  },
  'perms.notApplicable': {
    en: 'Not applicable',
    de: 'Nicht anwendbar',
    fr: 'Sans objet',
    it: 'Non applicabile',
    es: 'No aplicable',
    pt: 'Não aplicável',
    nl: 'Niet van toepassing',
    pl: 'Nie dotyczy',
  },
  'perms.hintMaintenance': {
    en: 'Create = carry out a maintenance · Edit = change the maintenance status of devices',
    de: 'Erstellen = Wartung durchführen · Bearbeiten = Wartungsstatus der Geräte ändern',
    fr: 'Créer = effectuer une maintenance · Modifier = changer l’état de maintenance des appareils',
    it: 'Creare = eseguire una manutenzione · Modificare = cambiare lo stato di manutenzione dei dispositivi',
    es: 'Crear = realizar un mantenimiento · Editar = cambiar el estado de mantenimiento de los equipos',
    pt: 'Criar = realizar uma manutenção · Editar = alterar o estado de manutenção dos equipamentos',
    nl: 'Aanmaken = onderhoud uitvoeren · Bewerken = onderhoudsstatus van apparaten wijzigen',
    pl: 'Tworzenie = wykonanie przeglądu · Edycja = zmiana statusu przeglądu urządzeń',
  },
  'perms.hintInvoices': {
    en: 'Create = print / PDF · Edit = adjust prices and VAT',
    de: 'Erstellen = drucken / PDF · Bearbeiten = Preise und MwSt. anpassen',
    fr: 'Créer = imprimer / PDF · Modifier = ajuster les prix et la TVA',
    it: 'Creare = stampa / PDF · Modificare = adattare prezzi e IVA',
    es: 'Crear = imprimir / PDF · Editar = ajustar precios e IVA',
    pt: 'Criar = imprimir / PDF · Editar = ajustar preços e IVA',
    nl: 'Aanmaken = afdrukken / pdf · Bewerken = prijzen en btw aanpassen',
    pl: 'Tworzenie = druk / PDF · Edycja = korekta cen i VAT',
  },
  'perms.sec.customers': {
    en: 'Customers',
    de: 'Kunden',
    fr: 'Clients',
    it: 'Clienti',
    es: 'Clientes',
    pt: 'Clientes',
    nl: 'Klanten',
    pl: 'Klienci',
  },
  'perms.sec.extinguishers': {
    en: 'Extinguishers',
    de: 'Feuerlöscher',
    fr: 'Extincteurs',
    it: 'Estintori',
    es: 'Extintores',
    pt: 'Extintores',
    nl: 'Blusapparaten',
    pl: 'Gaśnice',
  },
  'perms.sec.hoses': {
    en: 'Hoses',
    de: 'Schläuche',
    fr: 'Tuyaux',
    it: 'Manichette',
    es: 'Mangueras',
    pt: 'Mangueiras',
    nl: 'Slangen',
    pl: 'Węże',
  },
  'perms.sec.maintenance': {
    en: 'Maintenance',
    de: 'Wartung',
    fr: 'Maintenance',
    it: 'Manutenzione',
    es: 'Mantenimiento',
    pt: 'Manutenção',
    nl: 'Onderhoud',
    pl: 'Przeglądy',
  },
  'perms.sec.calendar': {
    en: 'Calendar',
    de: 'Kalender',
    fr: 'Calendrier',
    it: 'Calendario',
    es: 'Calendario',
    pt: 'Calendário',
    nl: 'Kalender',
    pl: 'Kalendarz',
  },
  'perms.sec.map': {
    en: 'Map',
    de: 'Karte',
    fr: 'Carte',
    it: 'Mappa',
    es: 'Mapa',
    pt: 'Mapa',
    nl: 'Kaart',
    pl: 'Mapa',
  },
  'perms.sec.reminders': {
    en: 'Reminders',
    de: 'Erinnerungen',
    fr: 'Rappels',
    it: 'Promemoria',
    es: 'Recordatorios',
    pt: 'Lembretes',
    nl: 'Herinneringen',
    pl: 'Przypomnienia',
  },
  'perms.sec.reports': {
    en: 'Reports',
    de: 'Berichte',
    fr: 'Rapports',
    it: 'Report',
    es: 'Informes',
    pt: 'Relatórios',
    nl: 'Rapporten',
    pl: 'Raporty',
  },
  'perms.sec.invoices': {
    en: 'Invoices',
    de: 'Rechnungen',
    fr: 'Factures',
    it: 'Fatture',
    es: 'Facturas',
    pt: 'Faturas',
    nl: 'Facturen',
    pl: 'Faktury',
  },
  'perms.sec.manufacturers': {
    en: 'Manufacturers',
    de: 'Hersteller',
    fr: 'Fabricants',
    it: 'Produttori',
    es: 'Fabricantes',
    pt: 'Fabricantes',
    nl: 'Fabrikanten',
    pl: 'Producenci',
  },
  'perms.sec.deviceTypes': {
    en: 'Device types',
    de: 'Gerätetypen',
    fr: 'Types d’appareils',
    it: 'Tipi di dispositivo',
    es: 'Tipos de equipo',
    pt: 'Tipos de equipamento',
    nl: 'Apparaattypen',
    pl: 'Typy urządzeń',
  },
  'perms.sec.spareParts': {
    en: 'Spare parts',
    de: 'Ersatzteile',
    fr: 'Pièces détachées',
    it: 'Ricambi',
    es: 'Repuestos',
    pt: 'Peças de reposição',
    nl: 'Onderdelen',
    pl: 'Części zamienne',
  },
  'perms.sec.servicePrices': {
    en: 'Service prices',
    de: 'Servicepreise',
    fr: 'Tarifs de service',
    it: 'Prezzi di servizio',
    es: 'Precios de servicio',
    pt: 'Preços de serviço',
    nl: 'Servicetarieven',
    pl: 'Ceny usług',
  },
  'perms.sec.hoseOptions': {
    en: 'Hose options',
    de: 'Schlauchoptionen',
    fr: 'Options de tuyaux',
    it: 'Opzioni manichette',
    es: 'Opciones de mangueras',
    pt: 'Opções de mangueiras',
    nl: 'Slangopties',
    pl: 'Opcje węży',
  },
  'perms.sec.workers': {
    en: 'Workers',
    de: 'Mitarbeiter',
    fr: 'Collaborateurs',
    it: 'Collaboratori',
    es: 'Empleados',
    pt: 'Funcionários',
    nl: 'Medewerkers',
    pl: 'Pracownicy',
  },
  'perms.sec.settings': {
    en: 'Settings',
    de: 'Einstellungen',
    fr: 'Paramètres',
    it: 'Impostazioni',
    es: 'Ajustes',
    pt: 'Configurações',
    nl: 'Instellingen',
    pl: 'Ustawienia',
  },
  'perms.noAccessTitle': {
    en: 'No access',
    de: 'Kein Zugriff',
    fr: 'Pas d’accès',
    it: 'Nessun accesso',
    es: 'Sin acceso',
    pt: 'Sem acesso',
    nl: 'Geen toegang',
    pl: 'Brak dostępu',
  },
  'perms.noCustomerAccess': {
    en: 'Your role may not view customers. An admin can grant access under Administration → Permissions.',
    de: 'Ihre Rolle darf keine Kunden ansehen. Ein Admin kann den Zugriff unter Verwaltung → Berechtigungen freigeben.',
    fr: 'Votre rôle ne permet pas de voir les clients. Un admin peut accorder l’accès sous Administration → Autorisations.',
    it: 'Il suo ruolo non può vedere i clienti. Un admin può concedere l’accesso in Amministrazione → Autorizzazioni.',
    es: 'Su rol no puede ver clientes. Un administrador puede conceder acceso en Administración → Permisos.',
    pt: 'A sua função não pode ver clientes. Um admin pode conceder acesso em Administração → Permissões.',
    nl: 'Uw rol mag geen klanten bekijken. Een admin kan toegang geven onder Beheer → Rechten.',
    pl: 'Twoja rola nie może przeglądać klientów. Administrator może nadać dostęp w Administracja → Uprawnienia.',
  },
  'perms.readOnly': {
    en: 'Read only — you can view this but not change it.',
    de: 'Nur Lesezugriff – Sie können ansehen, aber nicht ändern.',
    fr: 'Lecture seule — vous pouvez consulter mais pas modifier.',
    it: 'Sola lettura — può vedere ma non modificare.',
    es: 'Solo lectura: puede ver pero no modificar.',
    pt: 'Somente leitura — você pode ver, mas não alterar.',
    nl: 'Alleen lezen — u kunt dit bekijken maar niet wijzigen.',
    pl: 'Tylko odczyt — możesz przeglądać, ale nie zmieniać.',
  },
  'common.view': {
    en: 'View',
    de: 'Ansehen',
    fr: 'Voir',
    it: 'Visualizza',
    es: 'Ver',
    pt: 'Ver',
    nl: 'Bekijken',
    pl: 'Podgląd',
  },
  'perms.roleTech': {
    en: 'Technician',
    de: 'Techniker',
    fr: 'Technicien',
    it: 'Tecnico',
    es: 'Técnico',
    pt: 'Técnico',
    nl: 'Technicus',
    pl: 'Technik',
  },
  // The client calls this role "Büro" (email 13 Oct, #95).
  'perms.roleStaff': {
    en: 'Office',
    de: 'Büro',
    fr: 'Bureau',
    it: 'Ufficio',
    es: 'Oficina',
    pt: 'Escritório',
    nl: 'Kantoor',
    pl: 'Biuro',
  },
  'perms.save': {
    en: 'Save',
    de: 'Speichern',
    fr: 'Enregistrer',
    it: 'Salva',
    es: 'Guardar',
    pt: 'Salvar',
    nl: 'Opslaan',
    pl: 'Zapisz',
  },
  'perms.reset': {
    en: 'Restore defaults',
    de: 'Standard wiederherstellen',
    fr: 'Restaurer les valeurs par défaut',
    it: 'Ripristina predefiniti',
    es: 'Restaurar valores predeterminados',
    pt: 'Restaurar padrões',
    nl: 'Standaard herstellen',
    pl: 'Przywróć domyślne',
  },
  'perms.saved': {
    en: 'Permissions saved',
    de: 'Berechtigungen gespeichert',
    fr: 'Autorisations enregistrées',
    it: 'Autorizzazioni salvate',
    es: 'Permisos guardados',
    pt: 'Permissões salvas',
    nl: 'Rechten opgeslagen',
    pl: 'Uprawnienia zapisane',
  },
  'nav.hoseOptions': {
    en: 'Hose Options',
    de: 'Schlauchoptionen',
    fr: 'Options tuyaux',
    it: 'Opzioni manichetta',
    es: 'Opciones manguera',
    pt: 'Opções mangueira',
    nl: 'Slangopties',
    pl: 'Opcje węży',
  },

  // Header
  'header.online': {
    en: 'Online',
    de: 'Online',
    fr: 'En ligne',
    it: 'Online',
    es: 'En línea',
    pt: 'Online',
    nl: 'Online',
    pl: 'Online',
  },
  'header.offline': {
    en: 'Offline',
    de: 'Offline',
    fr: 'Hors ligne',
    it: 'Offline',
    es: 'Sin conexión',
    pt: 'Offline',
    nl: 'Offline',
    pl: 'Offline',
  },
  'header.syncing': {
    en: 'Syncing…',
    de: 'Synchronisiert…',
    fr: 'Synchronisation…',
    it: 'Sincronizzazione…',
    es: 'Sincronizando…',
    pt: 'A sincronizar…',
    nl: 'Synchroniseren…',
    pl: 'Synchronizacja…',
  },
  'sync.loading': {
    en: 'Loading your data…',
    de: 'Daten werden geladen…',
    fr: 'Chargement de vos données…',
    it: 'Caricamento dei dati…',
    es: 'Cargando tus datos…',
    pt: 'A carregar os seus dados…',
    nl: 'Uw gegevens worden geladen…',
    pl: 'Ładowanie danych…',
  },
  'sync.hint': {
    en: 'First load downloads all your data for offline use',
    de: 'Beim ersten Laden werden alle Daten für die Offline-Nutzung heruntergeladen',
    fr: 'Le premier chargement télécharge toutes vos données pour une utilisation hors ligne',
    it: 'Il primo caricamento scarica tutti i dati per l’uso offline',
    es: 'La primera carga descarga todos tus datos para uso sin conexión',
    pt: 'O primeiro carregamento descarrega todos os seus dados para uso offline',
    nl: 'Bij de eerste keer laden worden al uw gegevens gedownload voor offline gebruik',
    pl: 'Pierwsze ładowanie pobiera wszystkie dane do użytku offline',
  },
  'sync.saveFailed': {
    en: 'Save failed — will retry',
    de: 'Speichern fehlgeschlagen — wird wiederholt',
    fr: 'Échec de l’enregistrement — nouvel essai',
    it: 'Salvataggio non riuscito — verrà ritentato',
    es: 'Error al guardar — se reintentará',
    pt: 'Falha ao guardar — será repetido',
    nl: 'Opslaan mislukt — opnieuw proberen',
    pl: 'Zapis nie powiódł się — ponowna próba',
  },
  'sync.rejected': {
    en: 'Change rejected by the server',
    de: 'Änderung vom Server abgelehnt',
    fr: 'Modification rejetée par le serveur',
    it: 'Modifica rifiutata dal server',
    es: 'Cambio rechazado por el servidor',
    pt: 'Alteração rejeitada pelo servidor',
    nl: 'Wijziging geweigerd door de server',
    pl: 'Zmiana odrzucona przez serwer',
  },
  'perm.denied': {
    en: 'You do not have permission for this change',
    de: 'Sie haben keine Berechtigung für diese Änderung',
    fr: 'Vous n’avez pas l’autorisation pour cette modification',
    it: 'Non hai i permessi per questa modifica',
    es: 'No tienes permiso para este cambio',
    pt: 'Não tem permissão para esta alteração',
    nl: 'U heeft geen toestemming voor deze wijziging',
    pl: 'Nie masz uprawnień do tej zmiany',
  },
  'header.help': {
    en: 'Help',
    de: 'Hilfe',
    fr: 'Aide',
    it: 'Aiuto',
    es: 'Ayuda',
    pt: 'Ajuda',
    nl: 'Help',
    pl: 'Pomoc',
  },
  'header.tour': {
    en: 'Tour',
    de: 'Tour',
    fr: 'Visite',
    it: 'Tour',
    es: 'Tour',
    pt: 'Tour',
    nl: 'Rondleiding',
    pl: 'Przewodnik',
  },
  'header.lang': {
    en: 'Language',
    de: 'Sprache',
    fr: 'Langue',
    it: 'Lingua',
    es: 'Idioma',
    pt: 'Idioma',
    nl: 'Taal',
    pl: 'Język',
  },

  // Common
  'common.back': {
    en: 'Back',
    de: 'Zurück',
    fr: 'Retour',
    it: 'Indietro',
    es: 'Atrás',
    pt: 'Voltar',
    nl: 'Terug',
    pl: 'Wstecz',
  },
  'common.backToOverview': {
    en: 'Back to overview',
    de: 'Zurück zur Übersicht',
    fr: "Retour à l'aperçu",
    it: 'Torna alla panoramica',
    es: 'Volver al resumen',
    pt: 'Voltar à visão geral',
    nl: 'Terug naar overzicht',
    pl: 'Powrót do przeglądu',
  },
  'common.previous': {
    en: 'Previous',
    de: 'Zurück',
    fr: 'Précédent',
    it: 'Precedente',
    es: 'Anterior',
    pt: 'Anterior',
    nl: 'Vorige',
    pl: 'Poprzedni',
  },
  'common.next': {
    en: 'Next',
    de: 'Weiter',
    fr: 'Suivant',
    it: 'Successivo',
    es: 'Siguiente',
    pt: 'Próximo',
    nl: 'Volgende',
    pl: 'Następny',
  },
  'common.dragToReorder': {
    en: 'Drag to reorder',
    de: 'Zum Sortieren ziehen',
    fr: 'Glisser pour réordonner',
    it: 'Trascina per riordinare',
    es: 'Arrastrar para reordenar',
    pt: 'Arraste para reordenar',
    nl: 'Sleep om te herordenen',
    pl: 'Przeciągnij, aby zmienić kolejność',
  },
  'device.dupWarning': {
    en: 'A device with this number already exists. Save anyway?',
    de: 'Ein Gerät mit dieser Nummer existiert bereits. Trotzdem speichern?',
    fr: 'Un appareil avec ce numéro existe déjà. Enregistrer quand même ?',
    it: 'Un dispositivo con questo numero esiste già. Salvare comunque?',
    es: 'Ya existe un dispositivo con este número. ¿Guardar de todos modos?',
    pt: 'Já existe um dispositivo com este número. Guardar mesmo assim?',
    nl: 'Er bestaat al een apparaat met dit nummer. Toch opslaan?',
    pl: 'Urządzenie o tym numerze już istnieje. Zapisać mimo to?',
  },
  'common.save': {
    en: 'Save',
    de: 'Speichern',
    fr: 'Enregistrer',
    it: 'Salva',
    es: 'Guardar',
    pt: 'Salvar',
    nl: 'Opslaan',
    pl: 'Zapisz',
  },
  'common.clear': {
    en: 'Clear',
    de: 'Löschen',
    fr: 'Effacer',
    it: 'Cancella',
    es: 'Borrar',
    pt: 'Limpar',
    nl: 'Wissen',
    pl: 'Wyczyść',
  },
  'inspection.signHere': {
    en: 'Sign here',
    de: 'Hier unterschreiben',
    fr: 'Signez ici',
    it: 'Firma qui',
    es: 'Firme aquí',
    pt: 'Assine aqui',
    nl: 'Teken hier',
    pl: 'Podpisz tutaj',
  },
  'inspection.signed': {
    en: 'Signed',
    de: 'Unterschrieben',
    fr: 'Signé',
    it: 'Firmato',
    es: 'Firmado',
    pt: 'Assinado',
    nl: 'Ondertekend',
    pl: 'Podpisano',
  },
  'inspection.complete': {
    en: 'Complete inspection',
    de: 'Inspektion abschließen',
    fr: "Terminer l'inspection",
    it: 'Completa ispezione',
    es: 'Completar inspección',
    pt: 'Concluir inspeção',
    nl: 'Inspectie voltooien',
    pl: 'Zakończ inspekcję',
  },
  'inspection.completed': {
    en: 'Inspection completed',
    de: 'Inspektion abgeschlossen',
    fr: 'Inspection terminée',
    it: 'Ispezione completata',
    es: 'Inspección completada',
    pt: 'Inspeção concluída',
    nl: 'Inspectie voltooid',
    pl: 'Inspekcja zakończona',
  },
  'inspection.submit': {
    en: 'Complete & sign off',
    de: 'Abschließen & abzeichnen',
    fr: 'Terminer et signer',
    it: 'Completa e firma',
    es: 'Completar y firmar',
    pt: 'Concluir e assinar',
    nl: 'Voltooien & aftekenen',
    pl: 'Zakończ i podpisz',
  },
  'inspection.devices': {
    en: 'Devices serviced',
    de: 'Gewartete Geräte',
    fr: 'Appareils entretenus',
    it: 'Dispositivi sottoposti a manutenzione',
    es: 'Dispositivos revisados',
    pt: 'Dispositivos atendidos',
    nl: 'Onderhouden apparaten',
    pl: 'Serwisowane urządzenia',
  },
  'inspection.noDevices': {
    en: 'No devices on this building.',
    de: 'Keine Geräte in diesem Gebäude.',
    fr: 'Aucun appareil dans ce bâtiment.',
    it: 'Nessun dispositivo in questo edificio.',
    es: 'No hay dispositivos en este edificio.',
    pt: 'Nenhum dispositivo neste edifício.',
    nl: 'Geen apparaten in dit gebouw.',
    pl: 'Brak urządzeń w tym budynku.',
  },
  'inspection.signature': {
    en: 'Signature',
    de: 'Unterschrift',
    fr: 'Signature',
    it: 'Firma',
    es: 'Firma',
    pt: 'Assinatura',
    nl: 'Handtekening',
    pl: 'Podpis',
  },
  'inspection.hose': {
    en: 'Hose',
    de: 'Schlauch',
    fr: 'Tuyau',
    it: 'Manichetta',
    es: 'Manguera',
    pt: 'Mangueira',
    nl: 'Slang',
    pl: 'Wąż',
  },
  'inspection.note': {
    en: 'Note',
    de: 'Notiz',
    fr: 'Note',
    it: 'Nota',
    es: 'Nota',
    pt: 'Nota',
    nl: 'Notitie',
    pl: 'Notatka',
  },
  'inspection.extinguisher': {
    en: 'Extinguisher',
    de: 'Löscher',
    fr: 'Extincteur',
    it: 'Estintore',
    es: 'Extintor',
    pt: 'Extintor',
    nl: 'Blusser',
    pl: 'Gaśnica',
  },
  'inspection.condition': {
    en: 'Condition',
    de: 'Zustand',
    fr: 'État',
    it: 'Stato',
    es: 'Estado',
    pt: 'Estado',
    nl: 'Toestand',
    pl: 'Stan',
  },
  'inspection.inspect': {
    en: 'Inspect',
    de: 'Prüfen',
    fr: 'Inspecter',
    it: 'Ispeziona',
    es: 'Inspeccionar',
    pt: 'Inspecionar',
    nl: 'Inspecteren',
    pl: 'Sprawdź',
  },
  'inspection.refilled': {
    en: 'Refilled this visit',
    de: 'Füllung ersetzt',
    fr: 'Rechargé cette visite',
    it: 'Ricaricato questa visita',
    es: 'Recargado esta visita',
    pt: 'Recarregado nesta visita',
    nl: 'Bijgevuld dit bezoek',
    pl: 'Napełniono podczas wizyty',
  },
  'inspection.invoiceVisit': {
    en: 'Invoice this visit',
    de: 'Diesen Besuch verrechnen',
    fr: 'Facturer cette visite',
    it: 'Fattura questa visita',
    es: 'Facturar esta visita',
    pt: 'Faturar esta visita',
    nl: 'Dit bezoek factureren',
    pl: 'Rozlicz tę wizytę',
  },
  'common.cancel': {
    en: 'Cancel',
    de: 'Abbrechen',
    fr: 'Annuler',
    it: 'Annulla',
    es: 'Cancelar',
    pt: 'Cancelar',
    nl: 'Annuleren',
    pl: 'Anuluj',
  },
  'common.delete': {
    en: 'Delete',
    de: 'Löschen',
    fr: 'Supprimer',
    it: 'Elimina',
    es: 'Eliminar',
    pt: 'Excluir',
    nl: 'Verwijderen',
    pl: 'Usuń',
  },
  'common.edit': {
    en: 'Edit',
    de: 'Bearbeiten',
    fr: 'Modifier',
    it: 'Modifica',
    es: 'Editar',
    pt: 'Editar',
    nl: 'Bewerken',
    pl: 'Edytuj',
  },
  'common.add': {
    en: 'Add',
    de: 'Hinzufügen',
    fr: 'Ajouter',
    it: 'Aggiungi',
    es: 'Añadir',
    pt: 'Adicionar',
    nl: 'Toevoegen',
    pl: 'Dodaj',
  },
  'common.new': {
    en: 'New',
    de: 'Neu',
    fr: 'Nouveau',
    it: 'Nuovo',
    es: 'Nuevo',
    pt: 'Novo',
    nl: 'Nieuw',
    pl: 'Nowy',
  },
  'common.done': {
    en: 'Done',
    de: 'Fertig',
    fr: 'Terminé',
    it: 'Fatto',
    es: 'Hecho',
    pt: 'Concluído',
    nl: 'Klaar',
    pl: 'Gotowe',
  },
  'common.count': {
    en: 'Count',
    de: 'Anzahl',
    fr: 'Nombre',
    it: 'Quantità',
    es: 'Cantidad',
    pt: 'Quantidade',
    nl: 'Aantal',
    pl: 'Liczba',
  },
  'extReport.title': {
    en: 'Fire Extinguisher Report',
    de: 'Feuerlöscher-Rapport',
    fr: 'Rapport des extincteurs',
    it: 'Rapporto estintori',
    es: 'Informe de extintores',
    pt: 'Relatório de extintores',
    nl: 'Brandblusserrapport',
    pl: 'Raport gaśnic',
  },
  'common.print': {
    en: 'Print',
    de: 'Drucken',
    fr: 'Imprimer',
    it: 'Stampa',
    es: 'Imprimir',
    pt: 'Imprimir',
    nl: 'Afdrukken',
    pl: 'Drukuj',
  },
  'common.pdf': {
    en: 'PDF',
    de: 'PDF',
    fr: 'PDF',
    it: 'PDF',
    es: 'PDF',
    pt: 'PDF',
    nl: 'PDF',
    pl: 'PDF',
  },
  'common.email': {
    en: 'Email',
    de: 'E-Mail',
    fr: 'E-mail',
    it: 'Email',
    es: 'Correo',
    pt: 'E-mail',
    nl: 'E-mail',
    pl: 'E-mail',
  },
  'common.today': {
    en: 'Today',
    de: 'Heute',
    fr: "Aujourd'hui",
    it: 'Oggi',
    es: 'Hoy',
    pt: 'Hoje',
    nl: 'Vandaag',
    pl: 'Dziś',
  },
  'common.notFound': {
    en: 'Customer not found',
    de: 'Kunde nicht gefunden',
    fr: 'Client introuvable',
    it: 'Cliente non trovato',
    es: 'Cliente no encontrado',
    pt: 'Cliente não encontrado',
    nl: 'Klant niet gevonden',
    pl: 'Klient nie znaleziony',
  },
  'common.invalidYear': {
    en: 'Invalid year',
    de: 'Ungültiges Jahr',
    fr: 'Année invalide',
    it: 'Anno non valido',
    es: 'Año inválido',
    pt: 'Ano inválido',
    nl: 'Ongeldig jaar',
    pl: 'Nieprawidłowy rok',
  },
  'reports.noWarnings': {
    en: 'No warnings',
    de: 'Keine Warnungen',
    fr: 'Aucun avertissement',
    it: 'Nessun avviso',
    es: 'Sin advertencias',
    pt: 'Sem avisos',
    nl: 'Geen waarschuwingen',
    pl: 'Brak ostrzeżeń',
  },
  'reports.allCurrent': {
    en: 'All current',
    de: 'Alle aktuell',
    fr: 'Tout à jour',
    it: 'Tutto attuale',
    es: 'Todo actual',
    pt: 'Tudo atual',
    nl: 'Alles actueel',
    pl: 'Wszystko aktualne',
  },
  'reports.generate': {
    en: 'Generate report',
    de: 'Bericht erstellen',
    fr: 'Générer un rapport',
    it: 'Genera rapporto',
    es: 'Generar informe',
    pt: 'Gerar relatório',
    nl: 'Rapport genereren',
    pl: 'Generuj raport',
  },
  'reports.reportType': {
    en: 'Report type',
    de: 'Berichtstyp',
    fr: 'Type de rapport',
    it: 'Tipo di rapporto',
    es: 'Tipo de informe',
    pt: 'Tipo de relatório',
    nl: 'Rapporttype',
    pl: 'Typ raportu',
  },
  'reports.format': {
    en: 'Format',
    de: 'Format',
    fr: 'Format',
    it: 'Formato',
    es: 'Formato',
    pt: 'Formato',
    nl: 'Formaat',
    pl: 'Format',
  },
  'reports.generateButton': {
    en: 'Generate',
    de: 'Erstellen',
    fr: 'Générer',
    it: 'Genera',
    es: 'Generar',
    pt: 'Gerar',
    nl: 'Genereren',
    pl: 'Generuj',
  },
  'reports.download': {
    en: 'Download',
    de: 'Herunterladen',
    fr: 'Télécharger',
    it: 'Scarica',
    es: 'Descargar',
    pt: 'Baixar',
    nl: 'Downloaden',
    pl: 'Pobierz',
  },
  'reports.status.queued': {
    en: 'Queued',
    de: 'In Warteschlange',
    fr: 'En file',
    it: 'In coda',
    es: 'En cola',
    pt: 'Na fila',
    nl: 'In wachtrij',
    pl: 'W kolejce',
  },
  'reports.status.done': {
    en: 'Done',
    de: 'Fertig',
    fr: 'Terminé',
    it: 'Fatto',
    es: 'Hecho',
    pt: 'Concluído',
    nl: 'Klaar',
    pl: 'Gotowe',
  },
  'reports.status.error': {
    en: 'Error',
    de: 'Fehler',
    fr: 'Erreur',
    it: 'Errore',
    es: 'Error',
    pt: 'Erro',
    nl: 'Fout',
    pl: 'Błąd',
  },
  'reports.type.customer_summary': {
    en: 'Customer summary',
    de: 'Kundenübersicht',
    fr: 'Résumé clients',
    it: 'Riepilogo clienti',
    es: 'Resumen de clientes',
    pt: 'Resumo de clientes',
    nl: 'Klantsamenvatting',
    pl: 'Podsumowanie klientów',
  },
  'reports.type.extinguisher_due': {
    en: 'Extinguishers due',
    de: 'Fällige Feuerlöscher',
    fr: 'Extincteurs à contrôler',
    it: 'Estintori in scadenza',
    es: 'Extintores pendientes',
    pt: 'Extintores pendentes',
    nl: 'Brandblussers vervallen',
    pl: 'Gaśnice do kontroli',
  },
  'reports.type.tenant_activity': {
    en: 'Tenant activity',
    de: 'Mandantenaktivität',
    fr: 'Activité locataire',
    it: 'Attività tenant',
    es: 'Actividad del inquilino',
    pt: 'Atividade do tenant',
    nl: 'Tenantactiviteit',
    pl: 'Aktywność najemcy',
  },
  'nav.reports': {
    en: 'Reports',
    de: 'Berichte',
    fr: 'Rapports',
    it: 'Rapporti',
    es: 'Informes',
    pt: 'Relatórios',
    nl: 'Rapporten',
    pl: 'Raporty',
  },
  'reports.title': {
    en: 'Reports',
    de: 'Berichte',
    fr: 'Rapports',
    it: 'Rapporti',
    es: 'Informes',
    pt: 'Relatórios',
    nl: 'Rapporten',
    pl: 'Raporty',
  },
  'reports.subtitle': {
    en: 'Generate PDF or CSV exports of your data',
    de: 'PDF- oder CSV-Exporte Ihrer Daten erstellen',
    fr: 'Générez des exports PDF ou CSV de vos données',
    it: 'Genera esportazioni PDF o CSV dei tuoi dati',
    es: 'Genere exportaciones PDF o CSV de sus datos',
    pt: 'Gere exportações PDF ou CSV dos seus dados',
    nl: 'Genereer PDF- of CSV-exports van uw gegevens',
    pl: 'Generuj eksporty danych w formacie PDF lub CSV',
  },
  'reports.status.failed': {
    en: 'Failed',
    de: 'Fehlgeschlagen',
    fr: 'Échoué',
    it: 'Fallito',
    es: 'Fallido',
    pt: 'Falhou',
    nl: 'Mislukt',
    pl: 'Niepowodzenie',
  },
  'reports.history': {
    en: 'Recent reports',
    de: 'Letzte Berichte',
    fr: 'Rapports récents',
    it: 'Rapporti recenti',
    es: 'Informes recientes',
    pt: 'Relatórios recentes',
    nl: 'Recente rapporten',
    pl: 'Ostatnie raporty',
  },
  'reports.requested': {
    en: 'Report requested — it will appear below when ready',
    de: 'Bericht angefordert — er erscheint unten, sobald er fertig ist',
    fr: 'Rapport demandé — il apparaîtra ci-dessous une fois prêt',
    it: 'Rapporto richiesto — apparirà qui sotto quando pronto',
    es: 'Informe solicitado — aparecerá abajo cuando esté listo',
    pt: 'Relatório solicitado — aparecerá abaixo quando estiver pronto',
    nl: 'Rapport aangevraagd — verschijnt hieronder zodra gereed',
    pl: 'Raport zamówiony — pojawi się poniżej, gdy będzie gotowy',
  },
  'ext.addExtinguisher': {
    en: 'Add Extinguisher',
    de: 'Feuerlöscher hinzufügen',
    fr: 'Ajouter un extincteur',
    it: 'Aggiungi estintore',
    es: 'Añadir extintor',
    pt: 'Adicionar extintor',
    nl: 'Brandblusser toevoegen',
    pl: 'Dodaj gaśnicę',
  },
  'ext.modelSearch': {
    en: 'Search model…',
    de: 'Modell suchen…',
    fr: 'Rechercher un modèle…',
    it: 'Cerca modello…',
    es: 'Buscar modelo…',
    pt: 'Pesquisar modelo…',
    nl: 'Model zoeken…',
    pl: 'Szukaj modelu…',
  },
  'mfr.content': {
    en: 'Content',
    de: 'Art des Löschmittel',
    fr: 'Contenu',
    it: 'Contenuto',
    es: 'Contenido',
    pt: 'Conteúdo',
    nl: 'Inhoud',
    pl: 'Zawartość',
  },
  'contact.role.safetyOfficer': {
    en: 'Safety Officer (SiBe)',
    de: 'Sicherheitsbeauftragter (SiBe)',
    fr: 'Chargé de sécurité (SiBe)',
    it: 'Responsabile sicurezza (SiBe)',
    es: 'Responsable de seguridad (SiBe)',
    pt: 'Responsável de segurança (SiBe)',
    nl: 'Veiligheidsfunctionaris (SiBe)',
    pl: 'Inspektor BHP (SiBe)',
  },
  'contact.role.custom': {
    en: 'Other (free text)',
    de: 'Andere (Freitext)',
    fr: 'Autre (texte libre)',
    it: 'Altro (testo libero)',
    es: 'Otro (texto libre)',
    pt: 'Outro (texto livre)',
    nl: 'Anders (vrije tekst)',
    pl: 'Inne (dowolny tekst)',
  },
  'contact.customRole': {
    en: 'Role name',
    de: 'Bezeichnung',
    fr: 'Intitulé',
    it: 'Denominazione',
    es: 'Denominación',
    pt: 'Designação',
    nl: 'Aanduiding',
    pl: 'Nazwa roli',
  },
  'common.search': {
    en: 'Search',
    de: 'Suche',
    fr: 'Rechercher',
    it: 'Cerca',
    es: 'Buscar',
    pt: 'Pesquisar',
    nl: 'Zoeken',
    pl: 'Szukaj',
  },
  'common.all': {
    en: 'All',
    de: 'Alle',
    fr: 'Tous',
    it: 'Tutti',
    es: 'Todos',
    pt: 'Todos',
    nl: 'Alles',
    pl: 'Wszystkie',
  },
  'common.copy': {
    en: 'Copy',
    de: 'Kopieren',
    fr: 'Copier',
    it: 'Copia',
    es: 'Copiar',
    pt: 'Copiar',
    nl: 'Kopiëren',
    pl: 'Kopiuj',
  },
  'common.yes': {
    en: 'Yes',
    de: 'Ja',
    fr: 'Oui',
    it: 'Sì',
    es: 'Sí',
    pt: 'Sim',
    nl: 'Ja',
    pl: 'Tak',
  },
  'common.no': {
    en: 'No',
    de: 'Nein',
    fr: 'Non',
    it: 'No',
    es: 'No',
    pt: 'Não',
    nl: 'Nee',
    pl: 'Nie',
  },
  'common.error': {
    en: 'Something went wrong',
    de: 'Etwas ist schiefgelaufen',
    fr: 'Une erreur est survenue',
    it: 'Qualcosa è andato storto',
    es: 'Algo salió mal',
    pt: 'Algo deu errado',
    nl: 'Er ging iets mis',
    pl: 'Coś poszło nie tak',
  },
  'common.confirm': {
    en: 'Confirm',
    de: 'Bestätigen',
    fr: 'Confirmer',
    it: 'Conferma',
    es: 'Confirmar',
    pt: 'Confirmar',
    nl: 'Bevestigen',
    pl: 'Potwierdź',
  },
  'common.close': {
    en: 'Close',
    de: 'Schliessen',
    fr: 'Fermer',
    it: 'Chiudi',
    es: 'Cerrar',
    pt: 'Fechar',
    nl: 'Sluiten',
    pl: 'Zamknij',
  },
  'common.notSet': {
    en: 'Not set',
    de: 'Nicht gesetzt',
    fr: 'Non défini',
    it: 'Non impostato',
    es: 'No definido',
    pt: 'Não definido',
    nl: 'Niet ingesteld',
    pl: 'Nie ustawiono',
  },
  'common.country': {
    en: 'Country',
    de: 'Land',
    fr: 'Pays',
    it: 'Paese',
    es: 'País',
    pt: 'País',
    nl: 'Land',
    pl: 'Kraj',
  },

  // Status
  'status.done': {
    en: 'Done',
    de: 'Erledigt',
    fr: 'Terminé',
    it: 'Fatto',
    es: 'Hecho',
    pt: 'Concluído',
    nl: 'Klaar',
    pl: 'Gotowe',
  },
  'status.waiting': {
    en: 'Waiting',
    de: 'Noch warten',
    fr: 'En attente',
    it: 'In attesa',
    es: 'Esperando',
    pt: 'Aguardando',
    nl: 'Wachtend',
    pl: 'Oczekuje',
  },
  'status.todo': {
    en: 'To do',
    de: 'Steht an',
    fr: 'À faire',
    it: 'Da fare',
    es: 'Pendiente',
    pt: 'A fazer',
    nl: 'Te doen',
    pl: 'Do zrobienia',
  },
  'status.none': {
    en: 'No date',
    de: 'Kein Termin',
    fr: 'Pas de date',
    it: 'Nessuna data',
    es: 'Sin fecha',
    pt: 'Sem data',
    nl: 'Geen datum',
    pl: 'Brak daty',
  },

  // Overview
  'overview.title': {
    en: 'Customer Overview',
    de: 'Kundenübersicht',
    fr: 'Aperçu clients',
    it: 'Panoramica clienti',
    es: 'Resumen de clientes',
    pt: 'Visão geral de clientes',
    nl: 'Klantenoverzicht',
    pl: 'Przegląd klientów',
  },
  'overview.subtitle': {
    en: 'All sites with fire protection equipment',
    de: 'Alle Standorte der Löschgeräte',
    fr: 'Tous les sites avec équipement incendie',
    it: 'Tutti i siti con attrezzatura antincendio',
    es: 'Todos los sitios con equipos contra incendios',
    pt: 'Todos os locais com equipamento contra incêndio',
    nl: 'Alle locaties met brandbeveiligingsapparatuur',
    pl: 'Wszystkie lokalizacje ze sprzętem przeciwpożarowym',
  },
  'overview.selected': {
    en: 'selected',
    de: 'ausgewählt',
    fr: 'sélectionnés',
    it: 'selezionati',
    es: 'seleccionados',
    pt: 'selecionados',
    nl: 'geselecteerd',
    pl: 'wybranych',
  },
  'overview.clearFilters': {
    en: 'Clear filters',
    de: 'Filter löschen',
    fr: 'Effacer filtres',
    it: 'Cancella filtri',
    es: 'Borrar filtros',
    pt: 'Limpar filtros',
    nl: 'Filters wissen',
    pl: 'Wyczyść filtry',
  },
  'overview.mapDisplayed': {
    en: 'Map of displayed',
    de: 'Karte angezeigter Daten',
    fr: 'Carte affichée',
    it: 'Mappa visualizzata',
    es: 'Mapa mostrado',
    pt: 'Mapa exibido',
    nl: 'Weergegeven kaart',
    pl: 'Wyświetlana mapa',
  },
  'overview.selectAll': {
    en: 'Select all',
    de: 'Alle auswählen',
    fr: 'Tout sélectionner',
    it: 'Seleziona tutto',
    es: 'Seleccionar todo',
    pt: 'Selecionar tudo',
    nl: 'Alles selecteren',
    pl: 'Zaznacz wszystko',
  },
  'overview.deselectAll': {
    en: 'Deselect all',
    de: 'Alle abwählen',
    fr: 'Tout désélectionner',
    it: 'Deseleziona tutto',
    es: 'Deseleccionar todo',
    pt: 'Desmarcar tudo',
    nl: 'Alles deselecteren',
    pl: 'Odznacz wszystko',
  },

  // Customer
  'customer.info': {
    en: 'Info & Addresses',
    de: 'Info & Adressen',
    fr: 'Info & Adresses',
    it: 'Info & Indirizzi',
    es: 'Info y Direcciones',
    pt: 'Info e Endereços',
    nl: 'Info & Adressen',
    pl: 'Info i Adresy',
  },
  'customer.extinguishers': {
    en: 'Fire Extinguishers',
    de: 'Feuerlöscher',
    fr: 'Extincteurs',
    it: 'Estintori',
    es: 'Extintores',
    pt: 'Extintores',
    nl: 'Brandblussers',
    pl: 'Gaśnice',
  },
  'customer.hoses': {
    en: 'Fire Hoses',
    de: 'Löschposten',
    fr: 'Lances incendie',
    it: 'Manichette',
    es: 'Mangueras',
    pt: 'Mangueiras',
    nl: 'Brandslangen',
    pl: 'Węże strażackie',
  },
  'customer.history': {
    en: 'History',
    de: 'Verlauf',
    fr: 'Historique',
    it: 'Cronologia',
    es: 'Historial',
    pt: 'Histórico',
    nl: 'Geschiedenis',
    pl: 'Historia',
  },
  'customer.billingAddress': {
    en: 'Billing Address',
    de: 'Rechnungsadresse',
    fr: 'Adresse de facturation',
    it: 'Indirizzo fatturazione',
    es: 'Dirección de facturación',
    pt: 'Endereço de faturação',
    nl: 'Factuuradres',
    pl: 'Adres rozliczeniowy',
  },
  'customer.objectAddress': {
    en: 'Object / Site Address',
    de: 'Objekt / Standort',
    fr: 'Adresse du site',
    it: 'Indirizzo sito',
    es: 'Dirección del sitio',
    pt: 'Endereço do local',
    nl: 'Locatieadres',
    pl: 'Adres obiektu',
  },
  'customer.ownerAddress': {
    en: 'Owner Address',
    de: 'Eigentümer',
    fr: 'Adresse propriétaire',
    it: 'Indirizzo proprietario',
    es: 'Dirección del propietario',
    pt: 'Endereço do proprietário',
    nl: 'Eigenaaradres',
    pl: 'Adres właściciela',
  },
  'customer.contact': {
    en: 'Janitor / Contact',
    de: 'Abwart / Kontakt',
    fr: 'Concierge / Contact',
    it: 'Custode / Contatto',
    es: 'Conserje / Contacto',
    pt: 'Zelador / Contacto',
    nl: 'Conciërge / Contact',
    pl: 'Dozorca / Kontakt',
  },
  'customer.contacts': {
    en: 'Contacts',
    de: 'Kontakte',
    fr: 'Contacts',
    it: 'Contatti',
    es: 'Contactos',
    pt: 'Contactos',
    nl: 'Contacten',
    pl: 'Kontakty',
  },
  'contact.role': {
    en: 'Role',
    de: 'Rolle',
    fr: 'Rôle',
    it: 'Ruolo',
    es: 'Rol',
    pt: 'Função',
    nl: 'Rol',
    pl: 'Rola',
  },
  'contact.name': {
    en: 'Name',
    de: 'Name',
    fr: 'Nom',
    it: 'Nome',
    es: 'Nombre',
    pt: 'Nome',
    nl: 'Naam',
    pl: 'Nazwa',
  },
  'contact.phone': {
    en: 'Phone',
    de: 'Telefon',
    fr: 'Téléphone',
    it: 'Telefono',
    es: 'Teléfono',
    pt: 'Telefone',
    nl: 'Telefoon',
    pl: 'Telefon',
  },
  'contact.role.janitor': {
    en: 'Janitor',
    de: 'Hauswart',
    fr: 'Concierge',
    it: 'Custode',
    es: 'Conserje',
    pt: 'Zelador',
    nl: 'Conciërge',
    pl: 'Dozorca',
  },
  'contact.role.janitor2': {
    en: 'Janitor 2',
    de: 'Hauswart 2',
    fr: 'Concierge 2',
    it: 'Custode 2',
    es: 'Conserje 2',
    pt: 'Zelador 2',
    nl: 'Conciërge 2',
    pl: 'Dozorca 2',
  },
  'contact.role.management': {
    en: 'Management',
    de: 'Verwaltung',
    fr: 'Gestion',
    it: 'Amministrazione',
    es: 'Administración',
    pt: 'Gestão',
    nl: 'Beheer',
    pl: 'Zarząd',
  },
  'contact.addTitle': {
    en: 'Add contact',
    de: 'Kontakt hinzufügen',
    fr: 'Ajouter un contact',
    it: 'Aggiungi contatto',
    es: 'Añadir contacto',
    pt: 'Adicionar contacto',
    nl: 'Contact toevoegen',
    pl: 'Dodaj kontakt',
  },
  'contact.editTitle': {
    en: 'Edit contact',
    de: 'Kontakt bearbeiten',
    fr: 'Modifier le contact',
    it: 'Modifica contatto',
    es: 'Editar contacto',
    pt: 'Editar contacto',
    nl: 'Contact bewerken',
    pl: 'Edytuj kontakt',
  },
  'customer.metadata': {
    en: 'Metadata',
    de: 'Administration',
    fr: 'Métadonnées',
    it: 'Metadati',
    es: 'Metadatos',
    pt: 'Metadados',
    nl: 'Metadata',
    pl: 'Metadane',
  },
  'customer.notes': {
    en: 'Notes',
    de: 'Notizen',
    fr: 'Notes',
    it: 'Note',
    es: 'Notas',
    pt: 'Notas',
    nl: 'Notities',
    pl: 'Notatki',
  },
  'customer.equipmentSummary': {
    en: 'Equipment Summary',
    de: 'Geräteübersicht',
    fr: 'Résumé des équipements',
    it: 'Riepilogo attrezzature',
    es: 'Resumen de equipos',
    pt: 'Resumo de equipamentos',
    nl: 'Apparatuuroverzicht',
    pl: 'Podsumowanie sprzętu',
  },
  'customer.created': {
    en: 'Created',
    de: 'Objekt erstellt',
    fr: 'Créé',
    it: 'Creato',
    es: 'Creado',
    pt: 'Criado',
    nl: 'Aangemaakt',
    pl: 'Utworzono',
  },
  'customer.createdBy': {
    en: 'Created by',
    de: 'Erstellt von',
    fr: 'Créé par',
    it: 'Creato da',
    es: 'Creado por',
    pt: 'Criado por',
    nl: 'Aangemaakt door',
    pl: 'Utworzono przez',
  },
  'customer.worker': {
    en: 'Worker',
    de: 'Servicetechniker',
    fr: 'Technicien',
    it: 'Tecnico',
    es: 'Técnico',
    pt: 'Técnico',
    nl: 'Medewerker',
    pl: 'Pracownik',
  },
  'customer.lastInspection': {
    en: 'Last Inspection',
    de: 'Letzte Kontrolle',
    fr: 'Dernière inspection',
    it: 'Ultima ispezione',
    es: 'Última inspección',
    pt: 'Última inspeção',
    nl: 'Laatste inspectie',
    pl: 'Ostatnia kontrola',
  },
  'customer.nextInspection': {
    en: 'Next Inspection',
    de: 'Nächste Kontrolle',
    fr: 'Prochaine inspection',
    it: 'Prossima ispezione',
    es: 'Próxima inspección',
    pt: 'Próxima inspeção',
    nl: 'Volgende inspectie',
    pl: 'Następna kontrola',
  },
  'customer.coordinates': {
    en: 'Coordinates',
    de: 'Koordinaten',
    fr: 'Coordonnées',
    it: 'Coordinate',
    es: 'Coordenadas',
    pt: 'Coordenadas',
    nl: 'Coördinaten',
    pl: 'Współrzędne',
  },
  'customer.archive': {
    en: 'Archive',
    de: 'Archiv erstellen',
    fr: 'Archiver',
    it: 'Archivia',
    es: 'Archivar',
    pt: 'Arquivar',
    nl: 'Archiveren',
    pl: 'Archiwizuj',
  },
  'customer.createArchive': {
    en: 'Create Archive',
    de: 'Archiv erstellen',
    fr: 'Créer archive',
    it: 'Crea archivio',
    es: 'Crear archivo',
    pt: 'Criar arquivo',
    nl: 'Archief aanmaken',
    pl: 'Utwórz archiwum',
  },

  // Extinguisher
  'ext.title': {
    en: 'Extinguisher',
    de: 'Feuerlöscher',
    fr: 'Extincteur',
    it: 'Estintore',
    es: 'Extintor',
    pt: 'Extintor',
    nl: 'Brandblusser',
    pl: 'Gaśnica',
  },
  'ext.manufacturer': {
    en: 'Manufacturer',
    de: 'Hersteller',
    fr: 'Fabricant',
    it: 'Produttore',
    es: 'Fabricante',
    pt: 'Fabricante',
    nl: 'Fabrikant',
    pl: 'Producent',
  },
  'ext.model': {
    en: 'Model',
    de: 'Modell',
    fr: 'Modèle',
    it: 'Modello',
    es: 'Modelo',
    pt: 'Modelo',
    nl: 'Model',
    pl: 'Model',
  },
  'ext.catFoam': {
    en: 'Foam',
    de: 'Schaum',
    fr: 'Mousse',
    it: 'Schiuma',
    es: 'Espuma',
    pt: 'Espuma',
    nl: 'Schuim',
    pl: 'Piana',
  },
  'ext.catPowder': {
    en: 'Powder',
    de: 'Pulver',
    fr: 'Poudre',
    it: 'Polvere',
    es: 'Polvo',
    pt: 'Pó',
    nl: 'Poeder',
    pl: 'Proszek',
  },
  'ext.catCo2': {
    en: 'CO2',
    de: 'CO2',
    fr: 'CO2',
    it: 'CO2',
    es: 'CO2',
    pt: 'CO2',
    nl: 'CO2',
    pl: 'CO2',
  },
  'ext.catWater': {
    en: 'Water',
    de: 'Wasser',
    fr: 'Eau',
    it: 'Acqua',
    es: 'Agua',
    pt: 'Água',
    nl: 'Water',
    pl: 'Woda',
  },
  'ext.agentType': {
    en: 'Agent Type',
    de: 'Löschmittel',
    fr: 'Agent extincteur',
    it: 'Agente estinguente',
    es: 'Tipo de agente',
    pt: 'Tipo de agente',
    nl: 'Blusmiddel',
    pl: 'Środek gaśniczy',
  },
  'ext.content': {
    en: 'Content',
    de: 'Inhalt',
    fr: 'Contenu',
    it: 'Contenuto',
    es: 'Contenido',
    pt: 'Conteúdo',
    nl: 'Inhoud',
    pl: 'Zawartość',
  },
  'ext.type': {
    en: 'Type',
    de: 'Gruppe',
    fr: 'Type',
    it: 'Tipo',
    es: 'Tipo',
    pt: 'Tipo',
    nl: 'Type',
    pl: 'Typ',
  },
  'ext.pressure': {
    en: 'Pressure',
    de: 'Druck',
    fr: 'Pression',
    it: 'Pressione',
    es: 'Presión',
    pt: 'Pressão',
    nl: 'Druk',
    pl: 'Ciśnienie',
  },
  'ext.ageLimit': {
    en: 'Age limit',
    de: 'Altersgrenze',
    fr: "Limite d'âge",
    it: 'Limite di età',
    es: 'Límite de edad',
    pt: 'Limite de idade',
    nl: 'Leeftijdsgrens',
    pl: 'Limit wieku',
  },
  'ext.placement': {
    en: 'Placement',
    de: 'Standort',
    fr: 'Emplacement',
    it: 'Posizione',
    es: 'Ubicación',
    pt: 'Localização',
    nl: 'Plaatsing',
    pl: 'Lokalizacja',
  },
  'ext.floor': {
    en: 'Floor',
    de: 'Stockwerk',
    fr: 'Étage',
    it: 'Piano',
    es: 'Planta',
    pt: 'Andar',
    nl: 'Verdieping',
    pl: 'Piętro',
  },
  'ext.location': {
    en: 'Location',
    de: 'Standort',
    fr: 'Lieu',
    it: 'Luogo',
    es: 'Ubicación',
    pt: 'Local',
    nl: 'Locatie',
    pl: 'Miejsce',
  },
  'ext.idNumber': {
    en: 'Our ID Number',
    de: 'Nummer',
    fr: 'Numéro ID',
    it: 'Numero ID',
    es: 'Número ID',
    pt: 'Número ID',
    nl: 'ID Nummer',
    pl: 'Numer ID',
  },
  'ext.year': {
    en: 'Manufacturing Year',
    de: 'Jahrgang',
    fr: 'Année de fabrication',
    it: 'Anno di produzione',
    es: 'Año de fabricación',
    pt: 'Ano de fabrico',
    nl: 'Bouwjaar',
    pl: 'Rok produkcji',
  },
  'ext.inspectionDates': {
    en: 'Inspection Dates',
    de: 'Kontrolldaten',
    fr: "Dates d'inspection",
    it: 'Date di ispezione',
    es: 'Fechas de inspección',
    pt: 'Datas de inspeção',
    nl: 'Inspectiedata',
    pl: 'Daty kontroli',
  },
  'ext.lastInspection': {
    en: 'Last Inspection',
    de: 'Letzte Kontrolle',
    fr: 'Dernière inspection',
    it: 'Ultima ispezione',
    es: 'Última inspección',
    pt: 'Última inspeção',
    nl: 'Laatste inspectie',
    pl: 'Ostatnia kontrola',
  },
  'ext.nextInspection': {
    en: 'Next Inspection',
    de: 'Nächste Kontrolle',
    fr: 'Prochaine inspection',
    it: 'Prossima ispezione',
    es: 'Próxima inspección',
    pt: 'Próxima inspeção',
    nl: 'Volgende inspectie',
    pl: 'Następna kontrola',
  },
  'ext.refilled': {
    en: 'Charge Replaced',
    de: 'Füllung ersetzt am',
    fr: 'Charge remplacée',
    it: 'Carica sostituita',
    es: 'Carga reemplazada',
    pt: 'Carga substituída',
    nl: 'Lading vervangen',
    pl: 'Ładunek wymieniony',
  },
  'ext.replaced': {
    en: 'Replaced',
    de: 'Ersetzt am',
    fr: 'Remplacé le',
    it: 'Sostituito il',
    es: 'Sustituido el',
    pt: 'Substituído em',
    nl: 'Vervangen op',
    pl: 'Wymieniono dnia',
  },
  'ext.billing': {
    en: 'Billing Type',
    de: 'Rechnungstyp',
    fr: 'Type de facturation',
    it: 'Tipo fatturazione',
    es: 'Tipo de facturación',
    pt: 'Tipo de faturação',
    nl: 'Facturatietype',
    pl: 'Typ rozliczenia',
  },
  'ext.billingNone': {
    en: 'No entry',
    de: 'Kein Eintrag',
    fr: 'Aucune entrée',
    it: 'Nessuna voce',
    es: 'Sin entrada',
    pt: 'Sem entrada',
    nl: 'Geen invoer',
    pl: 'Brak wpisu',
  },
  'ext.billingNew': {
    en: 'New device',
    de: 'Neu',
    fr: 'Nouvel appareil',
    it: 'Nuovo dispositivo',
    es: 'Dispositivo nuevo',
    pt: 'Novo dispositivo',
    nl: 'Nieuw apparaat',
    pl: 'Nowe urządzenie',
  },
  'ext.billingMaintenance': {
    en: 'Maintenance',
    de: 'Wartung',
    fr: 'Maintenance',
    it: 'Manutenzione',
    es: 'Mantenimiento',
    pt: 'Manutenção',
    nl: 'Onderhoud',
    pl: 'Konserwacja',
  },
  'ext.billingDisposed': {
    en: 'Disposed',
    de: 'Entsorgt',
    fr: 'Éliminé',
    it: 'Smaltito',
    es: 'Eliminado',
    pt: 'Descartado',
    nl: 'Afgevoerd',
    pl: 'Zutylizowano',
  },
  'ext.spareParts': {
    en: 'Spare Parts',
    de: 'Ersatzteile',
    fr: 'Pièces de rechange',
    it: 'Ricambi',
    es: 'Repuestos',
    pt: 'Peças',
    nl: 'Onderdelen',
    pl: 'Części zamienne',
  },
  'photos.title': {
    en: 'Photo documentation',
    de: 'Fotodokumentation',
    fr: 'Documentation photo',
    it: 'Documentazione fotografica',
    es: 'Documentación fotográfica',
    pt: 'Documentação fotográfica',
    nl: 'Fotodocumentatie',
    pl: 'Dokumentacja zdjęciowa',
  },
  'photos.add': {
    en: 'Add photo',
    de: 'Foto',
    fr: 'Photo',
    it: 'Foto',
    es: 'Foto',
    pt: 'Foto',
    nl: 'Foto',
    pl: 'Zdjęcie',
  },
  'photos.none': {
    en: 'No photos',
    de: 'Keine Fotos',
    fr: 'Aucune photo',
    it: 'Nessuna foto',
    es: 'Sin fotos',
    pt: 'Sem fotos',
    nl: 'Geen foto’s',
    pl: 'Brak zdjęć',
  },
  'ext.ageWarning': {
    en: 'exceeds age limit',
    de: 'überschreitet Altersgrenze',
    fr: "dépasse la limite d'âge",
    it: 'supera il limite di età',
    es: 'supera el límite de edad',
    pt: 'excede limite de idade',
    nl: 'overschrijdt leeftijdsgrens',
    pl: 'przekracza limit wieku',
  },
  'ext.documentation': {
    en: 'Documentation',
    de: 'Dokumentation',
    fr: 'Documentation',
    it: 'Documentazione',
    es: 'Documentación',
    pt: 'Documentação',
    nl: 'Documentatie',
    pl: 'Dokumentacja',
  },

  // Invoice
  'invoice.title': {
    en: 'Invoice',
    de: 'Rechnung',
    fr: 'Facture',
    it: 'Fattura',
    es: 'Factura',
    pt: 'Fatura',
    nl: 'Factuur',
    pl: 'Faktura',
  },
  'invoice.billTo': {
    en: 'Bill to',
    de: 'Rechnung an',
    fr: 'Facturer à',
    it: 'Fatturare a',
    es: 'Facturar a',
    pt: 'Faturar para',
    nl: 'Factureren aan',
    pl: 'Faktura dla',
  },
  'invoice.object': {
    en: 'Object',
    de: 'Objekt',
    fr: 'Objet',
    it: 'Oggetto',
    es: 'Objeto',
    pt: 'Objeto',
    nl: 'Object',
    pl: 'Obiekt',
  },
  'invoice.owner': {
    en: 'Owner',
    de: 'Eigentümer',
    fr: 'Propriétaire',
    it: 'Proprietario',
    es: 'Propietario',
    pt: 'Proprietário',
    nl: 'Eigenaar',
    pl: 'Właściciel',
  },
  'invoice.article': {
    en: 'Article-Nr.',
    de: 'Artikel-Nr.',
    fr: 'N° article',
    it: 'N° articolo',
    es: 'N° artículo',
    pt: 'N° artigo',
    nl: 'Artikelnr.',
    pl: 'Nr artykułu',
  },
  'invoice.description': {
    en: 'Description',
    de: 'Beschreibung',
    fr: 'Description',
    it: 'Descrizione',
    es: 'Descripción',
    pt: 'Descrição',
    nl: 'Beschrijving',
    pl: 'Opis',
  },
  'invoice.qty': {
    en: 'Qty',
    de: 'Anzahl',
    fr: 'Qté',
    it: 'Qtà',
    es: 'Cant.',
    pt: 'Qtd.',
    nl: 'Aantal',
    pl: 'Ilość',
  },
  'invoice.price': {
    en: 'Price',
    de: 'Preis',
    fr: 'Prix',
    it: 'Prezzo',
    es: 'Precio',
    pt: 'Preço',
    nl: 'Prijs',
    pl: 'Cena',
  },
  'invoice.total': {
    en: 'Total',
    de: 'Total',
    fr: 'Total',
    it: 'Totale',
    es: 'Total',
    pt: 'Total',
    nl: 'Totaal',
    pl: 'Suma',
  },
  'invoice.subtotal': {
    en: 'Subtotal',
    de: 'Zwischensumme',
    fr: 'Sous-total',
    it: 'Subtotale',
    es: 'Subtotal',
    pt: 'Subtotal',
    nl: 'Subtotaal',
    pl: 'Suma częściowa',
  },
  'invoice.vat': {
    en: 'VAT',
    de: 'MWST',
    fr: 'TVA',
    it: 'IVA',
    es: 'IVA',
    pt: 'IVA',
    nl: 'BTW',
    pl: 'VAT',
  },
  'invoice.totalIncl': {
    en: 'Total incl. VAT',
    de: 'Total inkl. MWST',
    fr: 'Total TTC',
    it: 'Totale IVA incl.',
    es: 'Total con IVA',
    pt: 'Total c/ IVA',
    nl: 'Totaal incl. BTW',
    pl: 'Suma z VAT',
  },
  'invoice.totalExcl': {
    en: 'Total excl. VAT',
    de: 'Total exkl. MWST',
    fr: 'Total HT',
    it: 'Totale IVA escl.',
    es: 'Total sin IVA',
    pt: 'Total s/ IVA',
    nl: 'Totaal excl. BTW',
    pl: 'Suma bez VAT',
  },
  'invoice.rounding': {
    en: 'Rounding difference',
    de: 'Rundungsdifferenz',
    fr: "Différence d'arrondi",
    it: 'Differenza di arrotondamento',
    es: 'Diferencia de redondeo',
    pt: 'Diferença de arredondamento',
    nl: 'Afrondingsverschil',
    pl: 'Różnica zaokrąglenia',
  },
  'invoice.date': {
    en: 'Date',
    de: 'Datum',
    fr: 'Date',
    it: 'Data',
    es: 'Fecha',
    pt: 'Data',
    nl: 'Datum',
    pl: 'Data',
  },
  'invoice.due': {
    en: 'Due',
    de: 'Fällig',
    fr: 'Échéance',
    it: 'Scadenza',
    es: 'Vencimiento',
    pt: 'Vencimento',
    nl: 'Vervaldatum',
    pl: 'Termin',
  },
  'invoice.paymentTerms': {
    en: 'Payment terms',
    de: 'Zahlungsfrist',
    fr: 'Conditions de paiement',
    it: 'Termini di pagamento',
    es: 'Condiciones de pago',
    pt: 'Condições de pagamento',
    nl: 'Betalingsvoorwaarden',
    pl: 'Warunki płatności',
  },
  'invoice.thanks': {
    en: 'Thank you for your order and your trust.',
    de: 'Vielen Dank für den Auftrag und das Vertrauen.',
    fr: 'Merci pour votre commande et votre confiance.',
    it: "Grazie per l'ordine e la fiducia.",
    es: 'Gracias por su pedido y su confianza.',
    pt: 'Obrigado pelo pedido e pela confiança.',
    nl: 'Bedankt voor uw bestelling en uw vertrouwen.',
    pl: 'Dziękujemy za zamówienie i zaufanie.',
  },
  'invoice.signature': {
    en: 'Signature (proof of service)',
    de: 'Unterschrift (Servicenachweis)',
    fr: 'Signature (preuve de service)',
    it: 'Firma (prova di servizio)',
    es: 'Firma (prueba de servicio)',
    pt: 'Assinatura (comprovativo de serviço)',
    nl: 'Handtekening (servicebewijs)',
    pl: 'Podpis (potwierdzenie serwisu)',
  },
  'invoice.daysNet': {
    en: 'days net',
    de: 'Tage netto',
    fr: 'jours net',
    it: 'giorni netti',
    es: 'días neto',
    pt: 'dias líquidos',
    nl: 'dagen netto',
    pl: 'dni netto',
  },
  'invoice.maintenance': {
    en: 'Maintenance',
    de: 'Wartung',
    fr: 'Maintenance',
    it: 'Manutenzione',
    es: 'Mantenimiento',
    pt: 'Manutenção',
    nl: 'Onderhoud',
    pl: 'Konserwacja',
  },
  'invoice.newAndDisposal': {
    en: 'New devices & disposal',
    de: 'Neugeräte & Entsorgung',
    fr: 'Nouveaux appareils et élimination',
    it: 'Nuovi dispositivi e smaltimento',
    es: 'Dispositivos nuevos y eliminación',
    pt: 'Novos dispositivos e descarte',
    nl: 'Nieuwe apparaten & afvoer',
    pl: 'Nowe urządzenia i utylizacja',
  },
  'invoice.totalNew': {
    en: 'New',
    de: 'Neu',
    fr: 'Nouveau',
    it: 'Nuovo',
    es: 'Nuevo',
    pt: 'Novo',
    nl: 'Nieuw',
    pl: 'Nowy',
  },
  'invoice.disposal': {
    en: 'Disposal',
    de: 'Entsorgung',
    fr: 'Élimination',
    it: 'Smaltimento',
    es: 'Eliminación',
    pt: 'Descarte',
    nl: 'Afvoer',
    pl: 'Utylizacja',
  },
  'invoice.missingPrice': {
    en: 'Missing service price for',
    de: 'Fehlender Servicepreis für',
    fr: 'Prix de service manquant pour',
    it: 'Prezzo del servizio mancante per',
    es: 'Falta precio de servicio para',
    pt: 'Preço de serviço ausente para',
    nl: 'Ontbrekende serviceprijs voor',
    pl: 'Brak ceny usługi dla',
  },
  'invoice.hoseMaintenance': {
    en: 'Hose Maintenance',
    de: 'Wartung Löschposten',
    fr: 'Maintenance lances',
    it: 'Manutenzione manichette',
    es: 'Mantenimiento mangueras',
    pt: 'Manutenção mangueiras',
    nl: 'Slangonderhoud',
    pl: 'Konserwacja węży',
  },
  'invoice.fireHoses': {
    en: 'Fire Hoses',
    de: 'Löschposten',
    fr: 'Lances incendie',
    it: 'Manichette',
    es: 'Mangueras',
    pt: 'Mangueiras',
    nl: 'Brandslangen',
    pl: 'Węże strażackie',
  },
  'invoice.unit': {
    en: 'pcs.',
    de: 'Stk.',
    fr: 'pcs',
    it: 'pz.',
    es: 'uds.',
    pt: 'pç.',
    nl: 'st.',
    pl: 'szt.',
  },
  'invoice.travelSurcharge': {
    en: 'Travel surcharge',
    de: 'Anfahrtspauschale',
    fr: 'Forfait déplacement',
    it: 'Supplemento viaggio',
    es: 'Recargo desplazamiento',
    pt: 'Taxa deslocação',
    nl: 'Reistoeslag',
    pl: 'Ryczałt dojazd',
  },
  'invoice.attn': {
    en: 'Attn.',
    de: 'z.Hd.',
    fr: "À l'att.",
    it: 'C.A.',
    es: 'A/A',
    pt: 'A/C',
    nl: 'T.a.v.',
    pl: 'Do wiad.',
  },
  'invoice.vatOverride': {
    en: 'VAT %',
    de: 'MWST %',
    fr: 'TVA %',
    it: 'IVA %',
    es: 'IVA %',
    pt: 'IVA %',
    nl: 'BTW %',
    pl: 'VAT %',
  },
  'invoice.showGross': {
    en: 'Show prices incl. VAT',
    de: 'Preise inkl. MWST anzeigen',
    fr: 'Afficher les prix TTC',
    it: 'Mostra prezzi IVA incl.',
    es: 'Mostrar precios con IVA',
    pt: 'Mostrar preços com IVA',
    nl: 'Prijzen incl. BTW tonen',
    pl: 'Pokaż ceny z VAT',
  },
  'invoice.inclVat': {
    en: 'incl. VAT',
    de: 'inkl. MWST',
    fr: 'TVA incl.',
    it: 'IVA incl.',
    es: 'IVA incl.',
    pt: 'IVA incl.',
    nl: 'incl. BTW',
    pl: 'z VAT',
  },

  // Settings
  'settings.title': {
    en: 'Settings',
    de: 'Einstellungen',
    fr: 'Paramètres',
    it: 'Impostazioni',
    es: 'Configuración',
    pt: 'Configurações',
    nl: 'Instellingen',
    pl: 'Ustawienia',
  },
  'settings.subtitle': {
    en: 'Company, billing, and inspection rules',
    de: 'Firma, Abrechnung und Kontrollregeln',
    fr: "Entreprise, facturation et règles d'inspection",
    it: 'Azienda, fatturazione e regole di ispezione',
    es: 'Empresa, facturación y reglas de inspección',
    pt: 'Empresa, faturação e regras de inspeção',
    nl: 'Bedrijf, facturatie en inspectieregels',
    pl: 'Firma, rozliczenia i zasady kontroli',
  },
  'settings.company': {
    en: 'Company',
    de: 'Firma',
    fr: 'Entreprise',
    it: 'Azienda',
    es: 'Empresa',
    pt: 'Empresa',
    nl: 'Bedrijf',
    pl: 'Firma',
  },
  'settings.financial': {
    en: 'Financial',
    de: 'Finanzen',
    fr: 'Finances',
    it: 'Finanze',
    es: 'Finanzas',
    pt: 'Finanças',
    nl: 'Financieel',
    pl: 'Finanse',
  },
  'settings.inspectionRules': {
    en: 'Inspection Rules',
    de: 'Kontrollregeln',
    fr: "Règles d'inspection",
    it: 'Regole di ispezione',
    es: 'Reglas de inspección',
    pt: 'Regras de inspeção',
    nl: 'Inspectieregels',
    pl: 'Zasady kontroli',
  },

  // Address dialog
  'address.companyName': {
    en: 'Company / Name',
    de: 'Firma / Name',
    fr: 'Société / Nom',
    it: 'Azienda / Nome',
    es: 'Empresa / Nombre',
    pt: 'Empresa / Nome',
    nl: 'Bedrijf / Naam',
    pl: 'Firma / Nazwa',
  },
  'address.attn': {
    en: 'Address Line 2',
    de: 'Adresszusatz',
    fr: "Complément d'adresse",
    it: 'Riga indirizzo 2',
    es: 'Línea de dirección 2',
    pt: 'Linha de endereço 2',
    nl: 'Adresregel 2',
    pl: 'Wiersz adresu 2',
  },
  'address.street': {
    en: 'Street',
    de: 'Strasse',
    fr: 'Rue',
    it: 'Via',
    es: 'Calle',
    pt: 'Rua',
    nl: 'Straat',
    pl: 'Ulica',
  },
  'address.zip': {
    en: 'ZIP',
    de: 'PLZ',
    fr: 'Code postal',
    it: 'CAP',
    es: 'C.P.',
    pt: 'CEP',
    nl: 'Postcode',
    pl: 'Kod pocztowy',
  },
  'address.city': {
    en: 'City',
    de: 'Ort',
    fr: 'Ville',
    it: 'Città',
    es: 'Ciudad',
    pt: 'Cidade',
    nl: 'Stad',
    pl: 'Miasto',
  },
  'address.phone': {
    en: 'Phone',
    de: 'Telefon',
    fr: 'Téléphone',
    it: 'Telefono',
    es: 'Teléfono',
    pt: 'Telefone',
    nl: 'Telefoon',
    pl: 'Telefon',
  },
  'address.email': {
    en: 'Email',
    de: 'E-Mail',
    fr: 'E-mail',
    it: 'Email',
    es: 'Correo',
    pt: 'E-mail',
    nl: 'E-mail',
    pl: 'E-mail',
  },
  'address.addPhone': {
    en: 'Add phone',
    de: 'Telefon hinzufügen',
    fr: 'Ajouter un téléphone',
    it: 'Aggiungi telefono',
    es: 'Añadir teléfono',
    pt: 'Adicionar telefone',
    nl: 'Telefoon toevoegen',
    pl: 'Dodaj telefon',
  },
  'address.applyToObject': {
    en: 'Apply to Object Address',
    de: 'Auf Objektadresse übernehmen',
    fr: "Appliquer à l'adresse de l'objet",
    it: "Applica all'indirizzo oggetto",
    es: 'Aplicar a dirección del objeto',
    pt: 'Aplicar ao endereço do objeto',
    nl: 'Toepassen op objectadres',
    pl: 'Zastosuj do adresu obiektu',
  },
  'customer.dupWarning': {
    en: 'A customer with this name may already exist:',
    de: 'Ein Kunde mit diesem Namen existiert möglicherweise bereits:',
    fr: 'Un client portant ce nom existe peut-être déjà :',
    it: 'Un cliente con questo nome potrebbe già esistere:',
    es: 'Es posible que ya exista un cliente con este nombre:',
    pt: 'Já pode existir um cliente com este nome:',
    nl: 'Er bestaat mogelijk al een klant met deze naam:',
    pl: 'Klient o tej nazwie może już istnieć:',
  },
  'customer.createAnyway': {
    en: 'Create anyway',
    de: 'Trotzdem erstellen',
    fr: 'Créer quand même',
    it: 'Crea comunque',
    es: 'Crear de todos modos',
    pt: 'Criar mesmo assim',
    nl: 'Toch aanmaken',
    pl: 'Utwórz mimo to',
  },
  'address.enterDetails': {
    en: 'Enter address details.',
    de: 'Adresse eingeben.',
    fr: "Saisir l'adresse.",
    it: 'Inserisci indirizzo.',
    es: 'Introduzca la dirección.',
    pt: 'Insira o endereço.',
    nl: 'Voer adresgegevens in.',
    pl: 'Wprowadź adres.',
  },
  'address.select': {
    en: '— Select —',
    de: '— Auswählen —',
    fr: '— Sélectionner —',
    it: '— Seleziona —',
    es: '— Seleccionar —',
    pt: '— Selecionar —',
    nl: '— Selecteer —',
    pl: '— Wybierz ���',
  },

  // Customer detail (extra keys)
  'customer.notFound': {
    en: 'Customer not found.',
    de: 'Kunde nicht gefunden.',
    fr: 'Client introuvable.',
    it: 'Cliente non trovato.',
    es: 'Cliente no encontrado.',
    pt: 'Cliente não encontrado.',
    nl: 'Klant niet gevonden.',
    pl: 'Klient nie znaleziony.',
  },
  'customer.deleteConfirm': {
    en: 'Delete customer',
    de: 'Kunde löschen',
    fr: 'Supprimer client',
    it: 'Eliminare cliente',
    es: 'Eliminar cliente',
    pt: 'Excluir cliente',
    nl: 'Klant verwijderen',
    pl: 'Usunąć klienta',
  },
  'customer.deleted': {
    en: 'Deleted',
    de: 'Gelöscht',
    fr: 'Supprimé',
    it: 'Eliminato',
    es: 'Eliminado',
    pt: 'Excluído',
    nl: 'Verwijderd',
    pl: 'Usunięto',
  },
  'customer.archiveCreated': {
    en: 'Archive snapshot created',
    de: 'Archiv-Snapshot erstellt',
    fr: 'Archive créée',
    it: 'Archivio creato',
    es: 'Archivo creado',
    pt: 'Arquivo criado',
    nl: 'Archief aangemaakt',
    pl: 'Archiwum utworzone',
  },
  'customer.addressSaved': {
    en: 'Address saved',
    de: 'Adresse gespeichert',
    fr: 'Adresse enregistrée',
    it: 'Indirizzo salvato',
    es: 'Dirección guardada',
    pt: 'Endereço salvo',
    nl: 'Adres opgeslagen',
    pl: 'Adres zapisany',
  },
  'customer.coordsResolved': {
    en: 'Coordinates resolved',
    de: 'Koordinaten aufgelöst',
    fr: 'Coordonnées résolues',
    it: 'Coordinate risolte',
    es: 'Coordenadas resueltas',
    pt: 'Coordenadas resolvidas',
    nl: 'Coördinaten opgelost',
    pl: 'Współrzędne rozwiązane',
  },
  'address.geocodeSave': {
    en: 'Geocode & Save',
    de: 'Geocode & Speichern',
    fr: 'Géocoder & Enregistrer',
    it: 'Geocodifica & Salva',
    es: 'Geocodificar & Guardar',
    pt: 'Geocodificar & Salvar',
    nl: 'Geocodeer & Opslaan',
    pl: 'Geokoduj & Zapisz',
  },
  'customer.copied': {
    en: 'Copied',
    de: 'Kopiert',
    fr: 'Copié',
    it: 'Copiato',
    es: 'Copiado',
    pt: 'Copiado',
    nl: 'Gekopieerd',
    pl: 'Skopiowano',
  },
  'customer.addNotes': {
    en: 'Add notes for this customer...',
    de: 'Notizen für diesen Kunden...',
    fr: 'Notes pour ce client...',
    it: 'Note per questo cliente...',
    es: 'Notas para este cliente...',
    pt: 'Notas para este cliente...',
    nl: 'Notities voor deze klant...',
    pl: 'Notatki dla tego klienta...',
  },
  'customer.noEquipment': {
    en: 'No equipment',
    de: 'Keine Geräte',
    fr: 'Aucun équipement',
    it: 'Nessuna attrezzatura',
    es: 'Sin equipos',
    pt: 'Sem equipamento',
    nl: 'Geen apparatuur',
    pl: 'Brak sprzętu',
  },
  'customer.sortedByFloor': {
    en: 'Sorted by floor / location',
    de: 'Sortiert nach Stockwerk / Standort',
    fr: 'Trié par étage / lieu',
    it: 'Ordinato per piano / luogo',
    es: 'Ordenado por planta / ubicación',
    pt: 'Ordenado por andar / local',
    nl: 'Gesorteerd op verdieping / locatie',
    pl: 'Sortowanie wg piętra / lokalizacji',
  },
  'customer.noExtinguishers': {
    en: 'No extinguishers yet',
    de: 'Noch keine Feuerlöscher',
    fr: "Pas encore d'extincteurs",
    it: 'Nessun estintore',
    es: 'Sin extintores aún',
    pt: 'Sem extintores ainda',
    nl: 'Nog geen brandblussers',
    pl: 'Brak gaśnic',
  },
  'customer.noHoses': {
    en: 'No hoses yet',
    de: 'Noch keine Löschposten',
    fr: 'Pas encore de lances',
    it: 'Nessuna manichetta',
    es: 'Sin mangueras aún',
    pt: 'Sem mangueiras ainda',
    nl: 'Nog geen slangen',
    pl: 'Brak węży',
  },
  'customer.noArchives': {
    en: 'No archives yet',
    de: 'Noch keine Archive',
    fr: "Pas encore d'archives",
    it: 'Nessun archivio',
    es: 'Sin archivos aún',
    pt: 'Sem arquivos ainda',
    nl: 'Nog geen archieven',
    pl: 'Brak archiwów',
  },
  'customer.inspections': {
    en: 'Inspections',
    de: 'Inspektionen',
    fr: 'Inspections',
    it: 'Ispezioni',
    es: 'Inspecciones',
    pt: 'Inspeções',
    nl: 'Inspecties',
    pl: 'Inspekcje',
  },
  'customer.noInspections': {
    en: 'No inspections yet',
    de: 'Noch keine Inspektionen',
    fr: "Pas encore d'inspections",
    it: 'Nessuna ispezione',
    es: 'Sin inspecciones aún',
    pt: 'Sem inspeções ainda',
    nl: 'Nog geen inspecties',
    pl: 'Brak inspekcji',
  },
  'inspection.viewSignature': {
    en: 'View signature',
    de: 'Unterschrift ansehen',
    fr: 'Voir la signature',
    it: 'Vedi firma',
    es: 'Ver firma',
    pt: 'Ver assinatura',
    nl: 'Handtekening bekijken',
    pl: 'Zobacz podpis',
  },
  'customer.readOnlySnapshots': {
    en: 'Read-only snapshots of past inspections',
    de: 'Schreibgeschützte Snapshots vergangener Kontrollen',
    fr: 'Instantanés en lecture seule',
    it: 'Istantanee in sola lettura',
    es: 'Instantáneas de inspecciones pasadas',
    pt: 'Instantâneos de inspeções passadas',
    nl: 'Alleen-lezen snapshots',
    pl: 'Migawki poprzednich kontroli',
  },
  'customer.devices': {
    en: 'devices',
    de: 'Geräte',
    fr: 'appareils',
    it: 'dispositivi',
    es: 'dispositivos',
    pt: 'dispositivos',
    nl: 'apparaten',
    pl: 'urządzeń',
  },
  'customer.tel': {
    en: 'Tel:',
    de: 'Tel:',
    fr: 'Tél:',
    it: 'Tel:',
    es: 'Tel:',
    pt: 'Tel:',
    nl: 'Tel:',
    pl: 'Tel:',
  },

  // Table headers (shared)
  'table.floor': {
    en: 'Floor',
    de: 'Stockwerk',
    fr: 'Étage',
    it: 'Piano',
    es: 'Planta',
    pt: 'Andar',
    nl: 'Verdieping',
    pl: 'Piętro',
  },
  'table.location': {
    en: 'Location',
    de: 'Standort',
    fr: 'Lieu',
    it: 'Luogo',
    es: 'Ubicación',
    pt: 'Local',
    nl: 'Locatie',
    pl: 'Miejsce',
  },
  'table.mfrModel': {
    en: 'Mfr / Model',
    de: 'Hersteller / Modell',
    fr: 'Fabricant / Modèle',
    it: 'Produttore / Modello',
    es: 'Fabricante / Modelo',
    pt: 'Fabricante / Modelo',
    nl: 'Fabrikant / Model',
    pl: 'Producent / Model',
  },
  'table.type': {
    en: 'Type',
    de: 'Typ',
    fr: 'Type',
    it: 'Tipo',
    es: 'Tipo',
    pt: 'Tipo',
    nl: 'Type',
    pl: 'Typ',
  },
  'table.agent': {
    en: 'Class',
    de: 'Brandklasse',
    fr: 'Classe',
    it: 'Classe',
    es: 'Clase',
    pt: 'Classe',
    nl: 'Klasse',
    pl: 'Klasa',
  },
  'table.exactAgent': {
    en: 'Agent',
    de: 'Löschmittel',
    fr: 'Agent',
    it: 'Agente',
    es: 'Agente',
    pt: 'Agente',
    nl: 'Middel',
    pl: 'Środek',
  },
  'table.id': {
    en: 'ID',
    de: 'Nr.',
    fr: 'ID',
    it: 'ID',
    es: 'ID',
    pt: 'ID',
    nl: 'ID',
    pl: 'ID',
  },
  'table.year': {
    en: 'Year',
    de: 'Jahr',
    fr: 'Année',
    it: 'Anno',
    es: 'Año',
    pt: 'Ano',
    nl: 'Jaar',
    pl: 'Rok',
  },
  'table.last': {
    en: 'Last',
    de: 'Letzte',
    fr: 'Dernière',
    it: 'Ultima',
    es: 'Última',
    pt: 'Última',
    nl: 'Laatste',
    pl: 'Ostatnia',
  },
  'table.next': {
    en: 'Next',
    de: 'Nächste',
    fr: 'Prochaine',
    it: 'Prossima',
    es: 'Próxima',
    pt: 'Próxima',
    nl: 'Volgende',
    pl: 'Następna',
  },
  'table.billing': {
    en: 'Billing',
    de: 'Abrechnung',
    fr: 'Facturation',
    it: 'Fatturazione',
    es: 'Facturación',
    pt: 'Faturação',
    nl: 'Facturatie',
    pl: 'Rozliczenie',
  },
  'table.actions': {
    en: 'Actions',
    de: 'Aktionen',
    fr: 'Actions',
    it: 'Azioni',
    es: 'Acciones',
    pt: 'Ações',
    nl: 'Acties',
    pl: 'Akcje',
  },
  'table.name': {
    en: 'Name',
    de: 'Name',
    fr: 'Nom',
    it: 'Nome',
    es: 'Nombre',
    pt: 'Nome',
    nl: 'Naam',
    pl: 'Nazwa',
  },
  'table.role': {
    en: 'Role',
    de: 'Rolle',
    fr: 'Rôle',
    it: 'Ruolo',
    es: 'Rol',
    pt: 'Função',
    nl: 'Rol',
    pl: 'Rola',
  },
  'table.status': {
    en: 'Status',
    de: 'Status',
    fr: 'Statut',
    it: 'Stato',
    es: 'Estado',
    pt: 'Estado',
    nl: 'Status',
    pl: 'Status',
  },
  'table.avatar': {
    en: 'Avatar',
    de: 'Avatar',
    fr: 'Avatar',
    it: 'Avatar',
    es: 'Avatar',
    pt: 'Avatar',
    nl: 'Avatar',
    pl: 'Avatar',
  },
  'table.articleNr': {
    en: 'Article #',
    de: 'Artikel-Nr.',
    fr: 'N° article',
    it: 'N° articolo',
    es: 'N° artículo',
    pt: 'N° artigo',
    nl: 'Artikelnr.',
    pl: 'Nr artykułu',
  },
  'table.compatible': {
    en: 'Compatible',
    de: 'Kompatibel',
    fr: 'Compatible',
    it: 'Compatibile',
    es: 'Compatible',
    pt: 'Compatível',
    nl: 'Compatibel',
    pl: 'Kompatybilne',
  },
  'table.price': {
    en: 'Price',
    de: 'Preis',
    fr: 'Prix',
    it: 'Prezzo',
    es: 'Precio',
    pt: 'Preço',
    nl: 'Prijs',
    pl: 'Cena',
  },
  'table.code': {
    en: 'Code',
    de: 'Code',
    fr: 'Code',
    it: 'Codice',
    es: 'Código',
    pt: 'Código',
    nl: 'Code',
    pl: 'Kod',
  },
  'table.german': {
    en: 'German',
    de: 'Deutsch',
    fr: 'Allemand',
    it: 'Tedesco',
    es: 'Alemán',
    pt: 'Alemão',
    nl: 'Duits',
    pl: 'Niemiecki',
  },
  'table.english': {
    en: 'English',
    de: 'Englisch',
    fr: 'Anglais',
    it: 'Inglese',
    es: 'Inglés',
    pt: 'Inglês',
    nl: 'Engels',
    pl: 'Angielski',
  },
  'table.deviceType': {
    en: 'Device Type',
    de: 'Gerätetyp',
    fr: "Type d'appareil",
    it: 'Tipo dispositivo',
    es: 'Tipo de dispositivo',
    pt: 'Tipo de dispositivo',
    nl: 'Apparaattype',
    pl: 'Typ urządzenia',
  },
  'table.description': {
    en: 'Description',
    de: 'Beschreibung',
    fr: 'Description',
    it: 'Descrizione',
    es: 'Descripción',
    pt: 'Descrição',
    nl: 'Beschrijving',
    pl: 'Opis',
  },

  // Hose edit
  'hose.fireHose': {
    en: 'Fire Hose',
    de: 'Löschposten',
    fr: 'Lance incendie',
    it: 'Manichetta',
    es: 'Manguera',
    pt: 'Mangueira',
    nl: 'Brandslang',
    pl: 'Wąż strażacki',
  },
  'hose.equipment': {
    en: 'Equipment',
    de: 'Ausrüstung',
    fr: 'Équipement',
    it: 'Attrezzatura',
    es: 'Equipo',
    pt: 'Equipamento',
    nl: 'Apparatuur',
    pl: 'Sprzęt',
  },
  'hose.length': {
    en: 'Length (m)',
    de: 'Länge (m)',
    fr: 'Longueur (m)',
    it: 'Lunghezza (m)',
    es: 'Longitud (m)',
    pt: 'Comprimento (m)',
    nl: 'Lengte (m)',
    pl: 'Długość (m)',
  },
  'hose.diameter': {
    en: 'Diameter',
    de: 'Durchmesser',
    fr: 'Diamètre',
    it: 'Diametro',
    es: 'Diámetro',
    pt: 'Diâmetro',
    nl: 'Diameter',
    pl: 'Średnica',
  },
  'hose.connection': {
    en: 'Connection',
    de: 'Anschluss',
    fr: 'Raccord',
    it: 'Connessione',
    es: 'Conexión',
    pt: 'Conexão',
    nl: 'Aansluiting',
    pl: 'Złączka',
  },
  'hose.conditionChecks': {
    en: 'Condition Checks',
    de: 'Zustandsprüfungen',
    fr: "Contrôles d'état",
    it: 'Controlli condizione',
    es: 'Comprobaciones de estado',
    pt: 'Verificações de condição',
    nl: 'Conditiecontroles',
    pl: 'Kontrole stanu',
  },
  'cond.fireCock': {
    en: 'Fire Cock',
    de: 'Feuerhahn',
    fr: 'Robinet',
    it: 'Rubinetto',
    es: 'Grifo',
    pt: 'Torneira',
    nl: 'Brandkraan',
    pl: 'Zawór',
  },
  'cond.seals': {
    en: 'Seals',
    de: 'Dichtung Anschluss',
    fr: 'Joints',
    it: 'Guarnizioni',
    es: 'Juntas',
    pt: 'Vedações',
    nl: 'Afdichtingen',
    pl: 'Uszczelki',
  },
  'cond.hose': {
    en: 'Hose',
    de: 'Schlauch',
    fr: 'Tuyau',
    it: 'Tubo',
    es: 'Manguera',
    pt: 'Mangueira',
    nl: 'Slang',
    pl: 'Wąż',
  },
  'cond.nozzle': {
    en: 'Nozzle',
    de: 'Strahlrohr',
    fr: 'Lance',
    it: 'Lancia',
    es: 'Boquilla',
    pt: 'Agulheta',
    nl: 'Straalpijp',
    pl: 'Prądownica',
  },
  'cond.axle': {
    en: 'Axle',
    de: 'Achse',
    fr: 'Axe',
    it: 'Asse',
    es: 'Eje',
    pt: 'Eixo',
    nl: 'As',
    pl: 'Oś',
  },
  'cond.swivelArm': {
    en: 'Swivel Arm',
    de: 'Schwenkarm',
    fr: 'Bras pivotant',
    it: 'Braccio girevole',
    es: 'Brazo giratorio',
    pt: 'Braço giratório',
    nl: 'Zwenkarm',
    pl: 'Ramię obrotowe',
  },
  'cond.doorLock': {
    en: 'Door Lock',
    de: 'Tür und Schloss',
    fr: 'Serrure de porte',
    it: 'Serratura',
    es: 'Cerradura',
    pt: 'Fechadura',
    nl: 'Deurslot',
    pl: 'Zamek drzwi',
  },
  'cond.water': {
    en: 'Water',
    de: 'Wasser',
    fr: 'Eau',
    it: 'Acqua',
    es: 'Agua',
    pt: 'Água',
    nl: 'Water',
    pl: 'Woda',
  },
  // Check-specific findings (client #97).
  'cond.dripping': {
    en: 'Dripping',
    de: 'Tropft',
    fr: 'Goutte',
    it: 'Gocciola',
    es: 'Gotea',
    pt: 'Pinga',
    nl: 'Druppelt',
    pl: 'Kapie',
  },
  'cond.rusty': {
    en: 'Rusty',
    de: 'Rostig',
    fr: 'Rouillé',
    it: 'Arrugginito',
    es: 'Oxidado',
    pt: 'Enferrujado',
    nl: 'Roestig',
    pl: 'Zardzewiały',
  },
  'cond.sandy': {
    en: 'Sandy',
    de: 'Sandig',
    fr: 'Sableux',
    it: 'Sabbioso',
    es: 'Arenoso',
    pt: 'Arenoso',
    nl: 'Zanderig',
    pl: 'Piaszczysty',
  },
  'hose.ok': {
    en: 'OK',
    de: 'OK',
    fr: 'OK',
    it: 'OK',
    es: 'OK',
    pt: 'OK',
    nl: 'OK',
    pl: 'OK',
  },
  'hose.notOk': {
    en: 'Not OK',
    de: 'Nicht OK',
    fr: 'Non OK',
    it: 'Non OK',
    es: 'No OK',
    pt: 'Não OK',
    nl: 'Niet OK',
    pl: 'Nie OK',
  },
  'hose.defectNote': {
    en: 'What is wrong?',
    de: 'Was ist defekt?',
    fr: 'Quel est le problème ?',
    it: 'Qual è il problema?',
    es: '¿Qué está mal?',
    pt: 'O que está errado?',
    nl: 'Wat is er mis?',
    pl: 'Co jest nie tak?',
  },
  'hoseOptions.connections': {
    en: 'Connections',
    de: 'Anschlüsse',
    fr: 'Raccords',
    it: 'Raccordi',
    es: 'Conexiones',
    pt: 'Ligações',
    nl: 'Koppelingen',
    pl: 'Przyłącza',
  },
  'hoseOptions.connectionPlaceholder': {
    en: 'e.g. Storz C',
    de: 'z.B. Storz C',
    fr: 'p.ex. Storz C',
    it: 'es. Storz C',
    es: 'p.ej. Storz C',
    pt: 'p.ex. Storz C',
    nl: 'bijv. Storz C',
    pl: 'np. Storz C',
  },
  'section.add': {
    en: 'Add section',
    de: 'Titelblock hinzufügen',
    fr: 'Ajouter une section',
    it: 'Aggiungi sezione',
    es: 'Añadir sección',
    pt: 'Adicionar secção',
    nl: 'Sectie toevoegen',
    pl: 'Dodaj sekcję',
  },
  'section.edit': {
    en: 'Edit section',
    de: 'Titelblock bearbeiten',
    fr: 'Modifier la section',
    it: 'Modifica sezione',
    es: 'Editar sección',
    pt: 'Editar secção',
    nl: 'Sectie bewerken',
    pl: 'Edytuj sekcję',
  },
  'section.title': {
    en: 'Title',
    de: 'Titel',
    fr: 'Titre',
    it: 'Titolo',
    es: 'Título',
    pt: 'Título',
    nl: 'Titel',
    pl: 'Tytuł',
  },
  'section.placeholder': {
    en: 'e.g. Basement / Boiler room',
    de: 'z.B. Untergeschoss / Heizraum',
    fr: 'p.ex. Sous-sol / Chaufferie',
    it: 'es. Seminterrato / Locale caldaia',
    es: 'p.ej. Sótano / Sala de calderas',
    pt: 'p.ex. Cave / Sala da caldeira',
    nl: 'bijv. Kelder / Stookruimte',
    pl: 'np. Piwnica / Kotłownia',
  },
  'section.colour': {
    en: 'Colour',
    de: 'Farbe',
    fr: 'Couleur',
    it: 'Colore',
    es: 'Color',
    pt: 'Cor',
    nl: 'Kleur',
    pl: 'Kolor',
  },
  'section.remove': {
    en: 'Delete title',
    de: 'Titel löschen',
    fr: 'Supprimer le titre',
    it: 'Elimina titolo',
    es: 'Eliminar título',
    pt: 'Eliminar título',
    nl: 'Titel verwijderen',
    pl: 'Usuń tytuł',
  },
  'section.insertHere': {
    en: 'Insert section here',
    de: 'Titelblock hier einfügen',
    fr: 'Insérer une section ici',
    it: 'Inserisci sezione qui',
    es: 'Insertar sección aquí',
    pt: 'Inserir secção aqui',
    nl: 'Sectie hier invoegen',
    pl: 'Wstaw sekcję tutaj',
  },
  // Reordering whole dividers, the second half of client #30.
  'section.moveUp': {
    en: 'Move section up',
    de: 'Abschnitt nach oben',
    fr: 'Déplacer la section vers le haut',
    it: 'Sposta la sezione in alto',
    es: 'Mover la sección hacia arriba',
    pt: 'Mover a secção para cima',
    nl: 'Sectie omhoog verplaatsen',
    pl: 'Przenieś sekcję w górę',
  },
  'section.moveDown': {
    en: 'Move section down',
    de: 'Abschnitt nach unten',
    fr: 'Déplacer la section vers le bas',
    it: 'Sposta la sezione in basso',
    es: 'Mover la sección hacia abajo',
    pt: 'Mover a secção para baixo',
    nl: 'Sectie omlaag verplaatsen',
    pl: 'Przenieś sekcję w dół',
  },
  'hose.replacedOn': {
    en: 'Replaced on',
    de: 'Ersetzt am',
    fr: 'Remplacé le',
    it: 'Sostituito il',
    es: 'Sustituido el',
    pt: 'Substituído em',
    nl: 'Vervangen op',
    pl: 'Wymieniono dnia',
  },
  'hose.serviceReport': {
    en: 'Service Report',
    de: 'Servicebericht',
    fr: 'Rapport de service',
    it: 'Rapporto di servizio',
    es: 'Informe de servicio',
    pt: 'Relatório de serviço',
    nl: 'Servicerapport',
    pl: 'Raport serwisowy',
  },
  'hose.serviceDate': {
    en: 'Service Date',
    de: 'Servicedatum',
    fr: 'Date de service',
    it: 'Data servizio',
    es: 'Fecha de servicio',
    pt: 'Data de serviço',
    nl: 'Servicedatum',
    pl: 'Data serwisu',
  },
  'hose.nextService': {
    en: 'Next Service',
    de: 'Nächster Service',
    fr: 'Prochain service',
    it: 'Prossimo servizio',
    es: 'Próximo servicio',
    pt: 'Próximo serviço',
    nl: 'Volgende service',
    pl: 'Następny serwis',
  },
  'hose.restingPressure': {
    en: 'Resting Pressure',
    de: 'Ruhedruck',
    fr: 'Pression au repos',
    it: 'Pressione di riposo',
    es: 'Presión en reposo',
    pt: 'Pressão de repouso',
    nl: 'Rustdruk',
    pl: 'Ciśnienie spoczynkowe',
  },
  'hose.flowPressure': {
    en: 'Flow Pressure',
    de: 'Fliessdruck',
    fr: 'Pression de débit',
    it: 'Pressione di flusso',
    es: 'Presión de flujo',
    pt: 'Pressão de fluxo',
    nl: 'Stroomdruk',
    pl: 'Ciśnienie przepływu',
  },
  'hose.flowRate': {
    en: 'Flow Rate',
    de: 'Durchfluss',
    fr: 'Débit',
    it: 'Portata',
    es: 'Caudal',
    pt: 'Vazão',
    nl: 'Debiet',
    pl: 'Przepływ',
  },
  'hose.pressureTestDate': {
    en: '12-Bar Test Date',
    de: '12-Bar Prüfdatum',
    fr: 'Date test 12 bar',
    it: 'Data test 12 bar',
    es: 'Fecha prueba 12 bar',
    pt: 'Data teste 12 bar',
    nl: '12-bar testdatum',
    pl: 'Data testu 12 bar',
  },
  'hose.serviceTechnician': {
    en: 'Service Technician',
    de: 'Servicetechniker',
    fr: 'Technicien',
    it: 'Tecnico',
    es: 'Técnico',
    pt: 'Técnico',
    nl: 'Servicemonteur',
    pl: 'Serwisant',
  },
  'hose.hoseStandard': {
    en: 'Per SN EN 671-3: annual inspection + 5-year pressure test',
    de: 'Gemäss SN EN 671-3: jährliche Kontrolle + 5-jährige Druckprüfung',
    fr: 'Selon SN EN 671-3 : inspection annuelle + test de pression tous les 5 ans',
    it: 'Secondo SN EN 671-3: ispezione annuale + test pressione ogni 5 anni',
    es: 'Según SN EN 671-3: inspección anual + prueba de presión cada 5 años',
    pt: 'Conforme SN EN 671-3: inspeção anual + teste de pressão a cada 5 anos',
    nl: 'Volgens SN EN 671-3: jaarlijkse inspectie + 5-jaarlijkse druktest',
    pl: 'Wg SN EN 671-3: roczna kontrola + test ciśnieniowy co 5 lat',
  },
  'hose.addHose': {
    en: 'Add Hose',
    de: 'Löschposten hinzufügen',
    fr: 'Ajouter lance',
    it: 'Aggiungi manichetta',
    es: 'Añadir manguera',
    pt: 'Adicionar mangueira',
    nl: 'Slang toevoegen',
    pl: 'Dodaj wąż',
  },
  'hose.condition': {
    en: 'Condition',
    de: 'Zustand',
    fr: 'État',
    it: 'Condizione',
    es: 'Condición',
    pt: 'Condição',
    nl: 'Conditie',
    pl: 'Stan',
  },
  'hose.service': {
    en: 'Service',
    de: 'Service',
    fr: 'Service',
    it: 'Servizio',
    es: 'Servicio',
    pt: 'Serviço',
    nl: 'Service',
    pl: 'Serwis',
  },

  // Hose Options admin
  'hoseOptions.title': {
    en: 'Hose Options',
    de: 'Schlauchoptionen',
    fr: 'Options tuyaux',
    it: 'Opzioni manichetta',
    es: 'Opciones manguera',
    pt: 'Opções mangueira',
    nl: 'Slangopties',
    pl: 'Opcje węży',
  },
  'hoseOptions.subtitle': {
    en: 'Configure dropdown options for fire hose forms',
    de: 'Dropdown-Optionen für Löschposten-Formulare konfigurieren',
    fr: 'Configurer les options des formulaires lance incendie',
    it: 'Configura le opzioni dei moduli manichetta',
    es: 'Configurar opciones de formularios de manguera',
    pt: 'Configurar opções dos formulários de mangueira',
    nl: 'Dropdown-opties configureren voor brandslangformulieren',
    pl: 'Konfiguruj opcje formularzy węży strażackich',
  },
  'hoseOptions.hoseDiameters': {
    en: 'Hose Diameters',
    de: 'Schlauchdurchmesser',
    fr: 'Diamètres tuyaux',
    it: 'Diametri tubo',
    es: 'Diámetros manguera',
    pt: 'Diâmetros mangueira',
    nl: 'Slangdiameters',
    pl: 'Średnice węży',
  },
  'hoseOptions.connectionDiameters': {
    en: 'Connection Diameters',
    de: 'Anschlussdurchmesser',
    fr: 'Diamètres raccord',
    it: 'Diametri raccordo',
    es: 'Diámetros conexión',
    pt: 'Diâmetros conexão',
    nl: 'Aansluitdiameters',
    pl: 'Średnice złączy',
  },
  'hoseOptions.hoseModels': {
    en: 'Hose Models',
    de: 'Schlauchmodelle',
    fr: 'Modèles de tuyaux',
    it: 'Modelli manichetta',
    es: 'Modelos de manguera',
    pt: 'Modelos de mangueira',
    nl: 'Slangmodellen',
    pl: 'Modele węży',
  },
  'hoseOptions.addValue': {
    en: 'Add',
    de: 'Hinzufügen',
    fr: 'Ajouter',
    it: 'Aggiungi',
    es: 'Añadir',
    pt: 'Adicionar',
    nl: 'Toevoegen',
    pl: 'Dodaj',
  },
  'hoseOptions.placeholder': {
    en: 'e.g. 25 mm',
    de: 'z.B. 25 mm',
    fr: 'p.ex. 25 mm',
    it: 'es. 25 mm',
    es: 'p.ej. 25 mm',
    pt: 'ex. 25 mm',
    nl: 'bijv. 25 mm',
    pl: 'np. 25 mm',
  },
  'hoseOptions.modelPlaceholder': {
    en: 'e.g. L30',
    de: 'z.B. L30',
    fr: 'p.ex. L30',
    it: 'es. L30',
    es: 'p.ej. L30',
    pt: 'ex. L30',
    nl: 'bijv. L30',
    pl: 'np. L30',
  },
  'hoseOptions.empty': {
    en: 'No entries yet. Add one above.',
    de: 'Noch keine Einträge. Oben hinzufügen.',
    fr: 'Aucune entrée. Ajoutez-en ci-dessus.',
    it: 'Nessuna voce. Aggiungine una sopra.',
    es: 'Sin entradas. Añade una arriba.',
    pt: 'Sem entradas. Adicione uma acima.',
    nl: 'Nog geen items. Voeg er een toe.',
    pl: 'Brak wpisów. Dodaj powyżej.',
  },

  // Overview (extra)
  'overview.customers': {
    en: 'customers',
    de: 'Kunden',
    fr: 'clients',
    it: 'clienti',
    es: 'clientes',
    pt: 'clientes',
    nl: 'klanten',
    pl: 'klientów',
  },
  'overview.newCustomer': {
    en: 'New Customer',
    de: 'Neuer Kunde',
    fr: 'Nouveau client',
    it: 'Nuovo cliente',
    es: 'Nuevo cliente',
    pt: 'Novo cliente',
    nl: 'Nieuwe klant',
    pl: 'Nowy klient',
  },
  'overview.searchPlaceholder': {
    en: 'Search name, street, city, ID...',
    de: 'Name, Strasse, Ort, ID suchen...',
    fr: 'Rechercher nom, rue, ville, ID...',
    it: 'Cerca nome, via, città, ID...',
    es: 'Buscar nombre, calle, ciudad, ID...',
    pt: 'Pesquisar nome, rua, cidade, ID...',
    nl: 'Zoek naam, straat, stad, ID...',
    pl: 'Szukaj nazwy, ulicy, miasta, ID...',
  },
  'overview.allStatuses': {
    en: 'All statuses',
    de: 'Alle Status',
    fr: 'Tous les statuts',
    it: 'Tutti gli stati',
    es: 'Todos los estados',
    pt: 'Todos os estados',
    nl: 'Alle statussen',
    pl: 'Wszystkie statusy',
  },
  'overview.allWorkers': {
    en: 'All workers',
    de: 'Alle Mitarbeiter',
    fr: 'Tous les techniciens',
    it: 'Tutti i tecnici',
    es: 'Todos los técnicos',
    pt: 'Todos os técnicos',
    nl: 'Alle medewerkers',
    pl: 'Wszyscy pracownicy',
  },
  'overview.allCities': {
    en: 'All cities',
    de: 'Alle Orte',
    fr: 'Toutes les villes',
    it: 'Tutte le città',
    es: 'Todas las ciudades',
    pt: 'Todas as cidades',
    nl: 'Alle steden',
    pl: 'Wszystkie miasta',
  },
  'overview.company': {
    en: 'Company',
    de: 'Firma',
    fr: 'Société',
    it: 'Azienda',
    es: 'Empresa',
    pt: 'Empresa',
    nl: 'Bedrijf',
    pl: 'Firma',
  },
  'overview.nextInsp': {
    en: 'Next Insp.',
    de: 'Nächste Kontr.',
    fr: 'Proch. insp.',
    it: 'Pross. isp.',
    es: 'Próx. insp.',
    pt: 'Próx. insp.',
    nl: 'Volg. insp.',
    pl: 'Nast. kontr.',
  },
  'overview.viewList': {
    en: 'List',
    de: 'Liste',
    fr: 'Liste',
    it: 'Elenco',
    es: 'Lista',
    pt: 'Lista',
    nl: 'Lijst',
    pl: 'Lista',
  },
  'overview.viewByBilling': {
    en: 'By billing party',
    de: 'Nach Rechnungsempfänger',
    fr: 'Par destinataire de facture',
    it: 'Per destinatario fattura',
    es: 'Por destinatario de factura',
    pt: 'Por destinatário de fatura',
    nl: 'Per factuurontvanger',
    pl: 'Wg odbiorcy faktury',
  },
  'overview.ungrouped': {
    en: 'Ungrouped / no billing party',
    de: 'Ohne Rechnungsempfänger',
    fr: 'Sans destinataire de facture',
    it: 'Senza destinatario fattura',
    es: 'Sin destinatario de factura',
    pt: 'Sem destinatário de fatura',
    nl: 'Zonder factuurontvanger',
    pl: 'Bez odbiorcy faktury',
  },
  'overview.sites': {
    en: 'Sites',
    de: 'Standorte',
    fr: 'Sites',
    it: 'Siti',
    es: 'Sitios',
    pt: 'Locais',
    nl: 'Locaties',
    pl: 'Lokalizacje',
  },

  // Workers
  'workers.title': {
    en: 'Workers',
    de: 'Mitarbeiter',
    fr: 'Techniciens',
    it: 'Tecnici',
    es: 'Técnicos',
    pt: 'Técnicos',
    nl: 'Medewerkers',
    pl: 'Pracownicy',
  },
  'workers.subtitle': {
    en: 'Service technicians',
    de: 'Servicetechniker',
    fr: 'Techniciens de service',
    it: 'Tecnici di servizio',
    es: 'Técnicos de servicio',
    pt: 'Técnicos de serviço',
    nl: 'Servicemonteurs',
    pl: 'Serwisanci',
  },
  'workers.newWorker': {
    en: 'New Worker',
    de: 'Neuer Mitarbeiter',
    fr: 'Nouveau technicien',
    it: 'Nuovo tecnico',
    es: 'Nuevo técnico',
    pt: 'Novo técnico',
    nl: 'Nieuwe medewerker',
    pl: 'Nowy pracownik',
  },
  'workers.team': {
    en: 'Team',
    de: 'Team',
    fr: 'Équipe',
    it: 'Team',
    es: 'Equipo',
    pt: 'Equipa',
    nl: 'Team',
    pl: 'Zespół',
  },
  'workers.active': {
    en: 'Active',
    de: 'Aktiv',
    fr: 'Actif',
    it: 'Attivo',
    es: 'Activo',
    pt: 'Ativo',
    nl: 'Actief',
    pl: 'Aktywny',
  },
  'workers.inactive': {
    en: 'Inactive',
    de: 'Inaktiv',
    fr: 'Inactif',
    it: 'Inattivo',
    es: 'Inactivo',
    pt: 'Inativo',
    nl: 'Inactief',
    pl: 'Nieaktywny',
  },
  'workers.password': L('Password'),
  'workers.passwordKeep': L('Leave blank to keep current'),
  'workers.emailRequired': L('Email is required'),
  'workers.passwordTooShort': L('Password must be at least 8 characters'),
  'workers.noTenant': L('No tenant context — cannot manage users'),
  'workers.saveFailed': L('Could not save worker'),
  'workers.deleteFailed': L('Could not delete worker'),

  // Spare parts
  'parts.title': {
    en: 'Spare Parts',
    de: 'Ersatzteile',
    fr: 'Pièces de rechange',
    it: 'Ricambi',
    es: 'Repuestos',
    pt: 'Peças sobressalentes',
    nl: 'Onderdelen',
    pl: 'Części zamienne',
  },
  'parts.subtitle': {
    en: 'Parts catalog with article numbers and pricing',
    de: 'Teilekatalog mit Artikelnummern und Preisen',
    fr: "Catalogue avec numéros d'articles et tarifs",
    it: 'Catalogo pezzi con numeri articolo e prezzi',
    es: 'Catálogo con números de artículo y precios',
    pt: 'Catálogo com números de artigo e preços',
    nl: 'Onderdelencatalogus met artikelnummers en prijzen',
    pl: 'Katalog z numerami artykułów i cenami',
  },
  'parts.newPart': {
    en: 'New Part',
    de: 'Neues Teil',
    fr: 'Nouvelle pièce',
    it: 'Nuovo ricambio',
    es: 'Nuevo repuesto',
    pt: 'Nova peça',
    nl: 'Nieuw onderdeel',
    pl: 'Nowa część',
  },
  'parts.extinguisher': {
    en: 'Extinguisher',
    de: 'Feuerlöscher',
    fr: 'Extincteur',
    it: 'Estintore',
    es: 'Extintor',
    pt: 'Extintor',
    nl: 'Brandblusser',
    pl: 'Gaśnica',
  },
  'parts.hose': {
    en: 'Hose',
    de: 'Löschposten',
    fr: 'Lance',
    it: 'Manichetta',
    es: 'Manguera',
    pt: 'Mangueira',
    nl: 'Slang',
    pl: 'Wąż',
  },
  'parts.searchPlaceholder': {
    en: 'Search article or name',
    de: 'Artikel oder Name suchen',
    fr: 'Rechercher article ou nom',
    it: 'Cerca articolo o nome',
    es: 'Buscar artículo o nombre',
    pt: 'Pesquisar artigo ou nome',
    nl: 'Zoek artikel of naam',
    pl: 'Szukaj artykułu lub nazwy',
  },
  'parts.priceWithVat': {
    en: 'Prices incl. VAT',
    de: 'Preise inkl. MwSt.',
    fr: 'Prix TTC',
    it: 'Prezzi IVA incl.',
    es: 'Precios con IVA',
    pt: 'Preços com IVA',
    nl: 'Prijzen incl. btw',
    pl: 'Ceny z VAT',
  },
  'parts.priceExclVat': {
    en: 'Prices excl. VAT',
    de: 'Preise exkl. MwSt.',
    fr: 'Prix HT',
    it: 'Prezzi IVA escl.',
    es: 'Precios sin IVA',
    pt: 'Preços sem IVA',
    nl: 'Prijzen excl. btw',
    pl: 'Ceny bez VAT',
  },
  // Client #3 — paired excl./incl. VAT inputs in the price edit dialogs.
  // Singular labels (the 'parts.price*Vat' pair above titles a table toggle).
  'price.exclVat': {
    en: 'Price excl. VAT',
    de: 'Preis exkl. MwSt.',
    fr: 'Prix HT',
    it: 'Prezzo IVA escl.',
    es: 'Precio sin IVA',
    pt: 'Preço sem IVA',
    nl: 'Prijs excl. btw',
    pl: 'Cena bez VAT',
  },
  'price.inclVat': {
    en: 'Price incl. VAT',
    de: 'Preis inkl. MwSt.',
    fr: 'Prix TTC',
    it: 'Prezzo IVA incl.',
    es: 'Precio con IVA',
    pt: 'Preço com IVA',
    nl: 'Prijs incl. btw',
    pl: 'Cena z VAT',
  },
  'price.vatPairHint': {
    en: 'Edit either field — the other is recalculated. VAT rate',
    de: 'Beliebiges Feld bearbeiten — das andere wird neu berechnet. MwSt.-Satz',
    fr: "Modifiez l'un des champs — l'autre est recalculé. Taux de TVA",
    it: "Modifica uno dei campi — l'altro viene ricalcolato. Aliquota IVA",
    es: 'Edite cualquier campo — el otro se recalcula. Tipo de IVA',
    pt: 'Edite qualquer campo — o outro é recalculado. Taxa de IVA',
    nl: 'Bewerk een van beide velden — het andere wordt herberekend. Btw-tarief',
    pl: 'Edytuj dowolne pole — drugie zostanie przeliczone. Stawka VAT',
  },
  'parts.compatibleModels': {
    en: 'Compatible models',
    de: 'Kompatible Modelle',
    fr: 'Modèles compatibles',
    it: 'Modelli compatibili',
    es: 'Modelos compatibles',
    pt: 'Modelos compatíveis',
    nl: 'Compatibele modellen',
    pl: 'Kompatybilne modele',
  },
  'parts.compatibleModelsHint': {
    en: 'Leave empty to allow this part on all models',
    de: 'Leer lassen, um dieses Teil für alle Modelle zu erlauben',
    fr: 'Laisser vide pour autoriser cette pièce sur tous les modèles',
    it: 'Lascia vuoto per consentire questo pezzo su tutti i modelli',
    es: 'Dejar vacío para permitir esta pieza en todos los modelos',
    pt: 'Deixe vazio para permitir esta peça em todos os modelos',
    nl: 'Laat leeg om dit onderdeel voor alle modellen toe te staan',
    pl: 'Pozostaw puste, aby dopuścić tę część do wszystkich modeli',
  },
  'parts.searchModels': {
    en: 'Search models…',
    de: 'Modelle suchen…',
    fr: 'Rechercher des modèles…',
    it: 'Cerca modelli…',
    es: 'Buscar modelos…',
    pt: 'Pesquisar modelos…',
    nl: 'Modellen zoeken…',
    pl: 'Szukaj modeli…',
  },
  'parts.compatibleCount': {
    en: 'compatible',
    de: 'kompatibel',
    fr: 'compatibles',
    it: 'compatibili',
    es: 'compatibles',
    pt: 'compatíveis',
    nl: 'compatibel',
    pl: 'kompatybilnych',
  },
  'parts.articleNrRenameHint': {
    en: 'Changing the article number renames it everywhere it is used.',
    de: 'Das Ändern der Artikelnummer benennt sie überall um, wo sie verwendet wird.',
    fr: "Modifier le numéro d'article le renomme partout où il est utilisé.",
    it: 'La modifica del numero articolo lo rinomina ovunque venga utilizzato.',
    es: 'Cambiar el número de artículo lo renombra en todos los lugares donde se usa.',
    pt: 'Alterar o número do artigo renomeia-o em todos os locais onde é usado.',
    nl: 'Het wijzigen van het artikelnummer hernoemt het overal waar het wordt gebruikt.',
    pl: 'Zmiana numeru artykułu zmienia jego nazwę wszędzie tam, gdzie jest używany.',
  },

  // Manufacturers
  'mfr.title': {
    en: 'Manufacturers',
    de: 'Hersteller',
    fr: 'Fabricants',
    it: 'Produttori',
    es: 'Fabricantes',
    pt: 'Fabricantes',
    nl: 'Fabrikanten',
    pl: 'Producenci',
  },
  'mfr.subtitle': {
    en: 'Equipment brands and their model catalogs',
    de: 'Geräte-Marken und Modellkataloge',
    fr: 'Marques et catalogues de modèles',
    it: 'Marche e cataloghi modelli',
    es: 'Marcas y catálogos de modelos',
    pt: 'Marcas e catálogos de modelos',
    nl: 'Merken en modelcatalogi',
    pl: 'Marki i katalogi modeli',
  },
  'mfr.newMfr': {
    en: 'New Manufacturer',
    de: 'Neuer Hersteller',
    fr: 'Nouveau fabricant',
    it: 'Nuovo produttore',
    es: 'Nuevo fabricante',
    pt: 'Novo fabricante',
    nl: 'Nieuwe fabrikant',
    pl: 'Nowy producent',
  },
  'mfr.catalog': {
    en: 'Catalog',
    de: 'Katalog',
    fr: 'Catalogue',
    it: 'Catalogo',
    es: 'Catálogo',
    pt: 'Catálogo',
    nl: 'Catalogus',
    pl: 'Katalog',
  },
  'mfr.models': {
    en: 'models',
    de: 'Modelle',
    fr: 'modèles',
    it: 'modelli',
    es: 'modelos',
    pt: 'modelos',
    nl: 'modellen',
    pl: 'modeli',
  },
  'mfr.addModel': {
    en: 'Add Model',
    de: 'Modell hinzufügen',
    fr: 'Ajouter modèle',
    it: 'Aggiungi modello',
    es: 'Añadir modelo',
    pt: 'Adicionar modelo',
    nl: 'Model toevoegen',
    pl: 'Dodaj model',
  },

  // Device types
  'types.title': {
    en: 'Device Types',
    de: 'Gerätetypen',
    fr: "Types d'appareils",
    it: 'Tipi di dispositivi',
    es: 'Tipos de dispositivos',
    pt: 'Tipos de dispositivos',
    nl: 'Apparaattypen',
    pl: 'Typy urządzeń',
  },
  'types.subtitle': {
    en: 'Extinguisher classes, pressure + age-limit rules',
    de: 'Löscherklassen, Druck- und Altersgrenzen',
    fr: "Classes d'extincteurs, pression et limites d'âge",
    it: 'Classi estintori, pressione e limiti di età',
    es: 'Clases de extintores, presión y límites de edad',
    pt: 'Classes de extintores, pressão e limites de idade',
    nl: 'Blusserklassen, druk- en leeftijdsregels',
    pl: 'Klasy gaśnic, ciśnienie i limity wieku',
  },
  'types.newType': {
    en: 'New Type',
    de: 'Neuer Typ',
    fr: 'Nouveau type',
    it: 'Nuovo tipo',
    es: 'Nuevo tipo',
    pt: 'Novo tipo',
    nl: 'Nieuw type',
    pl: 'Nowy typ',
  },
  'types.years': {
    en: 'y',
    de: 'J',
    fr: 'a',
    it: 'a',
    es: 'a',
    pt: 'a',
    nl: 'j',
    pl: 'l',
  },

  // Service prices
  'prices.title': {
    en: 'Service Prices',
    de: 'Servicepreise',
    fr: 'Tarifs',
    it: 'Prezzi servizio',
    es: 'Precios de servicio',
    pt: 'Preços de serviço',
    nl: 'Serviceprijzen',
    pl: 'Ceny usług',
  },
  'prices.subtitle': {
    en: 'Maintenance pricing per device type',
    de: 'Wartungspreise pro Gerätetyp',
    fr: "Tarifs de maintenance par type d'appareil",
    it: 'Prezzi manutenzione per tipo dispositivo',
    es: 'Precios de mantenimiento por tipo',
    pt: 'Preços de manutenção por tipo',
    nl: 'Onderhoudsprijzen per apparaattype',
    pl: 'Ceny konserwacji wg typu urządzenia',
  },
  'prices.newPrice': {
    en: 'New Price',
    de: 'Neuer Preis',
    fr: 'Nouveau tarif',
    it: 'Nuovo prezzo',
    es: 'Nuevo precio',
    pt: 'Novo preço',
    nl: 'Nieuwe prijs',
    pl: 'Nowa cena',
  },
  'prices.priceList': {
    en: 'Price List',
    de: 'Preisliste',
    fr: 'Liste de prix',
    it: 'Listino prezzi',
    es: 'Lista de precios',
    pt: 'Lista de preços',
    nl: 'Prijslijst',
    pl: 'Cennik',
  },

  // Reports
  'reports.sparePartsSummary': {
    en: 'Spare Parts Summary',
    de: 'Ersatzteilübersicht',
    fr: 'Résumé pièces de rechange',
    it: 'Riepilogo ricambi',
    es: 'Resumen de repuestos',
    pt: 'Resumo de peças',
    nl: 'Overzicht onderdelen',
    pl: 'Podsumowanie części',
  },
  'reports.noPartsUsed': {
    en: 'No parts used',
    de: 'Keine Teile verwendet',
    fr: 'Aucune pièce utilisée',
    it: 'Nessun ricambio usato',
    es: 'Sin repuestos usados',
    pt: 'Sem peças usadas',
    nl: 'Geen onderdelen gebruikt',
    pl: 'Brak użytych części',
  },
  'reports.upcomingInspections': {
    en: 'Upcoming Inspections (90d)',
    de: 'Anstehende Kontrollen (90T)',
    fr: 'Inspections à venir (90j)',
    it: 'Ispezioni imminenti (90g)',
    es: 'Inspecciones próximas (90d)',
    pt: 'Inspeções próximas (90d)',
    nl: 'Komende inspecties (90d)',
    pl: 'Nadchodzące kontrole (90d)',
  },
  'reports.none': {
    en: 'None',
    de: 'Keine',
    fr: 'Aucun',
    it: 'Nessuno',
    es: 'Ninguno',
    pt: 'Nenhum',
    nl: 'Geen',
    pl: 'Brak',
  },
  'reports.ageWarnings': {
    en: 'Age Warnings',
    de: 'Alterswarnungen',
    fr: "Avertissements d'âge",
    it: 'Avvisi di età',
    es: 'Avisos de edad',
    pt: 'Avisos de idade',
    nl: 'Leeftijdswaarschuwingen',
    pl: 'Ostrzeżenia wiekowe',
  },
  'reports.exceedsAgeLimit': {
    en: 'exceeds',
    de: 'überschreitet',
    fr: 'dépasse',
    it: 'supera',
    es: 'supera',
    pt: 'excede',
    nl: 'overschrijdt',
    pl: 'przekracza',
  },
  'reports.pressureTestDue': {
    en: 'Pressure Test Due (5y)',
    de: 'Druckprüfung fällig (5J)',
    fr: 'Test pression dû (5a)',
    it: 'Test pressione scaduto (5a)',
    es: 'Prueba presión pendiente (5a)',
    pt: 'Teste pressão pendente (5a)',
    nl: 'Druktest verschuldigd (5j)',
    pl: 'Test ciśnieniowy (5l)',
  },
  'reports.pressureTestSpec': {
    en: '12 Bar · EN 671-3',
    de: '12 Bar · SN EN 671-3',
    fr: '12 Bar · NF EN 671-3',
    it: '12 Bar · UNI EN 671-3',
    es: '12 Bar · UNE EN 671-3',
    pt: '12 Bar · NP EN 671-3',
    nl: '12 Bar · NEN EN 671-3',
    pl: '12 Bar · PN EN 671-3',
  },
  'reports.due': {
    en: 'due',
    de: 'fällig',
    fr: 'à faire',
    it: 'scaduti',
    es: 'pendientes',
    pt: 'pendentes',
    nl: 'verschuldigd',
    pl: 'do wykonania',
  },

  // Hose report
  'hoseReport.title': {
    en: 'Hose Service Report',
    de: 'Löschposten Servicebericht',
    fr: 'Rapport service lances',
    it: 'Rapporto servizio manichette',
    es: 'Informe servicio mangueras',
    pt: 'Relatório serviço mangueiras',
    nl: 'Slang servicerapport',
    pl: 'Raport serwisu węży',
  },
  'hoseReport.date': {
    en: 'Date',
    de: 'Datum',
    fr: 'Date',
    it: 'Data',
    es: 'Fecha',
    pt: 'Data',
    nl: 'Datum',
    pl: 'Data',
  },
  'hoseReport.customer': {
    en: 'Customer',
    de: 'Kunde',
    fr: 'Client',
    it: 'Cliente',
    es: 'Cliente',
    pt: 'Cliente',
    nl: 'Klant',
    pl: 'Klient',
  },
  'hoseReport.objectSite': {
    en: 'Object / Site',
    de: 'Objekt / Standort',
    fr: 'Objet / Site',
    it: 'Oggetto / Sito',
    es: 'Objeto / Sitio',
    pt: 'Objeto / Local',
    nl: 'Object / Locatie',
    pl: 'Obiekt / Lokalizacja',
  },
  'hoseReport.standard': {
    en: 'Standard: Annual inspection per SN EN 671-3. 12 bar pressure test every 5 years.',
    de: 'Standard: Jährliche Kontrolle nach SN EN 671-3. 12 bar Druckprüfung alle 5 Jahre.',
    fr: 'Norme : inspection annuelle SN EN 671-3. Test pression 12 bar tous les 5 ans.',
    it: 'Standard: ispezione annuale SN EN 671-3. Test pressione 12 bar ogni 5 anni.',
    es: 'Norma: inspección anual SN EN 671-3. Prueba presión 12 bar cada 5 años.',
    pt: 'Norma: inspeção anual SN EN 671-3. Teste pressão 12 bar a cada 5 anos.',
    nl: 'Norm: jaarlijkse inspectie SN EN 671-3. 12 bar druktest elke 5 jaar.',
    pl: 'Norma: roczna kontrola SN EN 671-3. Test ciśnieniowy 12 bar co 5 lat.',
  },
  'hoseReport.equipment': {
    en: 'Equipment',
    de: 'Ausrüstung',
    fr: 'Équipement',
    it: 'Attrezzatura',
    es: 'Equipo',
    pt: 'Equipamento',
    nl: 'Apparatuur',
    pl: 'Sprzęt',
  },
  'hoseReport.condition': {
    en: 'Condition',
    de: 'Zustand',
    fr: 'État',
    it: 'Condizione',
    es: 'Condición',
    pt: 'Condição',
    nl: 'Conditie',
    pl: 'Stan',
  },
  'hoseReport.measurements': {
    en: 'Measurements',
    de: 'Messwerte',
    fr: 'Mesures',
    it: 'Misurazioni',
    es: 'Mediciones',
    pt: 'Medições',
    nl: 'Metingen',
    pl: 'Pomiary',
  },
  'hoseReport.year': {
    en: 'Year',
    de: 'Baujahr',
    fr: 'Année',
    it: 'Anno',
    es: 'Año',
    pt: 'Ano',
    nl: 'Jaar',
    pl: 'Rok',
  },
  'hoseReport.length': {
    en: 'Length',
    de: 'Länge',
    fr: 'Longueur',
    it: 'Lunghezza',
    es: 'Longitud',
    pt: 'Comprimento',
    nl: 'Lengte',
    pl: 'Długość',
  },
  'hoseReport.connection': {
    en: 'Connection',
    de: 'Anschluss',
    fr: 'Raccord',
    it: 'Connessione',
    es: 'Conexión',
    pt: 'Conexão',
    nl: 'Aansluiting',
    pl: 'Przyłącze',
  },
  'hoseReport.resting': {
    en: 'Resting pressure',
    de: 'Ruhedruck',
    fr: 'Pression repos',
    it: 'Pressione riposo',
    es: 'Presión reposo',
    pt: 'Pressão repouso',
    nl: 'Rustdruk',
    pl: 'Ciśnienie spoczynkowe',
  },
  'hoseReport.flow': {
    en: 'Flow pressure',
    de: 'Fliessdruck',
    fr: 'Pression débit',
    it: 'Pressione flusso',
    es: 'Presión flujo',
    pt: 'Pressão fluxo',
    nl: 'Stroomdruk',
    pl: 'Ciśnienie przepływu',
  },
  'hoseReport.flowRate': {
    en: 'Flow rate',
    de: 'Durchfluss',
    fr: 'Débit',
    it: 'Portata',
    es: 'Caudal',
    pt: 'Vazão',
    nl: 'Debiet',
    pl: 'Przepływ',
  },
  'hoseReport.pressureTest': {
    en: '12-bar test',
    de: '12-Bar-Prüfung',
    fr: 'Test 12 bar',
    it: 'Test 12 bar',
    es: 'Prueba 12 bar',
    pt: 'Teste 12 bar',
    nl: '12-bar test',
    pl: 'Test 12 bar',
  },
  'hoseReport.techSignature': {
    en: 'Technician signature',
    de: 'Unterschrift Techniker',
    fr: 'Signature technicien',
    it: 'Firma tecnico',
    es: 'Firma técnico',
    pt: 'Assinatura técnico',
    nl: 'Handtekening technicus',
    pl: 'Podpis technika',
  },
  'hoseReport.custSignature': {
    en: 'Customer signature',
    de: 'Unterschrift Kunde',
    fr: 'Signature client',
    it: 'Firma cliente',
    es: 'Firma cliente',
    pt: 'Assinatura cliente',
    nl: 'Handtekening klant',
    pl: 'Podpis klienta',
  },
  'hoseReport.noHoses': {
    en: 'No hoses recorded',
    de: 'Keine Löschposten erfasst',
    fr: 'Aucune lance enregistrée',
    it: 'Nessuna manichetta registrata',
    es: 'Sin mangueras registradas',
    pt: 'Nenhuma mangueira registrada',
    nl: 'Geen slangen geregistreerd',
    pl: 'Brak zarejestrowanych węży',
  },
  'hoseReport.conditionFireCock': {
    en: 'Fire cock',
    de: 'Feuerhahn',
    fr: 'Robinet incendie',
    it: 'Rubinetto antincendio',
    es: 'Llave de incendio',
    pt: 'Torneira incêndio',
    nl: 'Brandkraan',
    pl: 'Zawór pożarowy',
  },
  'hoseReport.conditionSeals': {
    en: 'Seals',
    de: 'Dichtungen',
    fr: 'Joints',
    it: 'Guarnizioni',
    es: 'Juntas',
    pt: 'Vedações',
    nl: 'Afdichtingen',
    pl: 'Uszczelki',
  },
  'hoseReport.conditionHose': {
    en: 'Hose',
    de: 'Schlauch',
    fr: 'Tuyau',
    it: 'Manichetta',
    es: 'Manguera',
    pt: 'Mangueira',
    nl: 'Slang',
    pl: 'Wąż',
  },
  'hoseReport.conditionNozzle': {
    en: 'Nozzle',
    de: 'Düse',
    fr: 'Buse',
    it: 'Ugello',
    es: 'Boquilla',
    pt: 'Bocal',
    nl: 'Mondstuk',
    pl: 'Dysza',
  },
  'hoseReport.conditionAxle': {
    en: 'Axle',
    de: 'Achse',
    fr: 'Axe',
    it: 'Asse',
    es: 'Eje',
    pt: 'Eixo',
    nl: 'As',
    pl: 'Oś',
  },
  'hoseReport.conditionSwivelArm': {
    en: 'Swivel arm',
    de: 'Schwenkarm',
    fr: 'Bras pivotant',
    it: 'Braccio girevole',
    es: 'Brazo giratorio',
    pt: 'Braço giratório',
    nl: 'Zwenkarm',
    pl: 'Ramię obrotowe',
  },
  'hoseReport.conditionDoorLock': {
    en: 'Door lock',
    de: 'Türschloss',
    fr: 'Serrure',
    it: 'Serratura',
    es: 'Cerradura',
    pt: 'Fechadura',
    nl: 'Deurslot',
    pl: 'Zamek drzwi',
  },
  'hoseReport.conditionWater': {
    en: 'Water',
    de: 'Wasser',
    fr: 'Eau',
    it: 'Acqua',
    es: 'Agua',
    pt: 'Água',
    nl: 'Water',
    pl: 'Woda',
  },

  // Map
  'map.title': {
    en: 'Map',
    de: 'Karte',
    fr: 'Carte',
    it: 'Mappa',
    es: 'Mapa',
    pt: 'Mapa',
    nl: 'Kaart',
    pl: 'Mapa',
  },
  'scan.scan': {
    en: 'Scan',
    de: 'Scannen',
    fr: 'Scanner',
    it: 'Scansiona',
    es: 'Escanear',
    pt: 'Digitalizar',
    nl: 'Scannen',
    pl: 'Skanuj',
  },
  'scan.title': {
    en: 'Scan barcode',
    de: 'Barcode scannen',
    fr: 'Scanner le code-barres',
    it: 'Scansiona codice a barre',
    es: 'Escanear código de barras',
    pt: 'Digitalizar código de barras',
    nl: 'Streepjescode scannen',
    pl: 'Skanuj kod kreskowy',
  },
  'scan.unsupported': {
    en: 'Camera scanning is not supported on this device. Enter the number by hand.',
    de: 'Kamera-Scan wird auf diesem Gerät nicht unterstützt. Nummer manuell eingeben.',
    fr: "Le scan par caméra n'est pas pris en charge sur cet appareil. Saisissez le numéro manuellement.",
    it: 'La scansione con fotocamera non è supportata su questo dispositivo. Inserisci il numero manualmente.',
    es: 'El escaneo con cámara no es compatible con este dispositivo. Introduzca el número manualmente.',
    pt: 'A digitalização por câmara não é suportada neste dispositivo. Introduza o número manualmente.',
    nl: 'Camera scannen wordt niet ondersteund op dit apparaat. Voer het nummer handmatig in.',
    pl: 'Skanowanie aparatem nie jest obsługiwane na tym urządzeniu. Wprowadź numer ręcznie.',
  },
  'scan.permission': {
    en: 'Camera access was denied.',
    de: 'Kamerazugriff wurde verweigert.',
    fr: "L'accès à la caméra a été refusé.",
    it: "L'accesso alla fotocamera è stato negato.",
    es: 'Se denegó el acceso a la cámara.',
    pt: 'O acesso à câmara foi negado.',
    nl: 'Cameratoegang is geweigerd.',
    pl: 'Odmówiono dostępu do aparatu.',
  },
  'map.overdue': {
    en: 'Overdue',
    de: 'Überfällig',
    fr: 'En retard',
    it: 'Scaduto',
    es: 'Vencido',
    pt: 'Atrasado',
    nl: 'Achterstallig',
    pl: 'Zaległe',
  },
  'map.dueSoon': {
    en: 'Due soon',
    de: 'Bald fällig',
    fr: 'Bientôt dû',
    it: 'In scadenza',
    es: 'Próximo',
    pt: 'Em breve',
    nl: 'Binnenkort',
    pl: 'Wkrótce',
  },
  'map.upToDate': {
    en: 'Up to date',
    de: 'Aktuell',
    fr: 'À jour',
    it: 'Aggiornato',
    es: 'Al día',
    pt: 'Em dia',
    nl: 'Bijgewerkt',
    pl: 'Aktualne',
  },
  'map.minDevices': {
    en: 'Min. devices',
    de: 'Min. Geräte',
    fr: 'Appareils min.',
    it: 'Dispositivi min.',
    es: 'Dispositivos mín.',
    pt: 'Dispositivos mín.',
    nl: 'Min. apparaten',
    pl: 'Min. urządzeń',
  },
  'customer.addAttachment': {
    en: 'Add attachment',
    de: 'Anhang hinzufügen',
    fr: 'Ajouter une pièce jointe',
    it: 'Aggiungi allegato',
    es: 'Añadir adjunto',
    pt: 'Adicionar anexo',
    nl: 'Bijlage toevoegen',
    pl: 'Dodaj załącznik',
  },

  // Import/Export

  // Sidebar
  'sidebar.systemReady': {
    en: 'System ready',
    de: 'System bereit',
    fr: 'Système prêt',
    it: 'Sistema pronto',
    es: 'Sistema listo',
    pt: 'Sistema pronto',
    nl: 'Systeem gereed',
    pl: 'System gotowy',
  },
  'sidebar.offlineMode': {
    en: 'Offline mode',
    de: 'Offline-Modus',
    fr: 'Mode hors ligne',
    it: 'Modalità offline',
    es: 'Modo sin conexión',
    pt: 'Modo offline',
    nl: 'Offline modus',
    pl: 'Tryb offline',
  },

  'sidebar.checkUpdate': {
    en: 'Check for updates',
    de: 'Nach Updates suchen',
    fr: 'Vérifier les mises à jour',
    it: 'Controlla aggiornamenti',
    es: 'Buscar actualizaciones',
    pt: 'Verificar atualizações',
    nl: 'Controleer op updates',
    pl: 'Sprawdź aktualizacje',
  },
  'sidebar.checking': {
    en: 'Checking…',
    de: 'Prüfe…',
    fr: 'Vérification…',
    it: 'Controllo…',
    es: 'Comprobando…',
    pt: 'Verificando…',
    nl: 'Controleren…',
    pl: 'Sprawdzanie…',
  },
  'sidebar.upToDate': {
    en: 'Up to date',
    de: 'Aktuell',
    fr: 'À jour',
    it: 'Aggiornato',
    es: 'Actualizado',
    pt: 'Atualizado',
    nl: 'Up-to-date',
    pl: 'Aktualne',
  },
  'sidebar.updating': {
    en: 'Syncing update…',
    de: 'Update wird geladen…',
    fr: 'Mise à jour en cours…',
    it: 'Aggiornamento in corso…',
    es: 'Sincronizando actualización…',
    pt: 'Sincronizando atualização…',
    nl: 'Update synchroniseren…',
    pl: 'Synchronizacja aktualizacji…',
  },
  'sidebar.caching': {
    en: 'Caching for offline…',
    de: 'Wird offline verfügbar…',
    fr: 'Mise en cache…',
    it: 'Memorizzazione offline…',
    es: 'Guardando sin conexión…',
    pt: 'Armazenando offline…',
    nl: 'Offline opslaan…',
    pl: 'Zapisywanie offline…',
  },
  'sidebar.offlineReady': {
    en: 'Ready for offline',
    de: 'Offline bereit',
    fr: 'Prêt hors ligne',
    it: 'Pronto offline',
    es: 'Listo sin conexión',
    pt: 'Pronto offline',
    nl: 'Offline gereed',
    pl: 'Gotowe offline',
  },

  // Archive drill-down (client #73) — archives taken before the snapshot
  // existed have no device list to show.
  'customer.archiveNoSnapshot': {
    en: 'This archive has no device snapshot.',
    de: 'Dieses Archiv enthält keine Geräteliste.',
    fr: 'Cette archive ne contient pas de liste d’appareils.',
    it: 'Questo archivio non contiene un elenco di apparecchi.',
    es: 'Este archivo no contiene una lista de aparatos.',
    pt: 'Este arquivo não contém uma lista de aparelhos.',
    nl: 'Dit archief bevat geen apparatenlijst.',
    pl: 'To archiwum nie zawiera listy urządzeń.',
  },

  // Duplicate a customer (client #74).
  'customer.duplicate': {
    en: 'Duplicate',
    de: 'Duplizieren',
    fr: 'Dupliquer',
    it: 'Duplica',
    es: 'Duplicar',
    pt: 'Duplicar',
    nl: 'Dupliceren',
    pl: 'Duplikuj',
  },
  // The duplicate button sits next to Maintenance and Delete in the header and
  // was firing on a single click — misclicks left stray copies behind
  // (client #96).
  'customer.duplicateConfirm': {
    en: 'Duplicate customer',
    de: 'Kunde duplizieren',
    fr: 'Dupliquer le client',
    it: 'Duplicare il cliente',
    es: 'Duplicar cliente',
    pt: 'Duplicar cliente',
    nl: 'Klant dupliceren',
    pl: 'Zduplikować klienta',
  },

  // Disposal + replacement (client #54).
  'ext.replaceDevice': {
    en: 'Dispose and replace',
    de: 'Entsorgen und ersetzen',
    fr: 'Éliminer et remplacer',
    it: 'Smaltire e sostituire',
    es: 'Eliminar y sustituir',
    pt: 'Eliminar e substituir',
    nl: 'Afvoeren en vervangen',
    pl: 'Zutylizuj i wymień',
  },
  'ext.replaceDone': {
    en: 'Device replaced',
    de: 'Gerät ersetzt',
    fr: 'Appareil remplacé',
    it: 'Apparecchio sostituito',
    es: 'Aparato sustituido',
    pt: 'Aparelho substituído',
    nl: 'Apparaat vervangen',
    pl: 'Urządzenie wymienione',
  },

  // Catalogue price of a device model (client #60).
  'mfr.modelPrice': {
    en: 'Device price (new)',
    de: 'Gerätepreis (neu)',
    fr: 'Prix de l’appareil (neuf)',
    it: 'Prezzo apparecchio (nuovo)',
    es: 'Precio del aparato (nuevo)',
    pt: 'Preço do aparelho (novo)',
    nl: 'Prijs apparaat (nieuw)',
    pl: 'Cena urządzenia (nowe)',
  },

  // Pressure class of an extinguisher (client #63).
  'pressure.boost': {
    en: 'Boost',
    de: 'Aufladedruck',
    fr: 'Pression auxiliaire',
    it: 'Pressione ausiliaria',
    es: 'Presión auxiliar',
    pt: 'Pressão auxiliar',
    nl: 'Hulpdruk',
    pl: 'Ciśnienie pomocnicze',
  },
  'pressure.permanent': {
    en: 'Permanent',
    de: 'Dauerdruck',
    fr: 'Pression permanente',
    it: 'Pressione permanente',
    es: 'Presión permanente',
    pt: 'Pressão permanente',
    nl: 'Permanente druk',
    pl: 'Ciśnienie stałe',
  },

  // Data sync status — where the technician's data currently lives.
  'sync.downloading': {
    en: 'Downloading data…',
    de: 'Daten werden geladen…',
    fr: 'Téléchargement des données…',
    it: 'Download dei dati…',
    es: 'Descargando datos…',
    pt: 'A transferir dados…',
    nl: 'Gegevens downloaden…',
    pl: 'Pobieranie danych…',
  },
  'sync.uploading': {
    en: 'Saving to server…',
    de: 'Wird auf Server gespeichert…',
    fr: 'Enregistrement sur le serveur…',
    it: 'Salvataggio sul server…',
    es: 'Guardando en el servidor…',
    pt: 'A guardar no servidor…',
    nl: 'Opslaan op server…',
    pl: 'Zapisywanie na serwerze…',
  },
  'sync.onDeviceOnly': {
    en: 'change(s) on this device only',
    de: 'Änderung(en) nur auf diesem Gerät',
    fr: 'modification(s) sur cet appareil uniquement',
    it: 'modifica/he solo su questo dispositivo',
    es: 'cambio(s) solo en este dispositivo',
    pt: 'alteração(ões) apenas neste dispositivo',
    nl: 'wijziging(en) alleen op dit apparaat',
    pl: 'zmiana(y) tylko na tym urządzeniu',
  },
  'sync.allOnServer': {
    en: 'All changes saved on the server',
    de: 'Alle Änderungen auf dem Server gespeichert',
    fr: 'Toutes les modifications enregistrées sur le serveur',
    it: 'Tutte le modifiche salvate sul server',
    es: 'Todos los cambios guardados en el servidor',
    pt: 'Todas as alterações guardadas no servidor',
    nl: 'Alle wijzigingen op de server opgeslagen',
    pl: 'Wszystkie zmiany zapisane na serwerze',
  },

  // Install prompt
  'install.button': {
    en: 'Install App',
    de: 'App installieren',
    fr: "Installer l'appli",
    it: 'Installa app',
    es: 'Instalar app',
    pt: 'Instalar app',
    nl: 'App installeren',
    pl: 'Zainstaluj',
  },
  'install.dismiss': {
    en: 'Not now',
    de: 'Nicht jetzt',
    fr: 'Pas maintenant',
    it: 'Non ora',
    es: 'Ahora no',
    pt: 'Agora não',
    nl: 'Niet nu',
    pl: 'Nie teraz',
  },
  'install.ios.title': {
    en: 'Add to Home Screen',
    de: 'Zum Startbildschirm',
    fr: "Ajouter à l'écran d'accueil",
    it: 'Aggiungi a Home',
    es: 'Añadir a inicio',
    pt: 'Adicionar ao ecrã',
    nl: 'Voeg toe aan beginscherm',
    pl: 'Dodaj do ekranu',
  },
  'install.ios.step1': {
    en: 'Tap the Share button',
    de: 'Tippe auf Teilen',
    fr: 'Appuyez sur Partager',
    it: 'Tocca Condividi',
    es: 'Pulsa Compartir',
    pt: 'Toque em Partilhar',
    nl: 'Tik op Deel',
    pl: 'Kliknij Udostępnij',
  },
  'install.ios.step2': {
    en: 'Select "Add to Home Screen"',
    de: '„Zum Home-Bildschirm" wählen',
    fr: "Choisir « Sur l'écran d'accueil »",
    it: 'Seleziona "Aggiungi a Home"',
    es: 'Selecciona "Añadir a inicio"',
    pt: 'Selecione "Adicionar ao ecrã inicial"',
    nl: 'Kies "Zet op beginscherm"',
    pl: 'Wybierz "Dodaj do ekranu głównego"',
  },
  'install.chrome.title': {
    en: 'Install App',
    de: 'App installieren',
    fr: "Installer l'appli",
    it: 'Installa app',
    es: 'Instalar app',
    pt: 'Instalar app',
    nl: 'App installeren',
    pl: 'Zainstaluj',
  },
  'install.chrome.step1': {
    en: 'Open browser menu (\u22EE)',
    de: 'Browsermen\u00FC \u00F6ffnen (\u22EE)',
    fr: 'Ouvrir le menu du navigateur (\u22EE)',
    it: 'Apri il menu del browser (\u22EE)',
    es: 'Abrir men\u00FA del navegador (\u22EE)',
    pt: 'Abrir menu do navegador (\u22EE)',
    nl: 'Open browsermenu (\u22EE)',
    pl: 'Otw\u00F3rz menu przegl\u0105darki (\u22EE)',
  },
  'install.chrome.step2': {
    en: 'Select "Install Look2it"',
    de: '\u201ELook2it installieren\u201C w\u00E4hlen',
    fr: 'Choisir \u00AB Installer Look2it \u00BB',
    it: 'Seleziona "Installa Look2it"',
    es: 'Selecciona "Instalar Look2it"',
    pt: 'Selecione "Instalar Look2it"',
    nl: 'Kies "Look2it installeren"',
    pl: 'Wybierz "Zainstaluj Look2it"',
  },
  'install.safari.title': {
    en: 'Add to Dock',
    de: 'Zum Dock hinzuf\u00FCgen',
    fr: 'Ajouter au Dock',
    it: 'Aggiungi al Dock',
    es: 'A\u00F1adir al Dock',
    pt: 'Adicionar ao Dock',
    nl: 'Voeg toe aan Dock',
    pl: 'Dodaj do Docka',
  },
  'install.safari.step1': {
    en: 'Click File in the menu bar',
    de: 'Klicke auf Ablage in der Men\u00FCleiste',
    fr: 'Cliquez sur Fichier dans la barre de menus',
    it: 'Clicca su File nella barra dei menu',
    es: 'Haz clic en Archivo en la barra de men\u00FAs',
    pt: 'Clique em Ficheiro na barra de menus',
    nl: 'Klik op Archief in de menubalk',
    pl: 'Kliknij Plik na pasku menu',
  },
  'install.safari.step2': {
    en: 'Select "Add to Dock"',
    de: '\u201EZum Dock hinzuf\u00FCgen\u201C w\u00E4hlen',
    fr: 'Choisir \u00AB Ajouter au Dock \u00BB',
    it: 'Seleziona "Aggiungi al Dock"',
    es: 'Selecciona "A\u00F1adir al Dock"',
    pt: 'Selecione "Adicionar ao Dock"',
    nl: 'Kies "Voeg toe aan Dock"',
    pl: 'Wybierz "Dodaj do Docka"',
  },
  'install.firefox': {
    en: "Firefox doesn't support app install. Use Chrome, Edge, or Safari.",
    de: 'Firefox unterstützt keine App-Installation. Nutze Chrome, Edge oder Safari.',
    fr: "Firefox ne prend pas en charge l'installation. Utilisez Chrome, Edge ou Safari.",
    it: "Firefox non supporta l'installazione. Usa Chrome, Edge o Safari.",
    es: 'Firefox no permite instalar. Usa Chrome, Edge o Safari.',
    pt: 'Firefox não suporta instalação. Use Chrome, Edge ou Safari.',
    nl: 'Firefox ondersteunt geen installatie. Gebruik Chrome, Edge of Safari.',
    pl: 'Firefox nie obsługuje instalacji. Użyj Chrome, Edge lub Safari.',
  },

  // Settings (extra labels)
  'settings.companyName': {
    en: 'Company Name',
    de: 'Firmenname',
    fr: 'Nom de la société',
    it: 'Nome azienda',
    es: 'Nombre de empresa',
    pt: 'Nome da empresa',
    nl: 'Bedrijfsnaam',
    pl: 'Nazwa firmy',
  },
  'settings.street': {
    en: 'Street',
    de: 'Strasse',
    fr: 'Rue',
    it: 'Via',
    es: 'Calle',
    pt: 'Rua',
    nl: 'Straat',
    pl: 'Ulica',
  },
  'settings.zip': {
    en: 'ZIP',
    de: 'PLZ',
    fr: 'Code postal',
    it: 'CAP',
    es: 'C.P.',
    pt: 'CEP',
    nl: 'Postcode',
    pl: 'Kod pocztowy',
  },
  'settings.city': {
    en: 'City',
    de: 'Ort',
    fr: 'Ville',
    it: 'Città',
    es: 'Ciudad',
    pt: 'Cidade',
    nl: 'Stad',
    pl: 'Miasto',
  },
  'settings.phone': {
    en: 'Phone',
    de: 'Telefon',
    fr: 'Téléphone',
    it: 'Telefono',
    es: 'Teléfono',
    pt: 'Telefone',
    nl: 'Telefoon',
    pl: 'Telefon',
  },
  'settings.email': {
    en: 'Email',
    de: 'E-Mail',
    fr: 'E-mail',
    it: 'Email',
    es: 'Correo',
    pt: 'E-mail',
    nl: 'E-mail',
    pl: 'E-mail',
  },
  'settings.currency': {
    en: 'Currency',
    de: 'Währung',
    fr: 'Devise',
    it: 'Valuta',
    es: 'Moneda',
    pt: 'Moeda',
    nl: 'Valuta',
    pl: 'Waluta',
  },
  'settings.rounding': {
    en: 'Rounding',
    de: 'Rundung',
    fr: 'Arrondi',
    it: 'Arrotondamento',
    es: 'Redondeo',
    pt: 'Arredondamento',
    nl: 'Afronding',
    pl: 'Zaokrąglenie',
  },
  'settings.currencyHint': {
    en: 'Auto-set by country',
    de: 'Automatisch nach Land',
    fr: 'Défini automatiquement',
    it: 'Impostato automaticamente',
    es: 'Se establece automáticamente',
    pt: 'Definido automaticamente',
    nl: 'Automatisch ingesteld',
    pl: 'Ustawiane automatycznie',
  },
  'settings.iban': {
    en: 'IBAN',
    de: 'IBAN',
    fr: 'IBAN',
    it: 'IBAN',
    es: 'IBAN',
    pt: 'IBAN',
    nl: 'IBAN',
    pl: 'IBAN',
  },
  'settings.paymentTermsDays': {
    en: 'Payment Terms (days)',
    de: 'Zahlungsfrist (Tage)',
    fr: 'Délai de paiement (jours)',
    it: 'Termini di pagamento (giorni)',
    es: 'Plazo de pago (días)',
    pt: 'Prazo de pagamento (dias)',
    nl: 'Betalingstermijn (dagen)',
    pl: 'Termin płatności (dni)',
  },
  'customer.batchInspect': {
    en: 'Mark All Inspected',
    de: 'Alle als kontrolliert',
    fr: 'Tout inspecté',
    it: 'Tutti ispezionati',
    es: 'Marcar todos inspeccionados',
    pt: 'Marcar todos inspecionados',
    nl: 'Alles geïnspecteerd',
    pl: 'Wszystkie skontrolowane',
  },
  'customer.startMaintenance': {
    en: 'Start maintenance',
    de: 'Wartung starten',
    fr: 'Démarrer la maintenance',
    it: 'Avvia manutenzione',
    es: 'Iniciar mantenimiento',
    pt: 'Iniciar manutenção',
    nl: 'Onderhoud starten',
    pl: 'Rozpocznij konserwację',
  },
  'inspection.editDevice': {
    en: 'Correct device details',
    de: 'Gerätedaten korrigieren',
    fr: "Corriger les détails de l'appareil",
    it: 'Correggi i dettagli del dispositivo',
    es: 'Corregir detalles del dispositivo',
    pt: 'Corrigir detalhes do dispositivo',
    nl: 'Apparaatgegevens corrigeren',
    pl: 'Popraw dane urządzenia',
  },
  'customer.batchInspectDone': {
    en: 'All inspection dates updated',
    de: 'Alle Kontrolldaten aktualisiert',
    fr: "Dates d'inspection mises à jour",
    it: 'Date ispezione aggiornate',
    es: 'Fechas de inspección actualizadas',
    pt: 'Datas de inspeção atualizadas',
    nl: 'Inspectiedatums bijgewerkt',
    pl: 'Daty kontroli zaktualizowane',
  },
  'settings.travelSurcharge': {
    en: 'Travel Surcharge',
    de: 'Anfahrtspauschale',
    fr: 'Forfait déplacement',
    it: 'Supplemento viaggio',
    es: 'Recargo desplazamiento',
    pt: 'Taxa deslocação',
    nl: 'Reistoeslag',
    pl: 'Ryczałt dojazd',
  },
  'settings.inspectionInterval': {
    en: 'Inspection Interval (y)',
    de: 'Kontrollintervall (J)',
    fr: "Intervalle d'inspection (a)",
    it: 'Intervallo ispezione (a)',
    es: 'Intervalo de inspección (a)',
    pt: 'Intervalo de inspeção (a)',
    nl: 'Inspectie-interval (j)',
    pl: 'Interwał kontroli (l)',
  },
  'settings.boostAgeLimit': {
    en: 'Boost Age Limit (y)',
    de: 'Auflade-Altersgrenze (J)',
    fr: "Limite d'âge pression (a)",
    it: 'Limite età boost (a)',
    es: 'Límite edad boost (a)',
    pt: 'Limite idade boost (a)',
    nl: 'Opdruk leeftijdslimiet (j)',
    pl: 'Limit wieku boost (l)',
  },
  'settings.permanentAgeLimit': {
    en: 'Permanent Age Limit (y)',
    de: 'Dauerdr.-Altersgrenze (J)',
    fr: "Limite d'âge permanent (a)",
    it: 'Limite età permanente (a)',
    es: 'Límite edad permanente (a)',
    pt: 'Limite idade permanente (a)',
    nl: 'Permanent leeftijdslimiet (j)',
    pl: 'Limit wieku stałe (l)',
  },
  'settings.hoseTestInterval': {
    en: 'Hose Pressure Test (y)',
    de: 'Schlauch-Druckprüfung (J)',
    fr: 'Test pression tuyau (a)',
    it: 'Test pressione tubo (a)',
    es: 'Prueba presión manguera (a)',
    pt: 'Teste pressão mangueira (a)',
    nl: 'Slang druktest (j)',
    pl: 'Test ciśnieniowy węża (l)',
  },

  // Tour
  'tour.next': {
    en: 'Next',
    de: 'Weiter',
    fr: 'Suivant',
    it: 'Avanti',
    es: 'Siguiente',
    pt: 'Próximo',
    nl: 'Volgende',
    pl: 'Dalej',
  },
  'tour.prev': {
    en: 'Back',
    de: 'Zurück',
    fr: 'Précédent',
    it: 'Indietro',
    es: 'Anterior',
    pt: 'Anterior',
    nl: 'Vorige',
    pl: 'Wstecz',
  },
  'tour.skip': {
    en: 'Skip',
    de: 'Überspringen',
    fr: 'Passer',
    it: 'Salta',
    es: 'Omitir',
    pt: 'Pular',
    nl: 'Overslaan',
    pl: 'Pomiń',
  },
  'tour.finish': {
    en: 'Finish',
    de: 'Fertig',
    fr: 'Terminer',
    it: 'Fine',
    es: 'Finalizar',
    pt: 'Concluir',
    nl: 'Voltooien',
    pl: 'Zakończ',
  },
  'tour.step': {
    en: 'Step',
    de: 'Schritt',
    fr: 'Étape',
    it: 'Passo',
    es: 'Paso',
    pt: 'Etapa',
    nl: 'Stap',
    pl: 'Krok',
  },

  // ── Tour steps ──
  'tour.welcome.title': {
    en: 'Welcome to Look2it',
    de: 'Willkommen bei Look2it',
    fr: 'Bienvenue sur Look2it',
    it: 'Benvenuto su Look2it',
    es: 'Bienvenido a Look2it',
    pt: 'Bem-vindo ao Look2it',
    nl: 'Welkom bij Look2it',
    pl: 'Witamy w Look2it',
  },
  'tour.welcome.body': {
    en: 'A modern replacement for your XLSB workflow — customer management, inspections, invoicing and offline field work, all in one PWA.',
    de: 'Moderner Ersatz für Ihre XLSB — Kundenverwaltung, Kontrollen, Rechnungen und Offline-Feldarbeit in einer PWA.',
    fr: 'Remplacement moderne de votre flux XLSB — gestion clients, inspections, facturation et travail terrain hors ligne, le tout dans une PWA.',
    it: 'Sostituzione moderna del flusso XLSB — gestione clienti, ispezioni, fatturazione e lavoro sul campo offline, tutto in una PWA.',
    es: 'Reemplazo moderno de su flujo XLSB — gestión de clientes, inspecciones, facturación y trabajo de campo sin conexión, todo en una PWA.',
    pt: 'Substituto moderno do seu fluxo XLSB — gestão de clientes, inspeções, faturação e trabalho de campo offline, tudo numa PWA.',
    nl: 'Moderne vervanging voor uw XLSB-werkstroom — klantbeheer, inspecties, facturering en offline veldwerk, alles in één PWA.',
    pl: 'Nowoczesny zamiennik przepływu XLSB — zarządzanie klientami, inspekcje, fakturowanie i praca w terenie offline, wszystko w jednej PWA.',
  },
  'tour.nav.title': {
    en: 'Navigation',
    de: 'Navigation',
    fr: 'Navigation',
    it: 'Navigazione',
    es: 'Navegación',
    pt: 'Navegação',
    nl: 'Navigatie',
    pl: 'Nawigacja',
  },
  'tour.nav.body': {
    en: 'Main groups: Overview & Map, Administration master data, and Data import/export. Badges show live counts.',
    de: 'Hauptgruppen: Übersicht & Karte, Verwaltung, Daten Import/Export. Badges zeigen aktuelle Anzahl.',
    fr: 'Groupes principaux : Aperçu & Carte, Administration, Import/Export. Les badges affichent les compteurs en direct.',
    it: 'Gruppi principali: Panoramica & Mappa, Amministrazione, Import/Export. I badge mostrano i conteggi in tempo reale.',
    es: 'Grupos principales: Resumen y Mapa, Administración, Importar/Exportar. Las insignias muestran conteos en vivo.',
    pt: 'Grupos principais: Visão geral e Mapa, Administração, Importar/Exportar. Os emblemas mostram contagens em tempo real.',
    nl: 'Hoofdgroepen: Overzicht & Kaart, Administratie, Gegevens import/export. Badges tonen live aantallen.',
    pl: 'Główne grupy: Przegląd i Mapa, Administracja, Import/Eksport danych. Odznaki pokazują bieżące liczby.',
  },
  'tour.overview.title': {
    en: 'Customer Overview',
    de: 'Kundenübersicht',
    fr: 'Aperçu des clients',
    it: 'Panoramica clienti',
    es: 'Resumen de clientes',
    pt: 'Visão geral dos clientes',
    nl: 'Klantenoverzicht',
    pl: 'Przegląd klientów',
  },
  'tour.overview.body': {
    en: 'All sites at a glance. Status dots: green = done, blue = to do, amber = waiting. Click a row to open the customer.',
    de: 'Alle Standorte auf einen Blick. Statuspunkte: grün = erledigt, blau = offen, gelb = wartend. Zeile klicken öffnet Kunden.',
    fr: "Tous les sites en un coup d'œil. Points d'état : vert = terminé, bleu = à faire, orange = en attente. Cliquez sur une ligne pour ouvrir le client.",
    it: "Tutti i siti a colpo d'occhio. Punti di stato: verde = fatto, blu = da fare, arancione = in attesa. Clicca su una riga per aprire il cliente.",
    es: 'Todos los sitios de un vistazo. Puntos de estado: verde = hecho, azul = pendiente, ámbar = en espera. Haga clic en una fila para abrir el cliente.',
    pt: 'Todos os locais de relance. Pontos de estado: verde = concluído, azul = a fazer, âmbar = em espera. Clique numa linha para abrir o cliente.',
    nl: 'Alle locaties in één oogopslag. Statuspunten: groen = klaar, blauw = te doen, amber = wachtend. Klik op een rij om de klant te openen.',
    pl: 'Wszystkie lokalizacje na pierwszy rzut oka. Kropki statusu: zielona = gotowe, niebieska = do zrobienia, pomarańczowa = oczekujące. Kliknij wiersz, aby otworzyć klienta.',
  },
  'tour.map.title': {
    en: 'Map View',
    de: 'Kartenansicht',
    fr: 'Vue carte',
    it: 'Vista mappa',
    es: 'Vista de mapa',
    pt: 'Vista do mapa',
    nl: 'Kaartweergave',
    pl: 'Widok mapy',
  },
  'tour.map.body': {
    en: 'Every customer with coordinates is plotted with status-colored markers.',
    de: 'Alle Kunden mit Koordinaten werden mit statusfarbigen Markern angezeigt.',
    fr: 'Chaque client avec des coordonnées est affiché avec des marqueurs colorés par statut.',
    it: 'Ogni cliente con coordinate è tracciato con marcatori colorati per stato.',
    es: 'Cada cliente con coordenadas se traza con marcadores coloreados por estado.',
    pt: 'Cada cliente com coordenadas é marcado com marcadores coloridos por estado.',
    nl: 'Elke klant met coördinaten wordt geplot met statusgekleurde markers.',
    pl: 'Każdy klient ze współrzędnymi jest zaznaczony kolorowymi markerami statusu.',
  },
  'tour.customer.title': {
    en: 'Customer Detail',
    de: 'Kundendetails',
    fr: 'Détail client',
    it: 'Dettaglio cliente',
    es: 'Detalle del cliente',
    pt: 'Detalhe do cliente',
    nl: 'Klantdetail',
    pl: 'Szczegóły klienta',
  },
  'tour.customer.body': {
    en: 'Full record: billing, object, owner addresses and janitor contact. Plus extinguishers, hoses and archive history.',
    de: 'Vollständiger Datensatz: Rechnungs-, Objekt-, Eigentümeradresse und Abwart. Dazu Feuerlöscher, Löschposten, Archive.',
    fr: 'Fiche complète : adresses de facturation, objet, propriétaire et contact concierge. Plus extincteurs, tuyaux et historique.',
    it: 'Scheda completa: indirizzi di fatturazione, oggetto, proprietario e contatto custode. Più estintori, manichette e archivio.',
    es: 'Registro completo: direcciones de facturación, objeto, propietario y contacto del conserje. Más extintores, mangueras e historial.',
    pt: 'Registo completo: endereços de faturação, objeto, proprietário e contacto do zelador. Mais extintores, mangueiras e histórico.',
    nl: 'Volledig dossier: facturering, object, eigenaar en conciërge. Plus brandblussers, slangen en archiefgeschiedenis.',
    pl: 'Pełny zapis: adresy rozliczeniowe, obiekt, właściciel i kontakt z dozorcą. Plus gaśnice, węże i historia archiwum.',
  },
  'tour.addExt.title': {
    en: 'Add Extinguisher',
    de: 'Feuerlöscher anlegen',
    fr: 'Ajouter un extincteur',
    it: 'Aggiungi estintore',
    es: 'Añadir extintor',
    pt: 'Adicionar extintor',
    nl: 'Brandblusser toevoegen',
    pl: 'Dodaj gaśnicę',
  },
  'tour.addExt.body': {
    en: 'Cascade selector: manufacturer → agent type → model → device type. Documentation appears automatically.',
    de: 'Kaskadenauswahl: Hersteller → Löschmittel → Modell → Gruppe. Dokumentation erscheint automatisch.',
    fr: "Sélecteur en cascade : fabricant → type d'agent → modèle → type d'appareil. La documentation apparaît automatiquement.",
    it: 'Selettore a cascata: produttore → tipo agente → modello → tipo dispositivo. La documentazione appare automaticamente.',
    es: 'Selector en cascada: fabricante → tipo de agente → modelo → tipo de dispositivo. La documentación aparece automáticamente.',
    pt: 'Seletor em cascata: fabricante → tipo de agente → modelo → tipo de dispositivo. A documentação aparece automaticamente.',
    nl: 'Cascadeselector: fabrikant → blusmiddeltype → model → apparaattype. Documentatie verschijnt automatisch.',
    pl: 'Selektor kaskadowy: producent → typ środka → model → typ urządzenia. Dokumentacja pojawia się automatycznie.',
  },
  'tour.editExt.title': {
    en: 'Extinguisher Editor',
    de: 'Feuerlöscher-Editor',
    fr: "Éditeur d'extincteur",
    it: 'Editor estintore',
    es: 'Editor de extintor',
    pt: 'Editor de extintor',
    nl: 'Blusser-editor',
    pl: 'Edytor gaśnicy',
  },
  'tour.editExt.body': {
    en: 'Age warnings fire automatically based on device type limits. Spare parts aggregate by article number.',
    de: 'Altersgrenzen automatisch je nach Gerätetyp. Ersatzteile werden pro Artikel-Nr. zusammengefasst.',
    fr: "Les alertes d'âge se déclenchent automatiquement selon les limites du type d'appareil. Les pièces sont agrégées par numéro d'article.",
    it: 'Gli avvisi di età si attivano automaticamente in base ai limiti del tipo di dispositivo. I ricambi si aggregano per numero articolo.',
    es: 'Las advertencias de antigüedad se activan automáticamente según los límites del tipo de dispositivo. Los repuestos se agrupan por número de artículo.',
    pt: 'Os avisos de idade disparam automaticamente com base nos limites do tipo de dispositivo. As peças são agregadas por número de artigo.',
    nl: 'Leeftijdswaarschuwingen activeren automatisch op basis van apparaattype-limieten. Onderdelen worden per artikelnummer geaggregeerd.',
    pl: 'Ostrzeżenia o wieku uruchamiają się automatycznie na podstawie limitów typu urządzenia. Części zamienne są agregowane według numeru artykułu.',
  },
  'tour.invoice.title': {
    en: 'Invoice',
    de: 'Rechnung',
    fr: 'Facture',
    it: 'Fattura',
    es: 'Factura',
    pt: 'Fatura',
    nl: 'Factuur',
    pl: 'Faktura',
  },
  'tour.invoice.body': {
    en: 'Generate invoices with country-specific VAT and rounding. Billing, object and owner blocks. Print and PDF export ready.',
    de: 'Rechnungen mit landesspezifischer MWST und Rundung. Rechnungs-, Objekt-, Eigentümerblock. Druck- und PDF-Export.',
    fr: 'Générez des factures avec TVA et arrondi par pays. Blocs facturation, objet et propriétaire. Impression et export PDF.',
    it: 'Genera fatture con IVA e arrotondamento specifici per paese. Blocchi fatturazione, oggetto e proprietario. Stampa ed esportazione PDF.',
    es: 'Genere facturas con IVA y redondeo específicos por país. Bloques de facturación, objeto y propietario. Impresión y exportación PDF.',
    pt: 'Gere faturas com IVA e arredondamento específicos por país. Blocos de faturação, objeto e proprietário. Impressão e exportação PDF.',
    nl: 'Genereer facturen met landspecifieke BTW en afronding. Facturerings-, object- en eigenaarblokken. Print en PDF-export.',
    pl: 'Generuj faktury z VAT i zaokrąglaniem specyficznym dla kraju. Bloki rozliczeniowe, obiektowe i właściciela. Druk i eksport PDF.',
  },
  'tour.offline.title': {
    en: 'Offline-ready',
    de: 'Offline-fähig',
    fr: 'Hors ligne',
    it: 'Offline',
    es: 'Sin conexión',
    pt: 'Offline',
    nl: 'Offline-klaar',
    pl: 'Tryb offline',
  },
  'tour.offline.body': {
    en: 'Toggle offline to simulate field work without network. Data persists locally; syncs when you reconnect.',
    de: 'Offline umschalten für Feldarbeit ohne Netz. Daten lokal gespeichert, Synchronisation bei Verbindung.',
    fr: 'Basculez hors ligne pour simuler le travail de terrain. Les données sont conservées localement et synchronisées à la reconnexion.',
    it: 'Passa offline per simulare il lavoro sul campo. I dati persistono localmente e si sincronizzano alla riconnessione.',
    es: 'Active sin conexión para simular trabajo de campo. Los datos persisten localmente y se sincronizan al reconectar.',
    pt: 'Alterne para offline para simular trabalho de campo. Os dados persistem localmente e sincronizam ao reconectar.',
    nl: 'Schakel offline om veldwerk te simuleren. Gegevens blijven lokaal opgeslagen en synchroniseren bij herverbinding.',
    pl: 'Przełącz offline, aby symulować pracę w terenie. Dane są przechowywane lokalnie i synchronizowane po ponownym połączeniu.',
  },
  'tour.lang.title': {
    en: 'Language',
    de: 'Sprache',
    fr: 'Langue',
    it: 'Lingua',
    es: 'Idioma',
    pt: 'Idioma',
    nl: 'Taal',
    pl: 'Język',
  },
  'tour.lang.body': {
    en: 'Switch between 8 languages. All labels update instantly.',
    de: 'Zwischen 8 Sprachen wechseln. Alle Bezeichnungen ändern sofort.',
    fr: 'Basculez entre 8 langues. Toutes les étiquettes se mettent à jour instantanément.',
    it: 'Passa tra 8 lingue. Tutte le etichette si aggiornano istantaneamente.',
    es: 'Cambie entre 8 idiomas. Todas las etiquetas se actualizan al instante.',
    pt: 'Alterne entre 8 idiomas. Todos os rótulos atualizam instantaneamente.',
    nl: 'Schakel tussen 8 talen. Alle labels worden direct bijgewerkt.',
    pl: 'Przełączaj między 8 językami. Wszystkie etykiety aktualizują się natychmiast.',
  },
  'tour.ready.title': {
    en: "You're ready",
    de: 'Startklar',
    fr: 'Vous êtes prêt',
    it: 'Sei pronto',
    es: 'Estás listo',
    pt: 'Está pronto',
    nl: 'U bent klaar',
    pl: 'Gotowe',
  },
  'tour.ready.body': {
    en: 'Click the Tour button any time to replay this walkthrough.',
    de: 'Über Tour jederzeit erneut starten.',
    fr: 'Cliquez sur le bouton Visite à tout moment pour relancer cette présentation.',
    it: 'Clicca sul pulsante Tour in qualsiasi momento per ripetere questa guida.',
    es: 'Haga clic en el botón Tour en cualquier momento para repetir esta guía.',
    pt: 'Clique no botão Tour a qualquer momento para repetir esta apresentação.',
    nl: 'Klik op de Tour-knop om deze rondleiding opnieuw af te spelen.',
    pl: 'Kliknij przycisk Tour w dowolnym momencie, aby powtórzyć ten przewodnik.',
  },

  // Calendar / Visits
  'nav.calendar': {
    en: 'Calendar',
    de: 'Kalender',
    fr: 'Calendrier',
    it: 'Calendario',
    es: 'Calendario',
    pt: 'Calendário',
    nl: 'Kalender',
    pl: 'Kalendarz',
  },
  'calendar.title': {
    en: 'Visit Calendar',
    de: 'Besuchskalender',
    fr: 'Calendrier des visites',
    it: 'Calendario visite',
    es: 'Calendario de visitas',
    pt: 'Calendário de visitas',
    nl: 'Bezoekkalender',
    pl: 'Kalendarz wizyt',
  },
  'calendar.today': {
    en: 'Today',
    de: 'Heute',
    fr: "Aujourd'hui",
    it: 'Oggi',
    es: 'Hoy',
    pt: 'Hoje',
    nl: 'Vandaag',
    pl: 'Dziś',
  },
  'calendar.addVisit': {
    en: 'Add Visit',
    de: 'Besuch hinzufügen',
    fr: 'Ajouter une visite',
    it: 'Aggiungi visita',
    es: 'Añadir visita',
    pt: 'Adicionar visita',
    nl: 'Bezoek toevoegen',
    pl: 'Dodaj wizytę',
  },
  'calendar.editVisit': {
    en: 'Edit Visit',
    de: 'Besuch bearbeiten',
    fr: 'Modifier la visite',
    it: 'Modifica visita',
    es: 'Editar visita',
    pt: 'Editar visita',
    nl: 'Bezoek bewerken',
    pl: 'Edytuj wizytę',
  },
  'calendar.deleteVisit': {
    en: 'Delete Visit',
    de: 'Besuch löschen',
    fr: 'Supprimer la visite',
    it: 'Elimina visita',
    es: 'Eliminar visita',
    pt: 'Excluir visita',
    nl: 'Bezoek verwijderen',
    pl: 'Usuń wizytę',
  },
  'calendar.customer': {
    en: 'Customer',
    de: 'Kunde',
    fr: 'Client',
    it: 'Cliente',
    es: 'Cliente',
    pt: 'Cliente',
    nl: 'Klant',
    pl: 'Klient',
  },
  'calendar.worker': {
    en: 'Technician',
    de: 'Techniker',
    fr: 'Technicien',
    it: 'Tecnico',
    es: 'Técnico',
    pt: 'Técnico',
    nl: 'Monteur',
    pl: 'Technik',
  },
  // Client #92: the technician field sets name + login together; a worker
  // without a login gets the appointment but no device sees it.
  'calendar.workerNoLogin': {
    en: 'This technician has no login, so the appointment reaches no device.',
    de: 'Dieser Techniker hat kein Login, der Termin erscheint auf keinem Gerät.',
    fr: "Ce technicien n'a pas de connexion, le rendez-vous n'apparaît sur aucun appareil.",
    it: "Questo tecnico non ha un accesso, l'appuntamento non compare su nessun dispositivo.",
    es: 'Este técnico no tiene acceso, la cita no aparece en ningún dispositivo.',
    pt: 'Este técnico não tem acesso, a marcação não aparece em nenhum dispositivo.',
    nl: 'Deze monteur heeft geen login, de afspraak verschijnt op geen enkel apparaat.',
    pl: 'Ten technik nie ma loginu, termin nie pojawi się na żadnym urządzeniu.',
  },
  'calendar.assignedTech': {
    en: 'Assigned to (login)',
    de: 'Zugewiesen an (Login)',
    fr: 'Assigné à (connexion)',
    it: 'Assegnato a (login)',
    es: 'Asignado a (acceso)',
    pt: 'Atribuído a (login)',
    nl: 'Toegewezen aan (login)',
    pl: 'Przypisane do (login)',
  },
  'calendar.date': {
    en: 'Date',
    de: 'Datum',
    fr: 'Date',
    it: 'Data',
    es: 'Fecha',
    pt: 'Data',
    nl: 'Datum',
    pl: 'Data',
  },
  // Optional time of day on an appointment (client #93).
  'calendar.time': {
    en: 'Time',
    de: 'Uhrzeit',
    fr: 'Heure',
    it: 'Ora',
    es: 'Hora',
    pt: 'Hora',
    nl: 'Tijd',
    pl: 'Godzina',
  },
  'common.optional': {
    en: '(optional)',
    de: '(optional)',
    fr: '(facultatif)',
    it: '(facoltativo)',
    es: '(opcional)',
    pt: '(opcional)',
    nl: '(optioneel)',
    pl: '(opcjonalnie)',
  },
  'calendar.notes': {
    en: 'Notes',
    de: 'Notizen',
    fr: 'Notes',
    it: 'Note',
    es: 'Notas',
    pt: 'Notas',
    nl: 'Notities',
    pl: 'Notatki',
  },
  'calendar.status': {
    en: 'Status',
    de: 'Status',
    fr: 'Statut',
    it: 'Stato',
    es: 'Estado',
    pt: 'Estado',
    nl: 'Status',
    pl: 'Status',
  },
  'calendar.status.planned': {
    en: 'Planned',
    de: 'Geplant',
    fr: 'Planifié',
    it: 'Pianificato',
    es: 'Planificado',
    pt: 'Planeado',
    nl: 'Gepland',
    pl: 'Zaplanowana',
  },
  'calendar.status.confirmed': {
    en: 'Confirmed',
    de: 'Bestätigt',
    fr: 'Confirmé',
    it: 'Confermato',
    es: 'Confirmado',
    pt: 'Confirmado',
    nl: 'Bevestigd',
    pl: 'Potwierdzona',
  },
  'calendar.status.completed': {
    en: 'Completed',
    de: 'Abgeschlossen',
    fr: 'Terminé',
    it: 'Completato',
    es: 'Completado',
    pt: 'Concluído',
    nl: 'Voltooid',
    pl: 'Zakończona',
  },
  'calendar.status.cancelled': {
    en: 'Cancelled',
    de: 'Abgesagt',
    fr: 'Annulé',
    it: 'Annullato',
    es: 'Cancelado',
    pt: 'Cancelado',
    nl: 'Geannuleerd',
    pl: 'Anulowana',
  },
  'calendar.noVisits': {
    en: 'No visits scheduled',
    de: 'Keine Besuche geplant',
    fr: 'Aucune visite prévue',
    it: 'Nessuna visita programmata',
    es: 'No hay visitas programadas',
    pt: 'Nenhuma visita agendada',
    nl: 'Geen bezoeken gepland',
    pl: 'Brak zaplanowanych wizyt',
  },
  'calendar.allWorkers': {
    en: 'All technicians',
    de: 'Alle Techniker',
    fr: 'Tous les techniciens',
    it: 'Tutti i tecnici',
    es: 'Todos los técnicos',
    pt: 'Todos os técnicos',
    nl: 'Alle monteurs',
    pl: 'Wszyscy technicy',
  },
  'calendar.save': {
    en: 'Save',
    de: 'Speichern',
    fr: 'Enregistrer',
    it: 'Salva',
    es: 'Guardar',
    pt: 'Salvar',
    nl: 'Opslaan',
    pl: 'Zapisz',
  },
  'calendar.cancel': {
    en: 'Cancel',
    de: 'Abbrechen',
    fr: 'Annuler',
    it: 'Annulla',
    es: 'Cancelar',
    pt: 'Cancelar',
    nl: 'Annuleren',
    pl: 'Anuluj',
  },
  'calendar.visitSaved': {
    en: 'Visit saved',
    de: 'Besuch gespeichert',
    fr: 'Visite enregistrée',
    it: 'Visita salvata',
    es: 'Visita guardada',
    pt: 'Visita salva',
    nl: 'Bezoek opgeslagen',
    pl: 'Wizyta zapisana',
  },
  'calendar.visitDeleted': {
    en: 'Visit deleted',
    de: 'Besuch gelöscht',
    fr: 'Visite supprimée',
    it: 'Visita eliminata',
    es: 'Visita eliminada',
    pt: 'Visita excluída',
    nl: 'Bezoek verwijderd',
    pl: 'Wizyta usunięta',
  },
  'calendar.selectCustomer': {
    en: 'Select customer',
    de: 'Kunde auswählen',
    fr: 'Sélectionner un client',
    it: 'Seleziona cliente',
    es: 'Seleccionar cliente',
    pt: 'Selecionar cliente',
    nl: 'Klant selecteren',
    pl: 'Wybierz klienta',
  },

  // Searchable customer picker (client #27)
  'calendar.searchCustomer': {
    en: 'Search customers',
    de: 'Kunden suchen',
    fr: 'Rechercher des clients',
    it: 'Cerca clienti',
    es: 'Buscar clientes',
    pt: 'Pesquisar clientes',
    nl: 'Klanten zoeken',
    pl: 'Szukaj klientów',
  },
  'calendar.noCustomerMatch': {
    en: 'No matching customer',
    de: 'Kein passender Kunde',
    fr: 'Aucun client correspondant',
    it: 'Nessun cliente corrispondente',
    es: 'Ningún cliente coincidente',
    pt: 'Nenhum cliente correspondente',
    nl: 'Geen overeenkomende klant',
    pl: 'Brak pasującego klienta',
  },
  'calendar.moreMatches': {
    en: 'more matches — refine your search',
    de: 'weitere Treffer — Suche verfeinern',
    fr: 'autres résultats — affinez votre recherche',
    it: 'altri risultati — affina la ricerca',
    es: 'resultados más — refina la búsqueda',
    pt: 'mais resultados — refine a pesquisa',
    nl: 'meer resultaten — verfijn uw zoekopdracht',
    pl: 'więcej wyników — zawęź wyszukiwanie',
  },

  // Collapsible day-panel visit summary (client #70)
  'calendar.visitDetails': {
    en: 'Visit details',
    de: 'Besuchsdetails',
    fr: 'Détails de la visite',
    it: 'Dettagli visita',
    es: 'Detalles de la visita',
    pt: 'Detalhes da visita',
    nl: 'Bezoekdetails',
    pl: 'Szczegóły wizyty',
  },
  'calendar.extinguishersDue': {
    en: 'Extinguishers due',
    de: 'Fällige Feuerlöscher',
    fr: 'Extincteurs à contrôler',
    it: 'Estintori in scadenza',
    es: 'Extintores pendientes',
    pt: 'Extintores a vencer',
    nl: 'Te keuren brandblussers',
    pl: 'Gaśnice do przeglądu',
  },
  'calendar.hosesDue': {
    en: 'Fire hoses due',
    de: 'Fällige Löschposten',
    fr: 'Lances incendie à contrôler',
    it: 'Manichette in scadenza',
    es: 'Mangueras pendientes',
    pt: 'Mangueiras a vencer',
    nl: 'Te keuren brandslangen',
    pl: 'Węże do przeglądu',
  },
  'calendar.refillsDue': {
    en: 'Refills due',
    de: 'Fällige Füllungen',
    fr: 'Recharges à effectuer',
    it: 'Ricariche da eseguire',
    es: 'Recargas pendientes',
    pt: 'Recargas pendentes',
    nl: 'Uit te voeren vullingen',
    pl: 'Napełnienia do wykonania',
  },
  'calendar.dueModels': {
    en: 'Models',
    de: 'Modelle',
    fr: 'Modèles',
    it: 'Modelli',
    es: 'Modelos',
    pt: 'Modelos',
    nl: 'Modellen',
    pl: 'Modele',
  },
  'calendar.nothingDue': {
    en: 'Nothing due at this site',
    de: 'Nichts fällig an diesem Standort',
    fr: 'Rien à échéance sur ce site',
    it: 'Nulla in scadenza in questa sede',
    es: 'Nada pendiente en este sitio',
    pt: 'Nada a vencer neste local',
    nl: 'Niets te doen op deze locatie',
    pl: 'Nic do wykonania w tym obiekcie',
  },

  // Client feedback v1
  'overview.total': {
    en: 'Total',
    de: 'Total',
    fr: 'Total',
    it: 'Totale',
    es: 'Total',
    pt: 'Total',
    nl: 'Totaal',
    pl: 'Łącznie',
  },
  'customer.serviceContract': {
    en: 'Service Contract',
    de: 'Servicevertrag',
    fr: 'Contrat de service',
    it: 'Contratto di servizio',
    es: 'Contrato de servicio',
    pt: 'Contrato de serviço',
    nl: 'Servicecontract',
    pl: 'Umowa serwisowa',
  },
  'customer.uploadContract': {
    en: 'Upload Contract PDF',
    de: 'Vertrag PDF hochladen',
    fr: 'Télécharger PDF contrat',
    it: 'Carica PDF contratto',
    es: 'Subir PDF contrato',
    pt: 'Carregar PDF contrato',
    nl: 'Contract PDF uploaden',
    pl: 'Prześlij PDF umowy',
  },
  'customer.viewContract': {
    en: 'View Contract',
    de: 'Vertrag anzeigen',
    fr: 'Voir le contrat',
    it: 'Visualizza contratto',
    es: 'Ver contrato',
    pt: 'Ver contrato',
    nl: 'Contract bekijken',
    pl: 'Zobacz umowę',
  },
  'customer.contractUploaded': {
    en: 'Contract PDF uploaded',
    de: 'Vertrag PDF hochgeladen',
    fr: 'PDF contrat téléchargé',
    it: 'PDF contratto caricato',
    es: 'PDF contrato subido',
    pt: 'PDF contrato carregado',
    nl: 'Contract PDF geüpload',
    pl: 'PDF umowy przesłany',
  },
  'customer.linkedCustomers': {
    en: 'Linked Customers',
    de: 'Verknüpfte Kunden',
    fr: 'Clients liés',
    it: 'Clienti collegati',
    es: 'Clientes vinculados',
    pt: 'Clientes vinculados',
    nl: 'Gekoppelde klanten',
    pl: 'Powiązani klienci',
  },
  'customer.linkCustomer': {
    en: 'Link customer…',
    de: 'Kunde verknüpfen…',
    fr: 'Lier un client…',
    it: 'Collega cliente…',
    es: 'Vincular cliente…',
    pt: 'Vincular cliente…',
    nl: 'Klant koppelen…',
    pl: 'Powiąż klienta…',
  },
  'customer.searchCustomers': {
    en: 'Search customers…',
    de: 'Kunden suchen…',
    fr: 'Rechercher clients…',
    it: 'Cerca clienti…',
    es: 'Buscar clientes…',
    pt: 'Pesquisar clientes…',
    nl: 'Klanten zoeken…',
    pl: 'Szukaj klientów…',
  },
  'customer.noLinkedCustomers': {
    en: 'No linked customers',
    de: 'Keine verknüpften Kunden',
    fr: 'Aucun client lié',
    it: 'Nessun cliente collegato',
    es: 'Sin clientes vinculados',
    pt: 'Sem clientes vinculados',
    nl: 'Geen gekoppelde klanten',
    pl: 'Brak powiązanych klientów',
  },
  'customer.linkedFiles': {
    en: 'Linked files',
    de: 'Verknüpfte Dateien',
    fr: 'Fichiers liés',
    it: 'File collegati',
    es: 'Archivos vinculados',
    pt: 'Ficheiros vinculados',
    nl: 'Gekoppelde bestanden',
    pl: 'Powiązane pliki',
  },
  'customer.legacyImport': {
    en: 'Legacy import',
    de: 'Legacy-Import',
    fr: 'Import hérité',
    it: 'Importazione legacy',
    es: 'Importación heredada',
    pt: 'Importação legada',
    nl: 'Legacy-import',
    pl: 'Import ze starego systemu',
  },
  'legacy.sourceSheet': {
    en: 'Source sheet',
    de: 'Quellblatt',
    fr: 'Feuille source',
    it: 'Foglio di origine',
    es: 'Hoja de origen',
    pt: 'Folha de origem',
    nl: 'Bronblad',
    pl: 'Arkusz źródłowy',
  },
  'legacy.unmappedFields': {
    en: 'Unmapped fields',
    de: 'Nicht zugeordnete Felder',
    fr: 'Champs non mappés',
    it: 'Campi non mappati',
    es: 'Campos sin asignar',
    pt: 'Campos não mapeados',
    nl: 'Niet-toegewezen velden',
    pl: 'Pola niezmapowane',
  },
  'legacy.field': {
    en: 'Field',
    de: 'Feld',
    fr: 'Champ',
    it: 'Campo',
    es: 'Campo',
    pt: 'Campo',
    nl: 'Veld',
    pl: 'Pole',
  },
  'legacy.value': {
    en: 'Value',
    de: 'Wert',
    fr: 'Valeur',
    it: 'Valore',
    es: 'Valor',
    pt: 'Valor',
    nl: 'Waarde',
    pl: 'Wartość',
  },
  'ext.maintainedThisCycle': {
    en: 'Maintained this cycle',
    de: 'In diesem Zyklus gewartet',
    fr: 'Entretenu ce cycle',
    it: 'Manutenuto in questo ciclo',
    es: 'Mantenido este ciclo',
    pt: 'Mantido neste ciclo',
    nl: 'Onderhouden deze cyclus',
    pl: 'Konserwowane w tym cyklu',
  },
  'ext.notMaintained': {
    en: 'Not yet maintained',
    de: 'Noch nicht gewartet',
    fr: 'Pas encore entretenu',
    it: 'Non ancora manutenuto',
    es: 'Aún no mantenido',
    pt: 'Ainda não mantido',
    nl: 'Nog niet onderhouden',
    pl: 'Jeszcze nie konserwowane',
  },
  'hose.hoseDiameter': {
    en: 'Hose Diameter',
    de: 'Schlauchdurchmesser',
    fr: 'Diamètre tuyau',
    it: 'Diametro tubo',
    es: 'Diámetro manguera',
    pt: 'Diâmetro mangueira',
    nl: 'Slangdiameter',
    pl: 'Średnica węża',
  },
  'hose.connectionDiameter': {
    en: 'Connection Diameter',
    de: 'Anschlussdurchmesser',
    fr: 'Diamètre raccord',
    it: 'Diametro raccordo',
    es: 'Diámetro conexión',
    pt: 'Diâmetro conexão',
    nl: 'Aansluitdiameter',
    pl: 'Średnica złącza',
  },
  'ext.uploadDoc': {
    en: 'Upload Documentation PDF',
    de: 'Dokumentation PDF hochladen',
    fr: 'Télécharger documentation PDF',
    it: 'Carica documentazione PDF',
    es: 'Subir documentación PDF',
    pt: 'Carregar documentação PDF',
    nl: 'Documentatie PDF uploaden',
    pl: 'Prześlij PDF dokumentacji',
  },
  'ext.viewDoc': {
    en: 'View Documentation PDF',
    de: 'Dokumentation PDF anzeigen',
    fr: 'Voir documentation PDF',
    it: 'Visualizza documentazione PDF',
    es: 'Ver documentación PDF',
    pt: 'Ver documentação PDF',
    nl: 'Documentatie PDF bekijken',
    pl: 'Zobacz PDF dokumentacji',
  },
  'ext.mfrSearch': {
    en: 'Search manufacturers…',
    de: 'Hersteller suchen…',
    fr: 'Rechercher fabricants…',
    it: 'Cerca produttori…',
    es: 'Buscar fabricantes…',
    pt: 'Pesquisar fabricantes…',
    nl: 'Fabrikanten zoeken…',
    pl: 'Szukaj producentów…',
  },
  'table.refilled': {
    en: 'Refilled',
    de: 'Befüllt',
    fr: 'Rechargé',
    it: 'Ricaricato',
    es: 'Recargado',
    pt: 'Recarregado',
    nl: 'Hervuld',
    pl: 'Napełniony',
  },

  // Admin CRUD + IO
  'mfr.agentType': {
    en: 'Class',
    de: 'Brandklasse',
    fr: 'Classe',
    it: 'Classe',
    es: 'Clase',
    pt: 'Classe',
    nl: 'Klasse',
    pl: 'Klasa',
  },
  'mfr.modelDoc': {
    en: 'Device Documentation',
    de: 'Gerätedokumentation',
    fr: 'Documentation appareil',
    it: 'Documentazione dispositivo',
    es: 'Documentación del equipo',
    pt: 'Documentação do aparelho',
    nl: 'Apparaatdocumentatie',
    pl: 'Dokumentacja urządzenia',
  },
  'mfr.mfrName': {
    en: 'Manufacturer Name',
    de: 'Herstellername',
    fr: 'Nom du fabricant',
    it: 'Nome produttore',
    es: 'Nombre fabricante',
    pt: 'Nome fabricante',
    nl: 'Naam fabrikant',
    pl: 'Nazwa producenta',
  },
  'prices.qtyRange': {
    en: 'Qty Range',
    de: 'Mengenbereich',
    fr: 'Plage qté',
    it: 'Intervallo qtà',
    es: 'Rango cant.',
    pt: 'Intervalo qtd.',
    nl: 'Aantalsbereik',
    pl: 'Zakres ilości',
  },
  // Client #54: disposal can be priced three ways; say which row wins.
  'prices.disposalPrecedence': {
    en: 'Disposal is priced by content first, then by device type, otherwise at the general disposal price.',
    de: 'Entsorgung wird zuerst nach Inhalt berechnet, dann nach Gerätetyp, sonst zum allgemeinen Entsorgungspreis.',
    fr: "L'élimination est facturée d'abord selon le contenu, puis selon le type d'appareil, sinon au prix d'élimination général.",
    it: 'Lo smaltimento è calcolato prima in base al contenuto, poi al tipo di dispositivo, altrimenti al prezzo di smaltimento generale.',
    es: 'La eliminación se cobra primero según el contenido, luego según el tipo de dispositivo y, si no, al precio general de eliminación.',
    pt: 'O descarte é cobrado primeiro pelo conteúdo, depois pelo tipo de dispositivo e, caso contrário, pelo preço geral de descarte.',
    nl: 'Afvoer wordt eerst op inhoud berekend, dan op apparaattype, anders tegen de algemene afvoerprijs.',
    pl: 'Utylizacja jest rozliczana najpierw według zawartości, potem według typu urządzenia, a w pozostałych przypadkach według ogólnej ceny utylizacji.',
  },
  'customer.newCustomerTitle': {
    en: 'New Customer',
    de: 'Neuer Kunde',
    fr: 'Nouveau client',
    it: 'Nuovo cliente',
    es: 'Nuevo cliente',
    pt: 'Novo cliente',
    nl: 'Nieuwe klant',
    pl: 'Nowy klient',
  },

  'auth.signIn': {
    en: 'Sign in',
    de: 'Anmelden',
    fr: 'Se connecter',
    it: 'Accedi',
    es: 'Iniciar sesión',
    pt: 'Entrar',
    nl: 'Inloggen',
    pl: 'Zaloguj się',
  },
  'auth.signOut': {
    en: 'Sign out',
    de: 'Abmelden',
    fr: 'Se déconnecter',
    it: 'Esci',
    es: 'Cerrar sesión',
    pt: 'Sair',
    nl: 'Uitloggen',
    pl: 'Wyloguj się',
  },
  'role.orgAdmin': {
    en: 'Administrator',
    de: 'Administrator',
    fr: 'Administrateur',
    it: 'Amministratore',
    es: 'Administrador',
    pt: 'Administrador',
    nl: 'Beheerder',
    pl: 'Administrator',
  },
  'role.superAdmin': {
    en: 'Super Admin',
    de: 'Super-Administrator',
    fr: 'Super administrateur',
    it: 'Super amministratore',
    es: 'Superadministrador',
    pt: 'Superadministrador',
    nl: 'Superbeheerder',
    pl: 'Superadministrator',
  },
  'role.staff': {
    en: 'Staff',
    de: 'Mitarbeiter',
    fr: 'Personnel',
    it: 'Personale',
    es: 'Personal',
    pt: 'Pessoal',
    nl: 'Personeel',
    pl: 'Personel',
  },
  'role.technician': {
    en: 'Technician',
    de: 'Techniker',
    fr: 'Technicien',
    it: 'Tecnico',
    es: 'Técnico',
    pt: 'Técnico',
    nl: 'Technicus',
    pl: 'Technik',
  },
  'auth.email': {
    en: 'Email',
    de: 'E-Mail',
    fr: 'E-mail',
    it: 'Email',
    es: 'Correo',
    pt: 'E-mail',
    nl: 'E-mail',
    pl: 'Email',
  },
  'auth.password': {
    en: 'Password',
    de: 'Passwort',
    fr: 'Mot de passe',
    it: 'Password',
    es: 'Contraseña',
    pt: 'Senha',
    nl: 'Wachtwoord',
    pl: 'Hasło',
  },
  'auth.signingIn': {
    en: 'Signing in…',
    de: 'Anmelden…',
    fr: 'Connexion…',
    it: 'Accesso…',
    es: 'Iniciando…',
    pt: 'A entrar…',
    nl: 'Inloggen…',
    pl: 'Logowanie…',
  },
  'auth.invalidCreds': {
    en: 'Invalid credentials',
    de: 'Ungültige Anmeldedaten',
    fr: 'Identifiants invalides',
    it: 'Credenziali non valide',
    es: 'Credenciales no válidas',
    pt: 'Credenciais inválidas',
    nl: 'Ongeldige gegevens',
    pl: 'Nieprawidłowe dane',
  },
  'auth.loginFailed': {
    en: 'Login failed. Please try again.',
    de: 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.',
    fr: 'Échec de la connexion. Réessayez.',
    it: 'Accesso non riuscito. Riprova.',
    es: 'Error al iniciar sesión. Reintentar.',
    pt: 'Falha no login. Tente novamente.',
    nl: 'Inloggen mislukt. Probeer opnieuw.',
    pl: 'Logowanie nie powiodło się.',
  },
  'auth.enterEmailFirst': {
    en: 'Enter your email above first',
    de: 'Bitte zuerst E-Mail eingeben',
    fr: "Saisissez d'abord votre e-mail",
    it: 'Inserisci prima la tua email',
    es: 'Introduce tu correo arriba',
    pt: 'Introduza o seu e-mail acima',
    nl: 'Voer eerst je e-mail in',
    pl: 'Najpierw wpisz e-mail',
  },
  'auth.loading': {
    en: 'Loading…',
    de: 'Lädt…',
    fr: 'Chargement…',
    it: 'Caricamento…',
    es: 'Cargando…',
    pt: 'A carregar…',
    nl: 'Laden…',
    pl: 'Ładowanie…',
  },
  // Client #48 — surfaced by UploadFailedError when a failed upload may no
  // longer be silently inlined as a data: URL (see lib/uploads.ts).
  'upload.failed': {
    en: 'Upload failed. Check your connection and try again.',
    de: 'Upload fehlgeschlagen. Verbindung prüfen und erneut versuchen.',
    fr: 'Échec du téléversement. Vérifiez votre connexion et réessayez.',
    it: 'Caricamento non riuscito. Controlla la connessione e riprova.',
    es: 'Error al subir el archivo. Compruebe su conexión e inténtelo de nuevo.',
    pt: 'Falha no carregamento. Verifique a ligação e tente novamente.',
    nl: 'Uploaden mislukt. Controleer je verbinding en probeer opnieuw.',
    pl: 'Przesyłanie nie powiodło się. Sprawdź połączenie i spróbuj ponownie.',
  },
  'upload.failedOfflineTooLarge': {
    en: 'File too large to store offline. Reconnect and upload it again.',
    de: 'Datei zu gross für die Offline-Speicherung. Erneut verbinden und nochmals hochladen.',
    fr: 'Fichier trop volumineux pour être stocké hors ligne. Reconnectez-vous et réessayez.',
    it: 'File troppo grande per il salvataggio offline. Riconnettiti e caricalo di nuovo.',
    es: 'Archivo demasiado grande para guardarlo sin conexión. Vuelva a conectarse y súbalo otra vez.',
    pt: 'Ficheiro demasiado grande para guardar offline. Volte a ligar-se e carregue novamente.',
    nl: 'Bestand te groot om offline op te slaan. Maak opnieuw verbinding en upload het nogmaals.',
    pl: 'Plik zbyt duży, aby zapisać go offline. Połącz się ponownie i prześlij jeszcze raz.',
  },
  'auth.brandTagline': L('Inspections, on the job'),
  'auth.heroTitle': L('Every extinguisher, every hose, every visit.'),
  'auth.heroSubtitle': L(
    'One app for the whole crew — from the truck to the basement.'
  ),
  'auth.featureSecure': L('Your customers, your data, your team only'),
  'auth.featureOffline': L('Works underground. Syncs when you surface.'),
  'auth.signInSubtitle': L('Sign in with your work email.'),
  'auth.showPw': L('Show password'),
  'auth.hidePw': L('Hide password'),
  'auth.footerNote': L('Need access? Ask your organization admin.'),
  // Superadmin nav
  'nav.superadmin': L('Superadmin'),
  'nav.tenants': L('Tenants'),
  // Superadmin tenants list
  'superadmin.tenants.title': L('Tenants'),
  'superadmin.tenants.subtitle': L('Manage all customer organisations.'),
  'superadmin.tenants.new': L('New tenant'),
  'superadmin.tenants.empty': L('No tenants yet.'),
  'superadmin.tenants.colId': L('ID'),
  'superadmin.tenants.colName': L('Name'),
  'superadmin.tenants.colStatus': L('Status'),
  'superadmin.tenants.colCreated': L('Created'),
  'superadmin.tenants.colActions': L('Actions'),
  'superadmin.tenants.view': L('View'),
  // Create tenant form
  'superadmin.tenantNew.title': L('Create tenant'),
  'superadmin.tenantNew.subtitle': L(
    'Provision a new customer organisation and primary admin.'
  ),
  'superadmin.tenantNew.id': L('Tenant ID'),
  'superadmin.tenantNew.name': L('Tenant name'),
  'superadmin.tenantNew.contactEmail': L('Contact email (optional)'),
  'superadmin.tenantNew.primaryAdmin': L('Primary admin'),
  'superadmin.tenantNew.adminEmail': L('Admin email'),
  'superadmin.tenantNew.adminPassword': L('Admin password'),
  'superadmin.tenantNew.adminDisplayName': L('Display name (optional)'),
  'superadmin.tenantNew.submit': L('Create tenant'),
  'superadmin.tenantNew.submitting': L('Creating…'),
  'superadmin.tenantNew.cancel': L('Cancel'),
  // Tenant detail
  'superadmin.tenantDetail.notFound': L('Tenant not found.'),
  'superadmin.tenantDetail.status': L('Status'),
  'superadmin.tenantDetail.contactEmail': L('Contact email'),
  'superadmin.tenantDetail.created': L('Created'),
  'superadmin.tenantDetail.customers': L('Customers'),
  'superadmin.tenantDetail.extinguishers': L('Extinguishers'),
  'superadmin.tenantDetail.visits': L('Visits'),
  'superadmin.tenantDetail.users': L('Users'),
  'superadmin.tenantDetail.noUsers': L('No users.'),
  'superadmin.tenantDetail.userEmail': L('Email'),
  'superadmin.tenantDetail.userRoles': L('Roles'),
  'superadmin.tenantDetail.userStatus': L('Status'),
  'superadmin.tenantDetail.deactivated': L('Deactivated'),
  'superadmin.tenantDetail.active': L('Active'),
  'superadmin.tenantDetail.suspend': L('Suspend'),
  'superadmin.tenantDetail.unsuspend': L('Reactivate'),
  // Platform audit
} as const;

export type I18nKey = keyof typeof dict;

/** Maps a HoseCondition check key to its translatable label key. */
// Enum values are stored in English lowercase ('maintenance', 'boost'); these
// map them onto their translated labels wherever a raw value used to be shown
// (client #62 billing column, #63 pressure class).
export const BILLING_LABEL_KEYS = {
  none: 'ext.billingNone',
  new: 'ext.billingNew',
  maintenance: 'ext.billingMaintenance',
  disposed: 'ext.billingDisposed',
} as const satisfies Record<string, I18nKey>;

export const PRESSURE_LABEL_KEYS = {
  boost: 'pressure.boost',
  permanent: 'pressure.permanent',
} as const satisfies Record<string, I18nKey>;

export const HOSE_COND_LABEL_KEYS = {
  fireCock: 'cond.fireCock',
  seals: 'cond.seals',
  hose: 'cond.hose',
  nozzle: 'cond.nozzle',
  axle: 'cond.axle',
  swivelArm: 'cond.swivelArm',
  doorLock: 'cond.doorLock',
  water: 'cond.water',
} as const satisfies Record<string, I18nKey>;

export function t(key: I18nKey, lang: Lang): string {
  const entry = dict[key];
  if (!entry) return key;
  return (entry as Record<string, string>)[lang] ?? entry.en;
}

export function useT() {
  const lang = useApp(s => s.lang);
  return (key: I18nKey) => t(key, lang);
}
